import sharedTranslation from "shared/common/locales/en";

// If key is same as in shared translations we use it if value/translation is same.
// If we need to override value/translation we define it here or use different key.
// Keys should be grouped by sections.
// Keys that are used globally should be defined in global section.
// Some keys make sense to be defined in global section, but if want to allow more flexibility
// we can define them in specific section.
// Keys should be in camel case and should be descriptive.

const translation = {
  global: {
    ...sharedTranslation.global,
    toastSuccess: "Success!",
    toastApiError: "We have encountered an error. Error: {{message}}",
    toastSuccessErrorCount:
      "{{successCount}} records were updated. {{errorCount}} records were skipped.",
    toastBulkError: "Please first select records.", // Generic error for bulk actions
    toastPauseBulkError: "Please first select records you want to pause.",
    toastActivateBulkError: "Please first select records you want to activate.",
    toastSkipBulkError: "Please first select records you want to skip.",
    toastErrorCount: "{{errorCount}} records were skipped.{{errorMessages}}",
    toastExportError: "Error while exporting data. Error: {{message}}",
    toastExportDateError: "You need to select a valid export date range",
    toastDeleteBulkError: "Please first select records you want to delete.",
    toastArchiveBulkError: "Please first select records you want to archive.",
    toastUnarchiveBulkError:
      "Please first select records you want to unarchive.",
    toastStatusBulkError:
      "Please first select records you want to update status.",
    toastAssigneeBulkError:
      "Please first select records you want to update assignee.",
    toastStatusBulkInvalidError: "Please select valid status.",
    toastPermissionError:
      "Sorry, you don't have enough permissions. Try contacting BH admin.",
    toastAllRequiredFieldsError: "Please check all required (*) fields",
    toastMarkNotificationAsReadError:
      "An error occurred while marking notification as read. Please refresh the page and try again.",
    toastMarkAllNotificationsAsReadError:
      "An error occurred while marking all notifications as read. Please refresh the page and try again.",
    toastOpenRecordManuallyError:
      "We can't open this record automatically. Please try opening it manually.",
    toastNewNotificationIndicatorError:
      "An error occurred while adding new notification in Topbar indicator. Please refresh the page and try again.",
    toastNewNotificationListError:
      "An error occurred while adding new notification in Topbar list. Please refresh the page and try again.",
    toastSuccessfullyCopied: "Successfully copied!",
    toastEnterValidMobileNumber: "Please enter valid phone number",
    toastEnterValidCode: "Please enter valid code",
    toastEditPortfolioRecordsError: "You can not edit portfolio records",
    toastAfterParsingYourFileWeReceivedZeroRecords:
      "After parsing your file we received zero records. Please double check your file or select another.",
    toastNoImportFilesFound:
      "No files found. Please go back and upload new CSV.",
    toastInvalidSkipDate:
      "Please provide valid ID and Date to skip selected date.",
    toastMissingParameters: "Required parameters are missing.",

    // Errors
    errorTitle: "Error!",
    recordNotFoundTitle: "Sorry, we could not find the record",
    recordNotFoundDescription: "Please try again later..",
    notAuthorized: "Not authorized",
    propertyAdminAccessError: "Contact your property admin to request access.",
    noFilesUploaded: "No files uploaded",
    requiredField: "This field is required",
    invalidEmail: "Please enter valid email",
    invalidURL: "Invalid URL",
    noRecords: "There are no records at this moment",
    noRecordsDescription: "You can try changing view or adding new records..",

    // Common
    default: "Default",
    yes: "Yes",
    no: "No",
    minute: "minute",
    minutes: "minutes",
    hour: "hour",
    hours: "hours",
    quantity: "qty",
    day: "day",
    days: "days",
    month: "month",
    year: "year",
    from: "From",
    to: "To",
    date: "Date",
    time: "Time",
    weather: "Weather",
    copyLink: "Copy Link",
    uploadFile: "Upload File",
    uploadFiles: "Upload Files",
    file: "File",
    files: "Files",
    sync: "Sync",
    printer: "Printer",
    order: "Order",
    selectView: "Please select the view",
    priceSelectorBillingDayText:
      "Billing day: {{billingDay}} of the {{billingPeriod}}",
    removeAll: "Remove all",
    myAccount: "My Account",
    minimize: "Minimize",
    minimizeTooltip: "Click to minimize this record.",
    goToFeature: "Go to {{title}}",
    goToDashboard: "Go to dashboard",
    searchPlaceholder: "Search..",
    selectedCount: "{{count}} selected",
    propertyTitle: "Property",
    portfolioTitle: "Portfolio",

    // Actions
    nextAction: "Next",
    backAction: "Back",
    submitAction: "Submit",
    sendAction: "Send",
    importAction: "Import",
    selectAction: "Select",
    addNewAction: "Add New",
    exportAction: "Export",
    openAction: "Open",
    openNewTabAction: "Open in a new tab",
    enableAction: "Enable",
    disableAction: "Disable",
    archiveAction: "Archive",
    unarchiveAction: "Unarchive",
    pauseAction: "Pause",
    activateAction: "Activate",
    deactivateAction: "Deactivate",
    skipAction: "Skip",
    skipNextDateAction: "Skip next date",
    scheduleAction: "Schedule",
    generateAction: "Generate",
    generateNowAction: "Generate now",
    shareAction: "Share",
    printAction: "Print",
    cancelAction: "Cancel",
    refundAction: "Refund",
    viewAction: "View",
    tableBulkStatusButton: "Status",
    tableBulkAssigneeButton: "Assignee",

    // Archiving
    isArchivedTableHeader: "Is Archived?",
    archivedTableValue: "Yes",
    unarchivedTableValue: "No",
    allArchivedOption: "All",
    activeArchivedOption: "Active",
    archivedArchivedOption: "Archived",
    filterArchivedLabel: "Show Archived",

    // Property vs portfolio level record
    levelTableHeader: "Level",

    // Active/Inactive
    isActiveTableHeader: "Is Active?",
    activeStatus: "Active",
    inactiveStatus: "Inactive",

    // Filters
    filterAllLabel: "All", // Used for horizontal tabs, or any other filter that has 'All' option
    filterActiveLabel: "Active",
    filterInactiveLabel: "Inactive",
    filterResetButton: "Remove all filters",
    filterSaveButton: "Filter",

    // Magic Link
    invalidMagicLink: "Invalid Magic Link",
    invalidMagicLinkDescription:
      "Your magic link is invalid or expired. Please try again.",

    // Sidebar
    sidebarHome: "Dashboard",
    sidebarLock: "Lock",
    sidebarUnlock: "Unlock",
    sidebarLiveSupport: "Live Support",
    sidebarHelpCenter: "BuildingHub Help Center",
    sidebarHelpCenterURL: "https://support.buildinghub.io",
    sidebarPropertySettings: "Property Settings",
    sidebarHub: "Hub",
    sidebarAccounting: "Accounting",
    hubMenuErrorTitle: "Ooops! An error occurred.",
    hubMenuErrorDescription:
      "We had an error fetching all pages. Please try again later.",

    // Notifications
    notifications: "Notifications",
    notificationsAllFilter: "All notifications",
    notificationsActivitiesFilter: "Activities only",
    notificationsCommentsFilter: "Comments only",
    notificationsMessagesFilter: "Messages only",
    markAllAsRead: "Mark all as read",
    noNotifications: "No data, you are all set!",

    // Time Modal
    timeModalTitle: "Pick date and time",
    timeModalDate: "Date",
    timeModalDatePlaceholder: "Select date",
    timeModalDateError: "Please select valid date",
    timeModalTimezone: "Timezone",
    timeModalTimezonePlaceholder: "Select timezone",
    timeModalTimezoneError: "Timezone is required",

    // Next Date Generation Modal
    nextDateModalTitle: "Update Next Date",
    nextDateModalDate: "Next Date",
    nextDateModalDatePlaceholder: "Select next date",
    nextDateModalDateError: "Please select valid next date",

    // Review Modal
    reviewModalTitle: "Leave your review",
    reviewModalCommentLabel: "Add a comment (optional)",
    reviewModalCommentPlaceholder: "Add your comment here..",
    clickMe: "Click me!",
    bad: "Bad",
    excellent: "Excellent",

    // Share Modal
    shareModalTitle: "Share or invite someone",
    shareModalSelectLabel: "Enter name or email",
    shareModalNoData: "Nothing in this moment",

    // Sync Modal
    syncModalTitle: "Sync records",
    syncModalValueName: "Record index",
    syncModalValueNamePlaceholder: "Enter value..",
    syncModalValueType: "number",

    // Visit Time Modal
    visitTimeModalTitle: "Edit visit information",

    // Printer Modal
    printerModalTitle: "Update user printer",
    printerModalPrintTooltip:
      "If no printer is selected user will be able to use any printer manually, that is locally connected.",
    printerModalPrintPlaceholder: "Print in browser",

    // User Phone Modal
    userPhoneModalTitle: "Update user phone",
    userPhoneModalPhoneStep: "Update Number",
    userPhoneModalConfirmationStep: "Confirm Number",
    userPhoneModalPhoneDescription:
      "Please enter valid mobile number below. After that you will need to verify it in order to be added to the user profile.",
    userPhoneModalConfirmationDescription:
      "Please enter the 6 digit code we sent to the mobile number.",
    userPhoneModalPhone: "Mobile Number",
    userPhoneModalPhonePlaceholder: "No mobile number",
    userPhoneModalPhoneError: "Phone is required",
    userPhoneModalCode: "Code",
    userPhoneModalCodePlaceholder: "Enter code",
    userPhoneModalCodeError: "Code is required",
    userPhoneModalResendButton: "Resend Code",
    userPhoneModalResendSuccess: "Success! We sent a new code!",

    // Predefined Broadcast Modal
    predefinedBroadcastModalTitle: "Select template",
    predefinedBroadcastTitle: "Template",
    predefinedBroadcastPlaceholder: "Select template",
    predefinedBroadcastError: "Template is required",

    //Imports
    clickToUploadButton: "Click to upload",
    importFileLabel: "CSV File",
    downloadImportTemplateButton: "Download Import Template",
    importResultsTitle: "Your import is finished",
    importResultsDescription:
      "The data has been successfully imported, empowering you with valuable insights and streamlining your operations.",
    displayAllAction: "Display All",
    displayValidOnlyAction: "Display Valid Only",
    displayInvalidOnlyAction: "Display Invalid Only",
    importStep: "Select File",
    reviewStep: "Review",
    resultsStep: "Results",
    noAttachments: "No attachments",
    oneAttachment: "1 attachment",
    multipleAttachments: "{{count}} attachments",
  },
  globalSearch: {
    toastNoPropertyError:
      "Property was not provided. We can not open this feature",
    toastNoFeatureError:
      "Feature was not provided. We can not open this feature",
    noResults: "No results",
    noRecents: "No recents",
    noFeatures: "Currently there are no searchable features.",
    searchFor: "Search for:",
    startSearching: "You can start searching by typing above..",
    openTopbarShortcutTooltip: "Open Search (CMD + K)",
    searchFeature: "Search {{feature}}",
    goToFeature: "Go to {{feature}}",
    suggestedFilters: "Suggested Filters",
    toNavigate: "to navigate",
    toSelect: "to select",
  },
  stripe: {
    // Stripe Card Modal
    cardModalTitle: "Add a card",
    cardModalName: "Name on card",
    cardModalNamePlaceholder: "Enter name",
    cardModalNameError: "Name is required",
    cardModalCardNumber: "Card Number",
    cardModalCardNumberError: "Card number is required",
    cardModalCardExpiry: "Card Expiration",
    cardModalCardExpiryError: "Card expiry is required",
    cardModalCardCvc: "Card CVC",
    cardModalCardCvcError: "Card CVC is required",
    cardModalAddressLine1: "Address Line 1",
    cardModalAddressLine1Placeholder: "Enter address",
    cardModalAddressLine1Error: "Address is required",
    cardModalAddressLine2: "Address Line 2",
    cardModalAddressLine2Placeholder: "Enter secondary address",
    cardModalPostalCode: "Postal Code",
    cardModalPostalCodePlaceholder: "Enter postal code",
    cardModalPostalCodeError: "Postal code is required",
    cardModalCity: "City",
    cardModalCityPlaceholder: "Enter city",
    cardModalCityError: "City is required",
    cardModalCountry: "Country",
    cardModalCountryPlaceholder: "Select country",
    cardModalCountryError: "Country is required",
    cardModalState: "State",
    cardModalStatePlaceholder: "Enter state",
    toastStripeError:
      "We had an error establishing Stripe connection. Please try again later or contact support.",
    toastCardError: "Please check your card information and try again.",

    // Bank Account Modal
    bankAccountModalTitle: "Add new bank account (ACH)",

    bankName: "Bank Name",
    bankNameError: "Bank name is required",

    accountHolderName: "Account Holder Name",
    accountHolderNameError: "Account holder name is required",

    accountType: "Account Type",
    accountTypeError: "Account type is required",
    accountTypeChecking: "Checking",
    accountTypeSavings: "Savings",

    accountHolderType: "Account Holder Type",
    accountHolderTypeError: "Account holder type is required",
    accountHolderTypeIndividual: "Individual",
    accountHolderTypeCompany: "Company",

    routingNumber: "Routing Number",
    routingNumberError: "Routing number is required",

    accountNumber: "Account Number",
    accountNumberError: "Account number is required",

    confirmAccountNumber: "Confirm Account Number",
    confirmAccountNumberError: "Account numbers must match",

    toastApiError: "An error occurred while saving. Please try again later..",
    nextActionWarning: "Additional steps are needed: {{nextAction}}",
    verificationTitle: "Verification required",
    verificationDescription:
      "You will be prompted to verify your account on the following page. Verification up to 4 days to process.",
  },
  rrule: {
    // Modal
    updateModalTitle: "Update recurring settings",

    // Utils

    statusActive: "Active",
    statusPaused: "Paused",
    statusExpired: "Expired",

    frequencyHourly: "Hour(s)",
    frequencyDaily: "Day(s)",
    frequencyWeekly: "Week(s)",
    frequencyMonthly: "Month(s)",
    frequencyYearly: "Year(s)",

    endTypeDate: "Date",
    endTypeInterval: "Interval",
    endTypeNever: "Never",

    monthlyTypeMonthDay: "On day",
    monthlyTypePosDay: "Day position",

    posFirst: "First",
    posSecond: "Second",
    posThird: "Third",
    posFourth: "Fourth",
    posLast: "Last",

    weekdaySunday: "Sunday",
    weekdayShortSunday: "S",
    weekdayMonday: "Monday",
    weekdayShortMonday: "M",
    weekdayTuesday: "Tuesday",
    weekdayShortTuesday: "T",
    weekdayWednesday: "Wednesday",
    weekdayShortWednesday: "W",
    weekdayThursday: "Thursday",
    weekdayShortThursday: "T",
    weekdayFriday: "Friday",
    weekdayShortFriday: "F",
    weekdaySaturday: "Saturday",
    weekdayShortSaturday: "S",

    // Fields

    interval: "Repeat every",
    intervalPlaceholder: "Enter number",
    intervalError: "Interval is required (min: 1)",
    frequency: "Frequency",
    frequencyPlaceholder: "Select frequency",
    frequencyError: "Frequency is required",
    weeklyDays: "Days",
    weeklyDaysError: "Select at least one day",
    type: "Type",
    typeError: "Type is required",
    dayInMonth: "Day in month",
    dayInMonthPlaceholder: "Select day",
    dayInMonthError: "Month day is required",
    positionInMonth: "Position In Month",
    positionInMonthPlaceholder: "Select position",
    positionInMonthError: "Position in month is required",
    weekDay: "Day",
    weekDayPlaceholder: "Select day",
    weekDayError: "Day is required",
    endType: "Ends",
    endTypePlaceholder: "Select end type",
    endTypeError: "End type is required",
    count: "Count",
    countPlaceholder: "Enter count..",
    countError: "Count is required (min: 1)",
    endDate: "Date",
    endDateError: "Please select valid date",
  },
  activityLog: {
    title: "Activity log",
    noDataRecorded: "No data recorded",
    noChangesOccurred: "No changes occurred in this activity log.",

    // Tabs
    tabActivity: "Activity",
    tabComments: "Comments",
    tabAuthor: "Authored",
    tabParticipant: "Involved",

    // Comments
    commentPlaceholder: "Write comment here..",
    postInternalCommentAction: "Post as internal comment",

    // Print
    printLoadMoreButton: "Load more",
  },
  charges: {
    nameTableHeader: "Name",
    nameTableHeaderTooltip:
      "If type is Material then Material name is displayed. if type is Labor then Employee name is displayed.",
    quantityTableHeader: "Qty",
    total: "Total",
    billable: "Billable",
    billableTableValue: "Yes",
    notBillableTableValue: "No",
    type: "Type",
    labor: "Labor",
    record: "Record",
    existingRecords: "Existing Records",

    linkedChargeTitle: "Linked Charge",
    linkedChargeUpdateDescription:
      "This charge is linked to another record. Do you want to apply these changes in that linked record too?",
    linkedChargeDeleteDescription:
      "This charge is linked to another record. Do you want to delete this charge in that linked record too?",
    updateAll: "Update All Records",
    updateSingle: "Update This Record Only",
    deleteAll: "Delete All Records",
    deleteSingle: "Delete This Record Only",

    // Modal

    addTitle: "Add charge",
    editTitle: "Edit charge",
    whichItem: "Which item are you adding?",
    employee: "Employee",
    employeePlaceholder: "Select employee",
    employeeError: "Employee is required",
    laborRate: "Labor rate",
    laborRatePlaceholder: "Select labor rate",
    laborRateError: "Labor rate is required",
    datePerformed: "Date Performed",
    material: "Material",
    materialPlaceholder: "Select material",
    materialError: "Material is required",
    hours: "Hours",
    hoursPlaceholder: "Enter hours",
    quantity: "Quantity",
    quantityPlaceholder: "Enter quantity",
    price: "Price",
    pricePlaceholder: "Enter price",
    taxable: "Taxable?",
    markup: "Markup",
    markupPlaceholder: "No markup",
    markupAmount: "Markup Amount (per unit)",
    markupAmountPlaceholder: "No markup",
    markupTaxable: "Taxable?",
    tax: "Tax",
    taxPlaceholder: "No tax",
    taxError: "Tax is required",
    taxAmount: "Tax Amount",
    taxAmountPlaceholder: "No tax",
    notes: "Notes",
    notesPlaceholder: "Enter notes",
    baseTotalLaborLabel: "Labor ({{hours}}hrs * {{price}})",
    baseTotalMaterialLabel: "Material ({{quantity}}qty * {{price}})",
    markupTotalLabel: "Markup ({{markupAmount}} per unit)",
    taxTotalLabel: "Tax ({{taxAmount}}%)",
    totalPriceLabel: "Total price",

    // Filters

    allFilter: "All Charges",
    billableFilter: "Bil",
    notBillableFilter: "Not Billable Charges",
  },
  login: {
    header: "Log in to your account",
    description: "Enter your work email and password",
    magicDescription: "Enter your work email to start",
    verifyHeader: "Verify to login",
    verifyDescription: "Please select one option to verify login",
    verifyEmailDescription:
      "Please enter the 6 digit code you received on your email",
    verifyPhoneDescription:
      "Please enter the 6 digit code you received on your phone",
    email: "Email",
    emailPlaceholder: "Enter email",
    emailError: "Please enter a valid email",
    password: "Password",
    passwordPlaceholder: "Enter password",
    passwordError: "Password is required - should be 6 chars minimum.",
    code: "Code",
    codePlaceholder: "Enter code",
    codeError: "Please enter valid code",
    rememberMe: "Remember Me",
    resetPassword: "Reset password",
    sendMagicLink: "Send me a magic link",
    loginButton: "Login",
    verifyCode: "Verify code",
    resendCode: "Resend code",
    selectDifferentOption: "Select different option",
    or: "or",
    backToLogin: "Back to login",
    useEmailPassword: "Use email and password",
    verifyByEmail: "Verify by Email",
    verifyByPhone: "Verify by Phone",
    magicLinkSentTitle: "Magic link sent!",
    magicLinkSentText: "Check your email for your magic link.",
    alertErrorTitle: "Error!",
    alertErrorDescription:
      "Something went wrong while trying to login. Please contact BH Support if error keeps showing.",
    resendCodeSuccess: "Success! We sent a new code!",
  },
  forgottenPassword: {
    header: "Reset Password",
    description:
      "Enter your email address and we will send you a link to reset your password",
    resetButton: "Reset Password",
    backToLogin: "Remembered your password?",
    signIn: "Sign in!",
    email: "Email",
    emailPlaceholder: "Enter email",
    emailError: "Please enter a valid email",
    emailSentTitle: "Email sent!",
    emailSentText: "Check your email for further instructions.",
    alertErrorTitle: "Error!",
  },
  geneaDoors: {
    nameColumn: "Name",
    scheduleColumn: "Schedule",
    lockUnlockColumn: "Lock/Unlock",
    quickGrantColumn: "Quick Grant",
    isScheduled: "Is scheduled",
    unknownController: "Unknown Controller",
    noDataTitle: "No data",
    noDataDescription:
      "No doors available in this moment. Please try again later..",
    alreadyUnlocked: "Door is already unlocked",
    statusLocked: "Locked",
    statusUnlocked: "Unlocked",
    quickGrantYes: "Yes",
    quickGrantNo: "No",
  },
  broadcast: {
    tableTitle: "All messages",
    newTitle: "Add new message",
    informationStep: "Message Information",
    notificationsStep: "Notifications",
    sendVia: "Send via",
    sendViaError:
      "Please select how you would like this message to be sent by! (*)",
    email: "Email",
    sms: "SMS",
    app: "App",
    subject: "Subject",
    subjectPlaceholder: "Enter message subject",
    subjectError: "Subject is required",
    message: "Message",
    messagePlaceholder: "Enter message..",
    messageError: "Message is required",
    emailBody: "Email body",
    emailBodyPlaceholder: "Enter email body..",
    emailBodyError: "Email body is required",
    attachments: "Attachments",
    attachmentsTooltip:
      "Attachments will be sent only through email messages. All attachments combined can not exceed 10mb.",
    attachmentSizeError: "All attachments combined can not exceed 10mb.",
    recipientsUserColumn: "User",
    recipientsTimeColumn: "Time",
    sentVia: "Sent via",
    createdBy: "Created By",
    notifyUsersLabel: "To Users (Optional)",
    users: "Users",
    notifyCompaniesLabel: "To Company (Optional)",
    companies: "Companies",
    companiesCount: "{{count}} companies",
    allCompanies: "All companies",
    allCompaniesTooltip: "All accessible companies available for {{name}}.",
    notifyRolesLabel: "To Role (Optional)",
    roles: "Roles",
    rolesCount: "{{count}} roles",
    allRoles: "All roles",
    allRolesTooltip: "All accessible roles available for {{name}}.",
    notifyCompanyTypesLabel: "To Company Type (Optional)",
    companyTypes: "Company Types",
    companyTypesCount: "{{count}} company types",
    allCompanyTypes: "All company types",
    allCompanyTypesTooltip:
      "All accessible company types available for {{name}}.",
    noAttachments: "This message has no attachments.",
    event: "Event",
    minimizedTitle: "Message - {{subject}}",
    createdAt: "Created At",
    save: "Send Message",
    useTemplate: "Use template",
  },
  coi: {
    active: "Active",
    pending: "Pending",
    compliant: "Compliant",
    notCompliant: "Not Compliant",
    expiresToday: "Today",
    expiresSoon: "Expires Soon",
    expired: "Expired",
    pdf: "PDF",
    pdfError: "PDF is required",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    status: "Status",
    statusPlaceholder: "Select status",
    statusError: "Status is required",
    validFrom: "Valid From",
    validFromPlaceholder: "Select date",
    validFromError: "Please select valid from date",
    validTo: "Valid To",
    validToPlaceholder: "Select date",
    validToError: "Please select valid to date",
    notes: "Notes",
    notesPlaceholder: "Enter note",
    contactEmails: "Contact Emails",
    contactEmailsPlaceholder: "Enter email..",
    toggleRenew: "Toggle Renew Mode",
    toggleNormal: "Toggle Normal Mode",
    removePdf: "Remove PDF",
    renew: "Renew",
    createdBy: "Created By",
    createdByEmail: "Created By Email",
    dateCreated: "Date Created",
    expiresIn: "Expires In",
    documentTitle: "New Document",
    tableDatepickerPlaceholder: "Filter by expiration",
    filterTitle: "Filter",
    filterCompanyTypeLabel: "Company Types",
    filterCompanyTypePlaceholder: "Select company types",
    filterCompanyLabel: "Companies",
    filterCompanyPlaceholder: "Select companies",
    filterExpirationLabel: "Expiration",
    filterExpirationPlaceholder: "Select..",
  },
  companies: {
    deactivateTitle: "Deactivate {{company}}",
    deactivateUsers_one:
      "{{count}} user will be deactivated - this is their only company here.",
    deactivateUsers_other:
      "{{count}} users will be deactivated - this is their only company here.",
    deactivateLosingAccess_one:
      "{{count}} user will lose access to this company only and keeps their other one.",
    deactivateLosingAccess_other:
      "{{count}} users will lose access to this company only and keep their other one.",
    deactivateSubscriptions_one:
      "{{count}} subscription will be cancelled at the end of its paid period.",
    deactivateSubscriptions_other:
      "{{count}} subscriptions will be cancelled at the end of their paid period.",
    deactivateNothingAffected: "No users or subscriptions are affected.",
    deactivateRules:
      "Everything billed to this company ends at period end, even for people who stay. If this was someone's primary company, their next company becomes primary. Cancellations send the usual email.",
    deactivateConfirmAction: "Deactivate Company",
    deactivateDone:
      "Company deactivated: {{users}} users deactivated, {{lostAccess}} lost access, {{subscriptions}} subscriptions cancelled.",
    deactivateFailures_one:
      "{{count}} subscription could not be cancelled - cancel it manually:",
    deactivateFailures_other:
      "{{count}} subscriptions could not be cancelled - cancel them manually:",
    newTitle: "Add new company",
    newVendorTitle: "Add new vendor",
    tenantCompanyType: "Tenant",
    vendorCompanyType: "Vendor",
    managementCompanyType: "Management",
    otherCompanyType: "Other",
    usersCountTableHeader: "Users",
    vendorIdTableHeader: "Tenant ID",
    logo: "Logo",
    title: "Name",
    titlePlaceholder: "Enter company name",
    titleError: "Company name is required",
    titleDescription: "The official name of the company",
    taxCode: "Tax ID",
    taxCodePlaceholder: "Enter tax ID",
    taxCodeDescription: "Company tax identification number",
    vendorId: "Company ID (Vendor ID)",
    vendorIdPlaceholder: "Enter ID",
    vendorIdDescription: "Company ID (Vendor ID) of the company",
    type: "Type",
    typePlaceholder: "Select company type",
    typeError: "Company type is required",
    typeDescription: "The type of company relationship",
    building: "Building",
    buildingPlaceholder: "Select building",
    buildingDescription: "Associated building",
    floor: "Floor",
    floorPlaceholder: "Select floor",
    floorDescription: "Associated floor",
    suite: "Suite",
    suitePlaceholder: "Select suite",
    suiteDescription: "Associated suite",
    phone: "Phone",
    phonePlaceholder: "Enter phone number",
    phoneDescription: "Company contact number",
    address: "Address",
    addressPlaceholder: "Enter address",
    addressDescription: "Street address",
    city: "City",
    cityPlaceholder: "Enter city",
    cityDescription: "City name",
    country: "Country",
    countryPlaceholder: "Enter country",
    countryDescription: "Country name",
    zip: "ZIP Code",
    zipPlaceholder: "Enter ZIP code",
    zipDescription: "Postal/ZIP code",
    state: "State/Province",
    statePlaceholder: "Enter state/province",
    stateDescription: "State or province name",
    billingEmail: "Billing Email",
    billingEmailPlaceholder: "Enter billing email",
    billingEmailDescription: "Billing email address",
    billTo: "Bill To",
    billToPlaceholder: "Enter bill to",
    billToDescription: "Bill to address",
    duplicateTitle: "Duplicate Company Alert",
    duplicateDescription:
      "Similar company names found in your portfolio. Consider selecting from the list below to add to your property, rather than creating a new entry.",
    existsInPortfolio: "Exists in Portfolio",
    existsInProperty: "Exists in Property",
    existsInPropertyToast: "This company already exists in the property",
    addCompanyButton: "Add Company",
    addNewCompanyButton: "Add New Company",
    basicInformationTab: "Basic Information",
    addressTab: "Address",
    activityTab: "Activity",
    billingTab: "Billing",
    filterTitle: "Filter",
    filterActiveLabel: "Company Types",
    filterActivePlaceholder: "Select company types",
  },
  subscriptionCleanup: {
    billingTabTitle: "Cleanup",
    description:
      "Subscriptions still running for people who were deactivated, deleted or are no longer at this property, or whose company is inactive here. Cancelling ends them at the end of the paid period and sends no email.",
    allTab: "All",
    reasonUserDeactivated: "User deactivated",
    reasonUserDeleted: "User deleted",
    reasonUserNotAtProperty: "No longer at this property",
    reasonCompanyInactive: "Company inactive",
    numberColumn: "#",
    reasonColumn: "Reason",
    statusColumn: "Status",
    nextChargeColumn: "Next Charge",
    cancelSelectedAction: "Cancel Selected",
    cancelConfirm_one:
      "Cancel {{count}} subscription at the end of its paid period? No email is sent.",
    cancelConfirm_other:
      "Cancel {{count}} subscriptions at the end of their paid period? No email is sent.",
    progress: "Cancelling {{done}} of {{total}}…",
    resultCanceled_one: "{{count}} subscription cancelled at period end.",
    resultCanceled_other: "{{count}} subscriptions cancelled at period end.",
    resultFailed_one: "{{count}} could not be cancelled:",
    resultFailed_other: "{{count}} could not be cancelled:",
  },

  subscription: {
    billingTabTitle: "Subscriptions",
    title: "Subscription",
    statusActive: "Active",
    statusScheduled: "Scheduled",
    statusTrial: "Trial",
    statusPastDue: "Past Due",
    statusUnpaid: "Unpaid",
    statusIncomplete: "Incomplete",
    statusIncompleteExpired: "Incomplete Expired",
    statusCancelScheduled: "Cancelation Scheduled",
    statusCanceled: "Canceled",
    plan: "Plan",
    product: "Product",
    price: "Price",
    type: "Type",
    billingPeriod: "Billing Period",
    requestedBy: "Requested By",
    company: "Company",
    category: "Category",
    status: "Status",
    startDate: "Start date",
    createdOn: "Created on",
    noCoupon: "No coupon",
    waiver: "Waiver",
    viewAllWaivers: "View all waivers",
    waiverTableTitle: "All Waivers",
    waiverTitleTableHeader: "Waiver Template Title",
    waiverActiveTableHeader: "Active",
    waiverActiveTableValue: "Active",
    waiverInactiveTableValue: "Inactive",
    waiverLinkTableHeader: "Link",
    waiverLinkTableValue: "Waiver Link",
    waiverNoLinkTableValue: "No PDF",
    cancelTitle: "Cancel Subscription",
    cancelDescription:
      "Please select the time when you want cancelation to happen.",
    cancelNow: "Now",
    cancelNextCharge: "On next charge", // Used with date: "On next charge (MM/DD/YYYY)"
    cancelType: "Cancelation Type",
    cancelTypeError: "Please select cancelation type",
    switchTitle: "Switch Subscription Plan",
    switchPriceButton: "Select",
    switchPriceError: "Price is required",
    switchNoPrices:
      "Sorry, this product does not have any prices eligible to switch to.",
    switchCouponRemovedToast: "Coupon removed successfully.",
    switchCouponErrorToast: "Please enter valid coupon.",
    switchApplyCouponButton: "Apply coupon",
    filterTitle: "Filter Subscriptions",
    filterProductLabel: "Products",
    filterProductPlaceholder: "Select products",
    filterStatusLabel: "Status",
    filterStatusPlaceholder: "Select status",
    filterRequestedByLabel: "Requested By",
    filterRequestedByPlaceholder: "Select users",
    filterCompanyLabel: "Companies",
    filterCompanyPlaceholder: "Select companies",
    filterBillingPeriodLabel: "Billing Period",
    filterBillingPeriodPlaceholder: "Select billing period",
    successSwitchPlanToast: "Successfully switched subscription plan",
    couponTitle: "Coupon",
    coupon: "Coupon",
    couponPlaceholder: "Enter coupon",
    totalOverviewTitle: "Total",
    productPriceOverviewTitle: "Product Price",
    couponDiscountOverviewTitle: "Coupon discount",
    totalPriceOverviewTitle: "Total price",
    couponTestButton: "Test coupon",
    couponDiscountSuccessToast:
      "Success! You have been granted {{amount}} discount!",
    minimizedTitle: "Subscription #{{index}}",
    cancelAction: "Cancel",
    switchPlanAction: "Switch Plan",
    chargesTitle: "Charges",
    attachments: "Attachments",
    chargesAmountTableHeader: "Amount",
    chargesRefundedTableHeader: "Refunded",
    chargesInvoiceLinkTableHeader: "Invoice Link",
    chargesDateCreatedTableHeader: "Date Created",
    refundChargeAction: "Refund",
    refundChargeTitle: "Refund",
    refundChargeAmount: "Amount (Max: {{max}})",
    refundChargeAmountPlaceholder: "Enter amount to refund..",
    refundChargeAmountError: "Amount is required (Max: {{max}})",
  },
  invoices: {
    viewTitle: "Invoices",
    singleTitle: "Invoice",
    newTitle: "New invoice",
    recipientsTitle: "Update CC / BCC",
    minimizedTitle: "Invoice - {{index}}",

    payInvoice: "Pay Invoice",
    paymentMethod: "Payment Method",
    paymentMethodRequired: "Payment method is required",
    addNewPaymentMethod: "Add New Credit Card",
    payRemainingAmount: "Remaining Balance - {{amount}}",
    payOtherAmount: "Other Amount",
    amount: "Amount",
    total: "Total",
    totalPrice: "Total Price",
    serviceFee: "Service Fee",
    paymentSuccess: "Payment Successful!",
    amountError: "Amount is required",
    paymentStep: "Payment",
    confirmationStep: "Confirmation",
    invoiceDetails: "Invoice Details",
    stripeError: "Stripe is not enabled for this property.",
    creditCardPaymentError:
      "You are not allowed to pay invoices with credit card.",
    partialAmountMinError: "Amount must be greater than 0",
    partialAmountMaxError:
      "Amount must be less than or equal to the remaining balance - {{amount}}",

    company: "Company",
    companyError: "Company is required",

    email: "Email",
    emailError: "Email is required",

    cc: "CC",
    bcc: "BCC",
    ccBccDescription: "Separate emails with commas",

    invoiceDate: "Invoice Date",
    invoiceDateError: "Invoice date is required",

    dueDate: "Due Date",
    dueDateError: "Due date is required",

    notes: "Notes",
    noNotes: "No notes provided",

    billTo: "Bill To",
    noBillTo: "No bill to provided",

    attachmentsTitle: "Attachments",
    imageAttachmentsTitle: "Image Attachments",
    otherAttachmentsTitle: "Other Attachments",
    noImages: "No images",
    noOtherAttachments: "No other attachments",

    activityLogTitle: "Activity Log",
    informationTitle: "Information",
    chargesTitle: "Charges",
    paymentsTitle: "Payments",

    id: "ID",
    createdBy: "Created By",
    totalAmount: "Total Amount",
    paidAmount: "Paid Amount",
    status: "Status",
    openStatus: "Open",
    dueStatus: "Due",
    dueInDays: "Due in {{days}} days",
    dueToday: "Due today",
    overdueStatus: "Overdue",
    overdueDays: "Overdue {{days}} days",
    paidStatus: "Paid",
    overpaidStatus: "Overpaid",
    voidStatus: "Void",
    sentOn: "Sent on {{date}}",
    partiallyPaid: "Partially paid, {{amount}} due",

    deactivateAction: "Void",
    activateAction: "Unvoid",
    sendInvoiceAction: "Send Invoice",
  },
  customers: {
    allCustomersTitle: "All Customers",
    viewTitle: "Customers",
    singleTitle: "Customer",
    minimizedTitle: "Customer",
    id: "ID",
    company: "Company",
    balance: "Balance",
    email: "Email",
    phone: "Phone",
    billingAddress: "Billing Address",
    openBalance: "Open Balance",
    overduePayment: "Overdue Payment",
    notes: "Notes",
    notesPlaceholder: "Enter details",
    invoicesTitle: "Invoices",
    paymentsTitle: "Payments",
    allTransactionsTitle: "All Transactions",
    payBalanceAction: "Pay Balance",
    copyLinkAction: "Copy link",
    transactionId: "ID",
    transactionDate: "Date",
    transactionAmount: "Amount",
    transactionBalance: "Balance",
    transactionType: "Type",
    transactionCreatedBy: "Created By",
    currentBalance: "Current balance - {{amount}}",
    balanceDue: "Balance due - {{amount}}",
    otherAmount: "Other amount",
    selectInvoices: "Select invoice(s)",

    billingTitle: "Billing",
    openingBalance: "Opening Balance",
    openingBalanceDate: "Opening Balance As Of",
    asOfDate: "As of {{date}}",
    editOpeningBalanceTitle: "Opening Balance",
    openingBalanceAmount: "Amount",
    openingBalanceAmountPlaceholder: "0.00",
    openingBalanceAmountTooltip:
      "What this tenant owed before their BuildingHub history began. Use a negative amount if the tenant is in credit.",
    openingBalanceAmountRequired: "Amount is required",
    openingBalanceAsOfDate: "As of Date",
    openingBalanceAsOfDateRequired: "As of date is required",
    openingBalanceDescription:
      "The as of date must be before the tenant's first invoice or payment. Every balance and report for this tenant starts from this amount.",
    openingBalanceBooksClosed:
      "Books are closed through {{date}}, so earlier dates can't be selected.",
    openingBalanceLocked:
      "Books are closed through {{date}}. This opening balance can no longer be changed.",
    removeOpeningBalanceTitle: "Remove opening balance",
    removeOpeningBalanceDescription:
      "The opening balance of {{amount}} as of {{date}} will be removed. This tenant's balances and reports will be recalculated without it.",
    removeOpeningBalanceAction: "Remove",
  },
  customerPayments: {
    viewTitle: "Payments",
    singleTitle: "Customer Payment",
    minimizedTitle: "Customer Payment",
    newTitle: "New customer payment",
    id: "ID",
    createdBy: "Created By",
    company: "Company",
    paymentMethod: "Payment Method",
    stripeLink: "Stripe Link",
    status: "Status",
    statusSuccess: "Success",
    statusInProgress: "In Progress",
    statusPending: "Pending",
    statusFailed: "Failed",
    statusRefunded: "Refunded",
    statusPartialRefunded: "Partial Refunded",
    amount: "Amount",
    refundedAmount: "Refunded",
    allocatedAmount: "Allocated",
    createdAt: "Date",
    refundAction: "Refund",
    allocateAction: "Allocate",
    payBalanceAction: "Pay Balance",
    refundPayment: "Refund Payment",
    refundAmount: "Refund Amount",
    refundAmountError:
      "Amount is required and cannot be greater than the payment amount",
    refundMinError: "Amount must be greater than 0",
    refundMaxError: "Amount cannot exceed the payment amount - {{amount}}",
    invoicesTitle: "Invoices",
    invoiceNumber: "Invoice #",
    allocationAmount: "Allocate",
    totalPaymentAmount: "Total Payment",
    totalApplied: "Applied",
    totalUnapplied: "Unapplied",
    saveAllocations: "Save Allocations",
    overAllocated:
      "Allocations are {{amount}} more than the payment. Lower one before saving.",
    viewInStripeAction: "View in Stripe",
  },
  invoicePayments: {
    refundPayment: "Refund Payment",
    refundAmount: "Refund Amount",
    refundAmountError:
      "Amount is required and cannot be greater than the payment amount",
    refundMinError: "Amount must be greater than 0",
    refundMaxError: "Amount cannot exceed the payment amount - {{amount}}",

    id: "ID",
    createdBy: "Name",
    amount: "Amount",
    appFee: "App Fee",
    date: "Date",
    stripeLink: "Stripe Link",

    status: "Status",
    statusSuccess: "Success",
    statusInProgress: "In Progress",
    statusPending: "Pending",
    statusFailed: "Failed",
    statusRefunded: "Refunded",
    statusPartialRefunded: "Partial Refunded",

    paymentMethod: "Payment Method",
    creditCard: "Credit Card",
    ach: "ACH",

    refunded: "Refunded",
  },
  recurringInvoices: {
    viewTitle: "Recurring Invoices",
    singleTitle: "Recurring Invoice",
    newTitle: "New recurring invoice",
    recipientsTitle: "Update CC / BCC",
    minimizedTitle: "Recurring Invoice - {{index}}",

    company: "Company",
    companyError: "Company is required",

    email: "Email",
    emailError: "Email is required",

    cc: "CC",
    bcc: "BCC",
    ccBccDescription: "Separate emails with commas",

    notes: "Notes",
    noNotes: "No notes provided",

    billTo: "Bill To",
    noBillTo: "No bill to provided",

    attachmentsTitle: "Attachments",
    activityLogTitle: "Activity Log",
    chargesTitle: "Charges",
    paymentsTitle: "Payments",
    relatedRecordsTitle: "Related Records",

    id: "ID",
    createdBy: "Created By",
    totalAmount: "Total Amount",
    status: "Status",
    dateCreated: "Date Created",
    startDate: "Start Date",
    previousDate: "Previous Date",
    noPreviousDate: "No previous date",
    nextDate: "Next Date",
    noNextDate: "No next date",
    skippedDates: "Skipped Dates",
    hasSkippedDates: "Yes",
    noSkippedDates: "None",
    dueAfterDays: "Due After",
    dueAfterDaysTooltip:
      "Number of days until work order is due after generation",
    dueAfterDaysPlaceholder: "Enter number of days",
    interval: "Interval",

    tableDatepickerPlaceholder: "Filter by Next Date",

    addPayment: "Add Payment",
    paymentMethod: "Payment Method",
    paymentMethodRequired: "Payment method is required",
    addNewPaymentMethod: "Add New Credit Card",
    payRemainingAmount: "Remaining Balance - {{amount}}",
    payOtherAmount: "Other Amount",
    amount: "Amount",
    total: "Total",
    totalPrice: "Total Price",
    serviceFee: "Service Fee",
    paymentSuccess: "Payment successfully added!",
    amountError: "Amount is required",
    paymentStep: "Payment",
    confirmationStep: "Confirmation",
    invoiceDetails: "Invoice Details",
    invoiceDate: "Invoice Date",
    dueDate: "Due Date",
    stripeError: "Stripe is not enabled for this property.",
    creditCardPaymentError:
      "You are not allowed to pay invoices with credit card.",
    partialAmountMinError: "Amount must be greater than 0",
    partialAmountMaxError:
      "Amount must be less than or equal to the remaining balance - {{amount}}",
  },
  stripeInvoices: {
    billingTabTitle: "Invoices",
    title: "Invoice",
    minimizedTitle: "Invoice",
    number: "Invoice Number",
    category: "Category",
    product: "Product",
    amountDue: "Amount Due",
    amountPaid: "Amount Paid",
    requestedBy: "Requested By",
    company: "Company",
    createdOn: "Created on",
    status: "Status",
    linkLabel: "Invoice Link",
    paymentsTitle: "Payments",
    paymentId: "Payment ID",
    paymentStatus: "Status",
    paymentPrice: "Price",
    paymentDate: "Payment date",
    viewPaymentAction: "View Payment",
    viewSubscriptionAction: "View Subscription",
  },
  payments: {
    billingTabTitle: "Payments",
    title: "Payment",
    invoiceTitle: "Payment Invoice: {{number}}",
    minimizedTitle: "Payment",
    requestedBy: "Requested By",
    company: "Company",
    category: "Category",
    product: "Product",
    amount: "Amount",
    plan: "Plan",
    type: "Type",
    billingPeriod: "Billing Period",
    coupon: "Coupon",
    status: "Status",
    startDate: "Start Date",
    dateCreated: "Date Created",
    createdOn: "Created on",
    sourcesTitle: "Payment Sources",
    sourceName: "Name",
    sourceBrand: "Brand",
    sourceNumber: "Number",
    viewInvoiceAction: "View Invoice",
    viewSubscriptionAction: "View Subscription",
    succeededStatus: "succeeded",
  },
  adminProducts: {
    createdAt: "Date created",
    createdBy: "Created by",
    billingPeriodDaily: "Daily",
    billingPeriodWeekly: "Weekly",
    billingPeriodMonthly: "Monthly",
    billingPeriodYearly: "Yearly",
    priceTypeTrial: "Trial",
    priceTypeOneTime: "One Time",
    priceTypeRecurring: "Recurring",

    // Product
    detailsStep: "Details",
    pricingStep: "Pricing",
    accessStep: "Access",

    addProductTitle: "Add new product",
    editProductTitle: "Edit product",

    title: "Name",
    titlePlaceholder: "Enter product name",
    titleError: "Name is required",
    description: "Description",
    descriptionPlaceholder: "Enter description..",
    waiverTemplate: "Waiver Template",
    waiverTemplatePlaceholder: "Select waiver template",
    customFields: "Custom Fields",
    customFieldsDescription:
      "You can add custom fields that user can fill. Please use button below to add one.",
    customField: "Field Label",
    customFieldDescription:
      "Value of this field will be used as label/title for user.",
    customFieldPlaceholder: "Enter field label",
    customFieldRequired: "Required?",
    customFieldError: "Label is required",
    customFieldAddButton: "Add New Custom Field",
    prices: "Prices",
    priceActive: "Active",
    noPrices: "This product does not have any prices yet. Please add one.",
    newPriceButton: "Add new price",
    userSelectLabel: "To Users (Optional)",
    companySelectLabel: "To Company (Optional)",
    companyTypeSelectLabel: "To Company Type (Optional)",
    roleSelectLabel: "To Role (Optional)",
    users: "Users",
    companies: "Companies",
    roles: "Roles",
    companyTypes: "Company Types",

    toastMinOnePriceError: "Please add atleast one product price.",

    // Price
    addPriceTitle: "Add new price",
    editPriceTitle: "Edit price",

    priceTitle: "Name",
    priceTitlePlaceholder: "Enter price name",
    priceTitleError: "Name is required",

    priceDescription: "Description",
    priceDescriptionPlaceholder: "Enter description..",

    priceType: "Type",
    priceTypePlaceholder: "Select type",
    priceTypeError: "Type is required",

    priceTrialDays: "Trial Days",
    priceTrialDaysPlaceholder: "Enter number of days",
    priceTrialDaysError: "Days are required (min 1)",

    priceAmount: "Price",
    priceAmountPlaceholder: "Enter price",
    priceAmountError: "Price is required (min 0)",

    priceBillingPeriod: "Billing Period",
    priceBillingPeriodPlaceholder: "Select billing period",
    priceBillingPeriodError: "Billing period is required",

    priceBillingDay: "Billing Day",
    priceBillingDayPlaceholder: "Select day",
    priceBillingDayTooltip:
      "Please select a day of the month for your monthly payment to be drafted. If you do not select a day, the payment will be drafted each month on the same day as the initial purchase.",

    priceDateSelection: "Date selection required?",
    priceTimeSensitive: "Time sensitive price?",

    priceFrom: "From",
    priceTo: "To",

    priceTimezone: "Timezone",
    priceTimezonePlaceholder: "Select timezone",
    priceTimezoneError: "Timezone is required",

    billingDayOption: "Day {{day}} of the month",
  },
  products: {
    title: "Products",
    newSubscriptionTitle: "New Product Subscription",
    noProducts: "No products available",
    noProductsDescription: "No products available in this moment.",
    noPrices: "Product has no prices",
    noPricesDescription:
      "Product has no prices available. Please select different product.",
    unmatchedPriceError: "Error while trying to get product price.",
    total: "Total",
    couponDiscount: "Coupon discount",
    totalPrice: "Total price",

    // Fields
    requestedBy: "Requested By",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requester is required",

    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",

    date: "Start Date",
    dateError: "Please select valid start date",

    signature: "Signature",
    signatureError: "Signature is required",

    card: "{{name}}'s Card",
    cardPlaceholder: "Select card",
    cardError: "Card is required",
    addNewCardButton: "Add new card",

    coupon: "Coupon",
    couponPlaceholder: "Enter coupon..",
    applyCouponButton: "Apply coupon",

    // Waiver
    waiverTemplate: "Waiver Template",
    waiverTemplateDate: "Date",
    noWaiverTemplate: "This product does not have any waiver template.",

    // Confirmation
    oneTimeSuccess:
      "Success! You have successfully purchased {{product}}. Thank you for your purchase!",
    trialSuccess:
      "Success! You have successfully activated trial for {{product}}. Enjoy!",
    recurringSuccess:
      "Success! You have successfully subscribed to {{product}}. Enjoy!",

    // Validation messages
    signWaiverError: "Please sign the waiver",
    controlWalletError:
      "You do not have permission to access wallet of selected requester.",
    creditCardPaymentError:
      "Selected requester does not have permission to use credit card payment.",
    couponDiscountSuccess:
      "Success! You have been granted {{amount}} discount!",
    couponRemovedSuccess: "Coupon removed successfully.",
    invalidCoupon: "Please enter valid coupon.",

    // Price selector
    selectPrice: "Select",
  },
  concierge: {
    // Filters
    filterAllRecords: "All records",
    filterActiveRecords: "Active Records",
    filterInactiveRecords: "Inactive Records",
    filterExpiredRecords: "Expired Records",
    filterNotExpiredRecords: "Not Expired Records",

    // Categories
    manageCategoriesModalTitle: "Manage Categories",
    manageCategoriesTableTitle: "All Categories",
    addCategoryTitle: "Add new category",
    editCategoryTitle: "Edit category",
    categoryTitle: "Categories",
    categoryTitlePlaceholder: "Enter title",
    categoryTitleError: "Title is required",
    categoryIcon: "Icon",
    categoryIconPlaceholder: "Select icon",
    categoryIconError: "Icon is required",
    categoryOrderTableHeader: "Order",

    // Deals
    addDealTitle: "Add new deal",
    editDealTitle: "Edit deal",
    dealTitle: "Title",
    dealTitlePlaceholder: "Enter title name",
    dealTitleError: "Title is required",
    dealCategory: "Category",
    dealCategoryPlaceholder: "Select parent group",
    dealCategoryError: "Category is required",
    dealDiscount: "Discount",
    dealDiscountPlaceholder: "Enter discount",
    dealWebsite: "Website",
    dealWebsitePlaceholder: "Enter website",
    dealValidBy: "Valid by",
    dealValidByPlaceholder: "No expiration",
    dealDescription: "Description",
    dealDescriptionPlaceholder: "Add description",
    dealImage: "Image",
    dealImageError: "Image is required",
    dealPopupDescription: "Popup Description",
    dealPopupDescriptionPlaceholder: "Add popup description",
    dealCodeCheckbox: "Code - copy and paste code on the website",
    dealCode: "Code",
    dealCodePlaceholder: "Enter code",
    dealCodeError: "Code is required",
    dealLinkCheckbox: "Link - copy and visit link provided",
    dealLink: "Link",
    dealLinkPlaceholder: "Enter link (starts with: https://)",
    dealLinkError: "Link is required",
    dealPdfCheckbox: "PDF file - view and download file",
    dealPdf: "PDF",
    dealPdfError: "PDF is required",
    dealRedeemOptionError: "Please select at least one redeem option",
    dealRedeemOptions: "Redeem options (click to copy):",
    codeCopied: "Code has been copied to your clipboard!",
    linkCopied: "Link has been copied to your clipboard!",
    pdfCopied: "PDF link has been copied to your clipboard!",
    copyCode: "Copy Code",
    openLink: "Open Link",
    openPdf: "Open PDF",

    // Other
    newDealAction: "Add new record",
    newCategoryAction: "Add new category",
    manageCategoriesAction: "Manage categories",
    dealExpiresOn: "Expires on: {{date}}",
    dealNoExpiration: "No expiration",
    dealRedemptions: "redemptions",
    dealRedemptionTooltip:
      "{{uniqueCount}} unique redemption. {{totalCount}} total redemptions",
  },
  vendorAccess: {
    // Table
    id: "ID",
    tableCompanyColumn: "Vendor Name",
    tableLastCheckInColumn: "Last Check In",
    tableStatusColumn: "Status",
    coiLink: "COI Link",
    coiStatus: "COI Status",

    // Filters
    filterTitle: "Filter Vendor Access",
    filterCompanyLabel: "Company",
    filterCompanyPlaceholder: "Select company",
    filterBuildingPlaceholder: "All buildings",

    // Visit Information
    visitType: "Visit type",
    visitSingleDayOption: "Single Day",
    visitMultiDayOption: "Multi Day",
    visitPeriodOption: "Period",
    visitDateLabel: "Date",
    visitAddNewDateButton: "Add new date",
    visitDateError: "Please select valid date",
    allDayAccess: "All day access",
    startTime: "Start Time",
    startTimePlaceholder: "Select time",
    startTimeError: "Please select valid start time",
    endTime: "End Time",
    endTimePlaceholder: "Select time",
    endTimeError: "Please select valid end time",
    timezone: "Timezone",
    timezonePlaceholder: "Select timezone",
    timezoneError: "Please select valid timezone",
    notes: "Notes",
    notesPlaceholder: "Enter notes",

    // New Guest
    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required.",
    requestedBy: "Host",
    requestedByPlaceholder: "Select host",
    requestedByError: "Host is required",
    company: "Host Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    coi: "COI",
    coiPlaceholder: "Select COI",
    coiError: "COI is required",
    notifyUsers: "Notify Users",
    notifyUsersPlaceholder: "Select users to notify",
    newVendorAccessInformationStep: "Vendor Information",
    newVendorAccessVisitInformationStep: "Visit Information",
    newVendorAccessNotificationsStep: "Notifications",
    newVendorAccessTitle: "Register Vendor",

    // Notifications
    notifyButton: "Notify",
    notifyPreview: "Notify {{count}} users",

    // Actions & Messages
    checkInAction: "Check-in",
    printBadgeAction: "Print badge",
    activateAction: "Activate Vendor",
    approveAction: "Approve Vendor",
    cancelAction: "Cancel Guest",
    bulkCancelStatusAction: "Cancel",
    bulkApproveStatusAction: "Approve",
    canCheckInToday: "Vendor can check-in today",
    alreadyCheckedIn: "Vendor already checked-in today",
    cannotCheckIn: "Vendor can not check-in today",

    toastCheckInSuccess: "Check-in successful!",
    toastCheckInSuccessWithPrint: "Check-in successful! Printing badge..",
    toastCheckInSuccessErrorCount:
      "Check-in successful! {{successCount}} records were updated. {{errorCount}} records were skipped.",
    toastCheckInError: "Check-in error!{{errorMessages}}",
    toastCheckInErrorCount:
      "Check-in error! {{errorCount}} records were skipped.{{errorMessages}}",
    toastBulkCheckInError: "Please first select records you want to check-in.",

    toastPrintSuccess: "Print successful!",
    toastPrintError: "Print error!",
    toastPrintSuccessErrorCount:
      "Print successful! {{successCount}} records were updated. {{errorCount}} records were skipped.",
    toastPrintErrorCount:
      "Print error! {{errorCount}} records were skipped.{{errorMessages}}",
    toastPrintNotification:
      "Your file is being generated! We will notify you when file is ready.",
    toastPrintSuccessNotificationErrorCount:
      "Your file is being generated! We will notify you when file is ready! {{successCount}} records were updated. {{errorCount}} records were skipped.",
    toastBulkPrintBadgeError:
      "Please first select records you want to print badge.",

    toastApproveVendorNote:
      "NOTE: This record has already been approved. Updating this record will change the status of this guest back to PENDING.",

    // Status
    statusActive: "Active",
    statusCanceled: "Canceled",
    statusWaitingApproval: "Waiting Approval",

    // Badge Print
    badgePrintButton: "Print",

    // Other
    floor: "Floor",
    suite: "Suite",
    visitDate: "Visit Date",
    visitTime: "Visit Time",
    visitInformation: "Visit Information", // Used in single guest page section title
    dateAllowed: "Date allowed",
    timeAllowed: "Time allowed",
    allDayAccessTooltip:
      "This user can come in any period within the selected dates",
    multiDayVisit: "Multi Day Visit",
    noNotesDefault: "No notes",
    minimizedTitle: "Vendor Access - {{code}}",
    attachments: "Attachments",
    createdBy: "Created By",
    createdAt: "Date Created",
  },
  guests: {
    // Table
    tableImportAction: "Import Guests",
    id: "ID",
    tableGuestColumn: "Guest",
    tableLastCheckInColumn: "Last Check In",
    tableStatusColumn: "Status",

    // Filters
    filterTitle: "Filter Guests",
    filterCompanyLabel: "Company",
    filterCompanyPlaceholder: "Select company",
    filterBuildingPlaceholder: "All buildings",

    // Guest Form Fields
    firstName: "First Name",
    firstNamePlaceholder: "Enter first name",
    firstNameError: "First name can not be empty!",
    lastName: "Last Name",
    lastNamePlaceholder: "Enter last name",
    lastNameError: "Last name can not be empty!",
    email: "Email",
    emailPlaceholder: "Enter email",
    emailError: "Please provide valid email address!",
    companyTitle: "Guest Company / Title",
    companyTitlePlaceholder: "Enter guest company / title",

    // Visit Information
    visitType: "Visit type",
    visitSingleDayOption: "Single Day",
    visitMultiDayOption: "Multi Day",
    visitPeriodOption: "Period",
    visitDateLabel: "Date",
    visitAddNewDateButton: "Add new date",
    visitDateError: "Please select valid date",
    allDayAccess: "All day access",
    startTime: "Start Time",
    startTimePlaceholder: "Select time",
    startTimeError: "Please select valid start time",
    endTime: "End Time",
    endTimePlaceholder: "Select time",
    endTimeError: "Please select valid end time",
    timezone: "Timezone",
    timezonePlaceholder: "Select timezone",
    timezoneError: "Please select valid timezone",
    notes: "Notes",
    notesPlaceholder: "Enter notes",

    // New Guest
    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required.",
    requestedBy: "Host",
    requestedByPlaceholder: "Select host",
    requestedByError: "Host is required",
    company: "Host Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    notifyUsers: "Notify Users",
    notifyUsersPlaceholder: "Select users to notify",
    newGuestInformationStep: "Guest Information",
    newGuestVisitInformationStep: "Visit Information",
    newGuestNotificationsStep: "Notifications",
    newGuestTitle: "Register guest",
    newGuestFormAddLabel: "Add Guest", // used also for button
    newGuestFormEditLabel: "Edit Guest", // used also for button
    newGuestFormCancelButton: "Cancel",
    newGuestAllGuestsListLabel: "Guests",
    newGuestAllGuestsRemoveAllButton: "Remove all guests",
    toastNewGuestRequiredFieldsError: "Please fill required fields",
    toastNewGuestImportError:
      "Please upload valid CSV with atleast one valid guest",
    toastNewGuestMinimumError: "Please add atleast one valid guest",
    toastNewGuestValidError: "Please enter valid guest data.",
    toastNewGuestNoValidError:
      "Looks like you don't have any valid guest. Please check your guests data again.",
    toastNewGuestListErrorTooltip:
      "Any guest with error will be skipped from creation. Errors: {{errors}}",

    // Import
    importLabel: "Import Guest List",
    importDescription:
      "Upload your guest list as a CSV or XLSX file using the CSV template columns.",
    importTemplateLink: "Get the template here.",

    // Notifications
    notifyButton: "Notify",
    notifyPreview: "Notify {{count}} users",

    // Actions & Messages
    checkInGuestAction: "Check-in",
    printBadgeAction: "Print badge",
    activateGuestAction: "Activate Guest",
    approveGuestAction: "Approve Guest",
    cancelGuestAction: "Cancel Guest",
    bulkCancelStatusAction: "Cancel",
    bulkApproveStatusAction: "Approve",
    resendEmailAction: "Resend welcome email",
    canCheckInToday: "Guest can check-in today",
    alreadyCheckedIn: "Guest already checked-in today",
    cannotCheckIn: "Guest can not check-in today",

    toastCheckInSuccess: "Check-in successful!",
    toastCheckInSuccessWithPrint: "Check-in successful! Printing badge..",
    toastCheckInSuccessErrorCount:
      "Check-in successful! {{successCount}} records were updated. {{errorCount}} records were skipped.",
    toastCheckInError: "Check-in error!{{errorMessages}}",
    toastCheckInErrorCount:
      "Check-in error! {{errorCount}} records were skipped.{{errorMessages}}",
    toastBulkCheckInError: "Please first select records you want to check-in.",

    toastPrintSuccess: "Print successful!",
    toastPrintError: "Print error!",
    toastPrintSuccessErrorCount:
      "Print successful! {{successCount}} records were updated. {{errorCount}} records were skipped.",
    toastPrintErrorCount:
      "Print error! {{errorCount}} records were skipped.{{errorMessages}}",
    toastPrintNotification:
      "Your file is being generated! We will notify you when file is ready.",
    toastPrintSuccessNotificationErrorCount:
      "Your file is being generated! We will notify you when file is ready! {{successCount}} records were updated. {{errorCount}} records were skipped.",
    toastBulkPrintBadgeError:
      "Please first select records you want to print badge.",

    toastApproveGuestNote:
      "NOTE: This record has already been approved. Updating this record will change the status of this guest back to PENDING.",

    // Status
    statusActive: "Active",
    statusCanceled: "Canceled",
    statusWaitingApproval: "Waiting Approval",

    // Badge Print
    badgePrintButton: "Print",

    // Other
    floor: "Floor",
    suite: "Suite",
    visitDate: "Visit Date",
    visitTime: "Visit Time",
    visitInformation: "Visit Information", // Used in single guest page section title
    dateAllowed: "Date allowed",
    timeAllowed: "Time allowed",
    allDayAccessTooltip:
      "This user can come in any period within the selected dates",
    multiDayVisit: "Multi Day Visit",
    noNotesDefault: "No notes",
    minimizedTitle: "Guest - {{name}}",
    attachments: "Attachments",
    createdBy: "Created By",
    createdAt: "Date Created",
    checkInGuestDescription:
      "You are about to check-in this guest. But this guest's status indicates they cannot check in today. Do you still want to continue?",
    checkInGuestTitle: "Check-in Guest",

    // Watchlist Checker
    watchlistErrorTitle: "Error while checking watchlist",
    watchlistNoDataTitle: "No data",
    watchlistMultipleTitle: "These visitors appears on the watchlist",
    watchlistSingleTitle: "This visitor appears on the watchlist",
    watchlistErrorDescription:
      "There was an error while checking if guest is on watchlist. You can still check-in guest if you want by clicking button below.",
    watchlistNoMatchesDescription:
      "Guest is not on watchlist. You can still check-in guest if you want by clicking button below.",
    guestWatchlistViewRecordButton: "View record",
  },
  incidents: {
    id: "ID",
    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required",
    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    floor: "Floor",
    floorPlaceholder: "Select floor",
    suite: "Suite",
    suitePlaceholder: "Select suite",
    status: "Status",
    statusPlaceholder: "Select status",
    details: "Details",
    detailsPlaceholder: "Enter incident details",
    noDetailsDefault: "No details",
    issueType: "Issue Type",
    issueTypePlaceholder: "Select issue type",
    issueTypeError: "Issue type is required",
    requestedBy: "Requested By",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requester is required",
    requestedByEmail: "Requested by email",
    requestedByCompany: "Requested by company",
    createdBy: "Created By",
    dateCreated: "Date Created",
    startDate: "Incident Date",
    attachments: "Attachments",
    imageAttachments: "Image Attachments",
    otherAttachments: "Other Attachments",
    noImages: "No images",
    noOtherAttachments: "No other attachments",
    filterTitle: "Filter Incidents",
    filterBuildingPlaceholder: "All buildings",
    minimizedTitle: "Incident - {{index}}",
    newTitle: "Create a incident",
    watchersButton: "Watchers",
    watchersPreview: "{{count}} users watching",
    customFieldsTitle: "Additional Information",
    activityLogTitle: "Activity Log",
  },
  leasingHub: {
    newTitle: "New Lease",
    title: "Lease", // Used in single lease page title
    minimizedTitle: "Lease #{{index}}",
    leaseStart: "Lease Commencement", // Instead of startDate; Used in table column header and single lease page
    leaseEnd: "Lease Expiration", // Instead of endDate; Used in table column header and single lease page
    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    status: "Status",
    statusActive: "Active",
    statusExpired: "Expired",
    suites: "Suites",
    suitesPlaceholder: "Select suites",
    suitesError: "Suite is required",
    suitesCount: "{{count}} suites",
    suitesMultiple: "Multiple suites",
    chargeType: "Charge Type",
    chargeTypePlaceholder: "Select charge type",
    chargeTypeError: "Charge type is required",
    startDate: "Start Date",
    startDateError: "Start date is required",
    endDate: "End Date",
    endDateError: "End date is required",
    amount: "Amount",
    amountPlaceholder: "Enter amount",
    amountError: "Amount is required",
    amountUnitTotal: "Total",
    amountUnitMonthly: "Monthly",
    amountUnitYearly: "Yearly",
    rentFootage: "Rentable Square Footage",
    rentFootagePlaceholder: "Enter square footage",
    attachments: "Attachments",
    lateFeeAmount: "Late Fee Amount",
    lateFeeAmountPlaceholder: "Enter late fee amount",
    lateFeeUnit: "Late Fee Unit",
    lateFeeUnitValue: "$ - Amount",
    lateFeeUnitPercent: "% - Percentage",
    gracePeriod: "Grace Period",
    gracePeriodPlaceholder: "Enter number of days",
    rentTotal: "Total",
    createdBy: "Created By",
    createdAt: "Created At",
    newLeaseInformationTitleReview: "Lease Information",
    newLeaseRentTitleReview: "Rent Information",
    addRentTitle: "Add Rent",
    editRentTitle: "Edit Rent",
    rentSchedule: "Rent Schedule",

    // Steps
    informationStep: "Lease Information",
    rentStep: "Rent Information",
    reviewStep: "Review",

    // Actions & Buttons
    adderButton: "Add",
    automateAction: "Automate",
    addRentAction: "Add Rent",
    removeAllButton: "Remove All",

    // Increaser
    increaserTitle: "Automate",
    increaseAmount: "Increase By",
    increaseAmountError: "Amount is required",
    increaseByMonthOption: "$/Month",
    increaseByYearOption: "$/Year",
    increaseByPercentMonthOption: "%/Month",
    increaseByPercentYearOption: "%/Year",

    // Toasts
    toastAutomationError:
      "Automation is available only for Monthly or Yearly types.",
    toastAdderRequiredFieldsError: "Please fill in all required fields",
    toastRentRequiredFieldsError: "Please fill all rent fields",
    toastRentMinimumError: "At least one rent is required",
  },

  packageManagement: {
    id: "ID",
    note: "Note",
    createdAt: "Created At",
    updatedAt: "Updated At",
    status: "Status",
    shippingProvider: "Shipping Provider",
    packageId: "Package ID",
    suite: "Suite",
    user: "Tenant Name",
    company: "Company",
    statusOpen: "Open",
    statusClosed: "Closed",
    minimizedTitle: "Package #{{index}}",
    createdBy: "Created By",
    newTitle: "New Package",
    noNoteDefault: "No note",
    attachments: "Attachments",
    filterTitle: "Filter Packages",
  },

  accounting: {
    dashboardPage: "Vendors",

    payablesSection: "Payables",

    expensesPage: "Bills & Expenses",
    expensesPageIcon: "moneyBill",

    purchaseOrdersPage: "Purchase Orders",
    purchaseOrdersPageIcon: "moneyBill",

    receivingPage: "Receiving",
    receivingPageIcon: "truck",

    receivablesSection: "Receivables",

    invoicesPage: "Invoices",
    invoicesPageIcon: "newspaper",

    paymentsPage: "Payments",
    paymentsPageIcon: "card",

    tenantsPage: "Tenants",
    tenantsPageIcon: "multiplePersons",

    leasesPage: "Leases",
    leasesPageIcon: "certificate",

    settingsSection: "Settings",
    settingsPage: "Settings",
    settingsPageIcon: "cog",

    chargeCodesPage: "Charge Codes",
    chargeCodesPageIcon: "priceTag",

    glAccountsPage: "Chart of Accounts",
    glAccountsPageIcon: "accountBalance",

    markupsPage: "Markups",
    markupsPageIcon: "calculator",

    poApprovalsPage: "PO Approvals",
    poApprovalsPageIcon: "checkmark",
    postingSettingsPage: "Posting",
    postingSettingsPageIcon: "clock",

    bankingSection: "Banking",
    bankingPage: "Banking",
    bankingPageIcon: "bank",

    reportsSection: "Reports",
    reportsPage: "Reports",
    reportsPageIcon: "report",
    vendorReportsPage: "Vendor Reports",
    periodsPage: "Periods",
    periodsPageIcon: "calendar",

    trialBalancePage: "Trial Balance",
    trialBalancePageIcon: "book",
    profitAndLossPage: "Profit & Loss",
    profitAndLossPageIcon: "chart",

    budgetsPage: "Budgets",
    budgetsPageIcon: "calculator",
    apAgingPage: "AP Aging",
    apAgingPageIcon: "history",
    form1099Page: "1099",
    form1099PageIcon: "document",
    budgetVariancePage: "Budget vs Actual",

    generalLedgerPage: "General Ledger",

    journalPage: "Journal",
    journalPageIcon: "clipboardText",

    needsAttentionPage: "Needs Attention",
    needsAttentionPageIcon: "warningAmber",

    chartMappingPage: "Chart Mapping",
    chartMappingPageIcon: "sitemap",
  },

  accountingHub: {
    reportsTitle: "Reports",
    reportsDescription: "Financial statements, ledgers and period close.",
    settingsTitle: "Settings",
    settingsDescription:
      "Accounts, codes and approval rules used across accounting.",

    vendorReportsDescription:
      "Generate and download vendor detail and summary reports.",
    periodsDescription:
      "Review each month's vendor activity and close the books.",
    trialBalanceDescription:
      "Opening balance, debits, credits and closing balance for every account.",
    profitAndLossDescription:
      "Income and expenses for a period, compared with the one before.",
    budgetsDescription: "Plan yearly figures per account and track them.",
    apAgingDescription:
      "What is owed to each vendor, by how long it has been owed.",
    form1099Description:
      "Payments to 1099 vendors for the year, by box, with the summary PDF and CSV.",
    needsAttentionDescription:
      "Records that could not be posted to the journal.",

    chargeCodesDescription: "Codes used to bill tenants for charges.",
    glAccountsDescription: "The accounts every journal line posts to.",
    chartMappingDescription:
      "The account the journal uses for each control role.",
    markupsDescription: "Fixed or percentage markups added to billed charges.",
    poApprovalsDescription:
      "Who can approve purchase orders, and up to what amount.",
    postingSettingsDescription:
      "Which accounting period bills, invoices and payments post to.",
  },

  periods: {
    lastYearTitle: "Last 12 months",
    rangeTitle: "{{from}} – {{to}}",
    booksClosedThrough: "Books closed through {{date}}",
    booksNeverClosed: "Books have not been closed yet",
    olderAction: "Older",
    newerAction: "Newer",

    monthColumn: "Month",
    balanceForwardColumn: "Balance Forward",
    billsColumn: "Bills",
    paymentsColumn: "Payments",
    closingBalanceColumn: "Closing Balance",
    vendorsColumn: "Vendors",
    bankReviewColumn: "Bank Review",
    unmatchedBadge: "{{count}} to review",
    journalColumn: "Journal",
    notPostedBadge: "{{count}} not posted",
    closeBlockedTooltip_one:
      "{{count}} record in this month could not be posted to the journal. Fix it under Needs Attention before closing.",
    closeBlockedTooltip_other:
      "{{count}} records in this month could not be posted to the journal. Fix them under Needs Attention before closing.",

    closedByOn: "Closed by {{name}} on {{date}}",
    closedOn: "Closed on {{date}}",
    closedWithReason: "{{label}} · {{reason}}",

    closeAction: "Close",
    reopenAction: "Reopen",
    downloadPdfAction: "Download PDF",
    reportQueuedToast:
      "The report is being generated - you'll find it with your other reports once it's ready.",

    closeTitle: "Close {{month}}",
    closeDescription:
      "Nothing dated on or before {{date}} can be changed afterwards.",
    closeDescriptionPostMonth:
      "Nothing posted to {{month}} or earlier can be changed afterwards.",
    closeUnmatchedWarning_one:
      "{{count}} bank transaction in this month still needs review.",
    closeUnmatchedWarning_other:
      "{{count}} bank transactions in this month still need review.",
    closeConfirmAction: "Close Books",
    closeReasonLabel: "Note (optional)",
    closeReasonPlaceholder: "Anything worth knowing about this close",

    reopenTitle: "Reopen {{month}}",
    reopenDescription:
      "{{month}} can be edited again. The books stay closed through {{date}}.",
    reopenAllDescription:
      "The previous month is outside the loaded range, so this reopens every closed period - nothing will stay locked.",
    reopenConfirmAction: "Reopen Books",
    reopenReasonLabel: "Reason",
    reopenReasonPlaceholder: "Why does this month need to be reopened?",
    reopenReasonRequired: "Enter a reason for reopening.",

    overrideTitle: "Post to a Closed Period",
    overrideDescription:
      "You can post anyway. Your name, the period and the reason are logged.",
    overrideReasonLabel: "Reason",
    overrideReasonPlaceholder:
      "Why does this change belong in a closed period?",
    overrideReasonRequired: "Enter a reason to post to a closed period.",
    overrideConfirmAction: "Post Anyway",
    closedOverrideHint:
      "Books are closed through {{date}}. Saving into a closed month asks for a reason, which is logged.",
    closedOverrideBill:
      "Books are closed through {{date}} and this bill is in a closed month. Changes ask for a reason, which is logged.",

    vendorColumn: "Vendor",

    dateColumn: "Date",
    kindColumn: "Type",
    numberColumn: "Number",
    descriptionColumn: "Description",
    amountColumn: "Amount",
    balanceColumn: "Balance",
    kindBill: "Bill",
    kindExpense: "Expense",
    kindPayment: "Payment",

    lockedMinDateHint:
      "Books are closed through {{date}}, so earlier dates can't be selected.",
    lockedMinPostMonthHint:
      "Books are closed through {{date}}. Any date can be used, but the post month has to be an open month.",
    lockedBill:
      "Books are closed through {{date}}. This bill is dated inside closed books and can no longer be changed.",
    lockedInvoice:
      "Books are closed through {{date}}. This invoice is in a closed month, so its dates and charges can no longer be changed.",
    lockedBillPostMonth:
      "Books are closed through {{date}}. This bill is posted to a closed month and can no longer be changed.",
    lockedOpeningBalance:
      "Books are closed through {{date}}. This opening balance can no longer be changed.",
    lockedTransaction:
      "Books are closed through {{date}}. This transaction is dated inside closed books, so it can't be recorded as an expense or matched to bills.",

    postMonthLabel: "Post Month",
    postMonthColumn: "Post Month",
    postMonthTooltip:
      "The accounting period this record reports in. It can differ from its date.",
    postMonthFilterPlaceholder: "Post month",
    ledgerPostMonthNote:
      "Grouped by post month. Each row keeps its document's own date, so a late bill can show an earlier date.",
  },

  apAging: {
    title: "AP Aging",
    asOfDescription:
      "As of {{date}} · open bills by days past due. Each total is the buckets less unapplied credits and payments.",
    asOfLabel: "As of",
    asOfPlaceholder: "Today",
    vendorsFilter: "Vendors",
    vendorsFilterPlaceholder: "All vendors",
    withBillsLabel: "Show bills",
    vendorColumn: "Vendor",
    currentColumn: "Current",
    days1to30Column: "1-30",
    days31to60Column: "31-60",
    days61to90Column: "61-90",
    days90plusColumn: "90+",
    unappliedCreditsColumn: "Unapplied credits",
    unappliedPaymentsColumn: "Unapplied payments",
    totalColumn: "Total",
    totalRow: "Total",
    billDetail_one: "Due {{due}} · {{count}} day past due",
    billDetail_other: "Due {{due}} · {{count}} days past due",
  },

  form1099: {
    title: "1099 Report",
    description:
      "Money paid in the year to vendors marked 1099, split by box from the bills' accounts. Card payments are left out and refunds come off. Forms are due for vendors paid {{threshold}} or more.",
    yearLabel: "Year",
    vendorColumn: "Vendor",
    einColumn: "EIN",
    einMissing: "Missing",
    nec1Column: "NEC 1",
    misc1Column: "MISC 1",
    misc3Column: "MISC 3",
    misc6Column: "MISC 6",
    misc10Column: "MISC 10",
    totalColumn: "Total",
    totalRow: "Total",
    belowThresholdTitle: "Below the {{threshold}} threshold - no form",
    downloadPdfAction: "Download Summary PDF",
    downloadCsvAction: "Download CSV",
    exportReadyToast: "The 1099 report is ready - click to open it.",
    exportPendingToast:
      "The 1099 report is being generated. It will be in Reports when it's ready.",
    exportFailedToast: "The 1099 report could not be generated.",
  },

  postingSettings: {
    pageDescription:
      "How this property's bills, invoices and payments are assigned to accounting periods.",
    postMonthTitle: "Post Month",
    usePostMonthLabel: "Use post month",
    usePostMonthDescription:
      "Bills, invoices and payments get a Post Month field: the accounting period they report in, separate from their date. A late bill dated in a closed month can then be posted to the open month instead of being refused. Lists and searches still filter by date.",
    defaultPostMonth:
      "New entries default to {{month}}, the earliest open period.",
    changedOn: "Last changed {{date}}",
    changedOnBy: "Last changed {{date}} by {{name}}",
    adminOnly: "Only billing admins can change this setting.",
    enableConfirm:
      "Turn on post month? Bill, invoice and payment forms will show a Post Month field that defaults to the earliest open period.",
    disableConfirm:
      "Turn off post month? This is only possible while every record's post month still matches the month of its date.",
  },

  journal: {
    trialBalanceTitle: "Trial Balance",
    trialBalanceAsOfDescription:
      "As of {{date}} · opening balance, debits, credits and closing balance for every account. Click an account to open its ledger.",
    generalLedgerTitle: "General Ledger",
    generalLedgerDescription:
      "Every journal line that touched this account, with a running balance.",
    generalLedgerDescriptionWithType:
      "{{type}} · every journal line that touched this account, with a running balance",

    exportExcelAction: "Export to Excel",
    trialBalanceExportTitle: "Export Trial Balance",
    trialBalanceExportDescription: "Excel · {{from}} - {{to}} · accrual basis",
    trialBalanceExportCompanyLabel: "Vendor or Tenant (Optional)",
    trialBalanceExportCompanyPlaceholder: "Whole property",
    trialBalanceExportZeroValuesLabel: "Show accounts with no activity",
    trialBalanceExportReadyToast:
      "The trial balance Excel is ready. Click here to download it.",
    trialBalanceExportFailedToast:
      "The trial balance Excel could not be generated.",

    basisLabel: "Basis",
    basisAccrual: "Accrual",
    basisCash: "Cash",
    basisStatus: "{{basis}} basis",
    buildingLabel: "Building",

    journalTitle: "Journal",
    adjustmentBadge: "Adjustment",
    adjustmentTooltip:
      "Belongs to {{date}}, which was already closed, so it posted in the first open month instead.",
    journalRangeDescription:
      "{{range}} · every accounting line posted to the period, whatever produced it. Click a line to open its record.",
    periodLabel: "Period",
    lateItemsNote:
      "Grouped by post month. A late item keeps its own date, so it can show a date outside the period.",
    periodRangeLabel: "Period (from month – to month)",

    needsAttentionTitle: "Records Not Posted",
    needsAttentionDescription:
      "These records could not be posted to the journal. Fix the reason, then re-post them.",

    blockedEntriesAlert_one:
      "{{count}} record could not be posted to the journal, so these totals are incomplete.",
    blockedEntriesAlert_other:
      "{{count}} records could not be posted to the journal, so these totals are incomplete.",
    reviewAction: "Review",

    accountNumberColumn: "Number",
    accountColumn: "Account Name",
    parentAccountColumn: "Rolls Into",
    accountTypeColumn: "Type",
    openingColumn: "Opening",
    debitColumn: "Debit",
    creditColumn: "Credit",
    closingColumn: "Closing",
    balanceColumn: "Balance",
    dateColumn: "Date",
    sourceColumn: "Source",
    memoColumn: "Memo",
    descriptionColumn: "Description",
    reasonColumn: "Reason",
    companyColumn: "Vendor / Tenant",

    debitShort: "Dr",
    creditShort: "Cr",

    openingTotals: "Opening",
    periodTotals: "Period Activity",
    closingTotals: "Closing",
    balancedStatus: "Balanced",
    outOfBalanceStatus: "Out of balance",

    sourceExpense: "Bill",
    sourceInvoice: "Invoice",
    sourcePayment: "Payment",
    sourceRefund: "Refund",
    sourceVendorOpeningBalance: "Vendor Opening Balance",
    sourceTenantOpeningBalance: "Tenant Opening Balance",
    sourceTransfer: "Transfer",

    openLedgerAction: "Open Ledger",
    viewJournalEntriesAction: "View in Journal",
    openRecordAction: "Open Record",
    repostAction: "Re-post",
    repostAllAction: "Re-post All",
    repostSelectedAction: "Re-post Selected",
    repostSuccessToast_one: "{{count}} record posted to the journal.",
    repostSuccessToast_other: "{{count}} records posted to the journal.",
    repostPartialToast:
      "{{posted}} posted, {{blocked}} still could not be posted - check the reason.",

    filterTitle: "Filter Journal",
    companiesFilter: "Vendors & Tenants",
    companiesFilterPlaceholder: "All vendors and tenants",
    accountsFilter: "Accounts",
    accountsFilterPlaceholder: "All accounts",
    sourcesFilter: "Transaction Types",
    sourcesFilterPlaceholder: "All transaction types",
    newestFirst: "Newest first",
    oldestFirst: "Oldest first",

    downloadPdfAction: "Download PDF",
    downloadCsvAction: "Download CSV",
    downloadExcelAction: "Download Excel",
    pdfFormat: "PDF",
    csvFormat: "CSV",
    excelFormat: "Excel",
    exportFiltersNote:
      "Downloads cover the date range and the vendor / tenant filter - account and transaction type filters are not applied.",
    exportPendingToast:
      "Generating the journal {{format}} - it will be ready to download shortly.",
    exportReadyToast:
      "The journal {{format}} is ready. Click here to download it.",
    exportFailedToast: "The journal {{format}} could not be generated.",
    exportSlowToast:
      "The journal {{format}} is taking longer than usual. Please try again in a few minutes.",
  },

  profitAndLoss: {
    title: "Profit & Loss",

    accountColumn: "Account",

    sectionIncome: "Income",
    sectionCostOfGoodsSold: "Cost of Goods Sold",
    sectionExpenses: "Expenses",
    sectionOtherIncome: "Other Income",
    sectionOtherExpenses: "Other Expenses",
    sectionTotal: "Total {{section}}",

    grossProfit: "Gross Profit",
    netOperatingIncome: "Net Operating Income",
    netOtherIncome: "Net Other Income",
    netIncome: "Net Income",

    compareLabel: "Compare to",
    compareNone: "No comparison",
    comparePreviousPeriod: "Previous period",
    comparePreviousYear: "Previous year",

    unclassifiedTitle: "Accounts without a type",
    unclassifiedDescription:
      "These accounts had activity in the period but have no detail type, so they are left out of every total above. Give each one a detail type in the chart of accounts.",
    unclassifiedTotal: "Total",
    openChartOfAccountsAction: "Open Chart of Accounts",

    downloadPdfAction: "Download PDF",
    downloadCsvAction: "Download CSV",
    downloadExcelAction: "Download Excel",
    pdfFormat: "PDF",
    csvFormat: "CSV",
    excelFormat: "Excel",
    exportCompareNote:
      "Downloads cover the main date range only - the comparison column is not included.",
    exportPendingToast:
      "Generating the Profit & Loss {{format}} - it will be ready to download shortly.",
    exportReadyToast:
      "The Profit & Loss {{format}} is ready. Click here to download it.",
    exportFailedToast: "The Profit & Loss {{format}} could not be generated.",
    exportSlowToast:
      "The Profit & Loss {{format}} is taking longer than usual. Please try again in a few minutes.",

    viewLabel: "View",
    viewPeriod: "Period",
    viewT12: "By month (T12)",
    buildingLabel: "Building",
    allBuildings: "All buildings",
    t12EndMonthLabel: "Last month",
    t12EndMonthPlaceholder: "Last complete month",
    t12MonthsLabel: "Months",
    t12MonthsOption_one: "{{count}} month",
    t12MonthsOption_other: "{{count}} months",
    totalColumn: "Total",
    downloadT12Action: "Download T12",
    t12ExportReadyToast:
      "The T12 for {{from}} - {{to}} is ready. Click here to download it.",
    t12ExportFailedToast: "The T12 could not be generated.",

    filterTitle: "Filters",
    companyFilter: "Vendor or tenant",
    companyFilterPlaceholder: "All vendors and tenants",
  },
  chartMapping: {
    title: "Chart Mapping",
    description:
      "The account the journal uses for each control role. Auto means one was picked from your chart - choose an account to override it.",
    sourceSetting: "Chosen",
    sourceDefault: "Auto",
    sourceMissing: "Missing",
    unsavedStatus: "Unsaved",
    requiredStatus: "Required",
    requiredMissing:
      "Map {{accounts}} before saving - bills, payments and invoices can't post without them.",
    controlAccountsHelp:
      "Accounts Payable and Accounts Receivable are control accounts: only bills, payments, tenant charges and receipts post to them.",
    pickerPlaceholder: "Select an account",
    saveAction: "Save",
    discardAction: "Discard",
    savedToast:
      "Saved. Records that were waiting on these accounts have been re-posted.",
  },

  leases: {
    pageTitle: "Leases",

    numberColumn: "Lease #",
    tenantColumn: "Tenant",
    suiteColumn: "Suite",
    rsfColumn: "RSF",
    termColumn: "Lease Term",
    baseRentColumn: "Base Rent",
    escalationColumn: "Escalation",
    statusColumn: "Status",

    statusActive: "Active",
    statusExpiringSoon: "Expiring Soon",
    statusUpcoming: "Upcoming",
    statusExpired: "Expired",
    statusDraft: "Draft",

    suitesCount: "{{count}} suites",
    // eslint-disable-next-line no-template-curly-in-string -- literal "$" before the i18next {{value}} placeholder
    perSfYear: "${{value}}/SF/yr",
    mtm: "Month-to-Month",

    newLeaseAction: "Add Lease",

    totalLeasesCard: "Total Leases",
    activeCard: "Active",
    expiringSoonCard: "Expiring Soon",
    annualRentRollCard: "Annual Rent Roll",

    createTitle: "Add Lease",
    cancelAction: "Cancel",
    saveAndOpenAbstract: "Save & Open Abstract",
    createPartialWarning:
      "Lease created, but some details couldn't be saved. Open the lease to finish setup.",

    // Import lease from PDF
    importLeaseAction: "Import from PDF",
    importTitle: "Import lease from PDF",
    importSubmitAction: "Import",
    importUploadLabel: "Lease document (PDF)",
    importUploadHint: "Supported format: PDF · Max 32 MB",
    importNotifyNotice: "We'll notify you here when it's ready.",

    importStartedTitle: "Import started",
    importStartedDesc:
      "We're reading your PDF and extracting lease details. We'll notify you here when it's ready.",

    importParsedCompanyLabel: "Company found in document",
    importPickTenantLabel: "Tenant",
    importPickTenantPlaceholder: "Select tenant...",
    importPickTenantRequired: "Select a tenant to continue",

    importSuccessTitle: "Lease created from PDF",
    importSuccessDesc:
      "Your lease was created. Review any skipped fields below, then open it to finish setup.",
    importWarningsTitle: "Some fields were skipped ({{count}})",
    openLeaseAction: "Open lease",

    importErrorTooLarge: "That PDF is too large. The maximum size is 32 MB.",
    importErrorUnparseable:
      "We couldn't read this PDF. Try a different file or a clearer scan.",
    importErrorNotFound:
      "We couldn't find the referenced tenant or lease. It may have been removed.",

    // Fill existing lease from PDF
    fillFromPdfAction: "Fill from PDF",
    fillTooltip: "Fill lease fields from a PDF",
    fillTitle: "Fill lease from PDF",
    fillSubmitAction: "Fill lease",
    fillStrategyLabel: "If a field already has a value",
    fillStrategyFillEmpty: "Keep it — only fill empty fields",
    fillStrategyOverwrite: "Replace it with the value from the PDF",
    fillStrategyHint:
      "Repeatable sections (units, options, parking, storage, escalations) are only added when missing — existing ones are never changed or removed.",

    fillStartedTitle: "Fill started",
    fillStartedDesc:
      "We're reading your PDF and filling in the lease. We'll notify you here when it's ready.",

    // Resolve tenant for a pending lease import
    resolveCompanyTitle: "Pick a tenant for this import",
    resolveCompanyFailedTitle: "Import failed",
    resolveCompanyFailedDesc:
      "Something went wrong finishing this import. Try importing the PDF again.",
    resolveCompanySubmitAction: "Create lease",

    sectionBasics: "Lease Basics",
    sectionTerm: "Term",
    sectionRent: "Rent & Escalation",
    sectionExpenses: "Operating Expenses & Proration",
    sectionNotes: "Maintenance & Notes",

    companyLabel: "Company",
    companyPlaceholder: "Select company...",
    companyRequired: "Company is required",
    buildingLabel: "Building",
    buildingPlaceholder: "Select building...",
    buildingRequired: "Building is required",
    suiteLabel: "Suite / Unit",
    suitePlaceholder: "Select suite...",
    suiteRequired: "Suite is required",
    rsfLabel: "Rentable Square Feet",

    commencementLabel: "Commencement Date",
    commencementRequired: "Commencement date is required",
    expirationLabel: "Expiration Date",
    expirationRequired: "Expiration date is required",
    endOfYear1Label: "End of Lease Year 1",

    baseRentLabel: "Base Rent (Annual)",
    escalationTypeLabel: "Escalation Type",
    escalationTypePercent: "Percent per year",
    escalationTypeFixed: "Fixed amount per year",
    escalationValuePercentLabel: "Escalation Value (%)",
    escalationValueAmountLabel: "Escalation Value ($/yr)",

    expenseStructureLabel: "Expense Structure",
    expenseNnn: "Triple Net (NNN)",
    expenseFsg: "Full Service Gross",
    expenseModifiedGross: "Modified Gross",
    expenseBaseYearStop: "Base Year Stop",
    expenseIndustrialGross: "Industrial Gross",
    expenseAbsoluteNet: "Absolute Net",
    prorationMethodLabel: "Proration Method",
    proration365: "Annual amount ÷ 365 / 366",
    proration360: "Annual amount ÷ 360",
    prorationMonthly: "Monthly (annual ÷ 12)",

    maintenanceLabel: "Maintenance Responsibility & Clause Notes",
    maintenancePlaceholder:
      "e.g. §4.2(a) Landlord responsible for roof & structure; §4.2(b) Tenant responsible for in-suite HVAC...",

    createHintLabel: "Next:",
    createHint:
      "saving opens the full lease abstract — Overview, Premises & Units, Rent, Recoveries/CAM, Deposits & Options, and Documents. Phase-2 sections (Parking, Storage, CPI, Late Fees, % Rent) appear as stubs for now.",

    tabOverview: "Overview",
    tabPremises: "Premises",
    tabRent: "Rent",
    tabSchedule: "Schedule",
    tabRecoveries: "Recoveries",
    tabDepositsOptions: "Deposits & Options",
    tabDocuments: "Documents",
    tabComingSoon: "This section is coming soon.",

    leaseIdWord: "Lease ID",
    summaryTerm: "Term",
    summaryBaseRent: "Base Rent",
    summaryRsf: "RSF",
    summaryPerSf: "$/SF",
    perYearSuffix: "/yr",
    saving: "Saving…",
    allChangesSaved: "All changes saved",

    partiesTermSection: "Parties & Term",
    billingSection: "Billing Information",
    manageInCompanyProfile: "Manage in company profile",
    fromCompanyProfile: "From company profile",
    companyProfileHint:
      "Identity & billing below are pulled from the company profile",

    leaseStatusLabel: "Lease Status",
    activeCompany: "Active company",
    inactiveCompany: "Inactive company",
    legalNameLabel: "Legal Name (Lessee)",
    dbaNameLabel: "DBA Name",
    entityTypeLabel: "Tenant Entity Type",
    einLabel: "Federal ID # (EIN)",
    contactNameLabel: "Contact Name",
    contactPhoneLabel: "Contact Phone #",
    contactEmailLabel: "Contact E-mail",

    leaseIdLabel: "Lease ID",
    leaseIdHint: "Matches accounting software",
    amendmentLabel: "Amendment #",
    anchorTenantLabel: "Anchor Tenant?",
    commencementFieldLabel: "Lease Commencement",
    expirationFieldLabel: "Expiration Date",
    termMonthsLabel: "Term (Months)",
    executionDateLabel: "Execution Date",
    firstOccupiedLabel: "First Occupied",
    endOfYear1FieldLabel: "End of Lease Year 1",
    endOfYear1FieldHint:
      "Defaults to the commencement anniversary. Override only if lease year 1 ends on a different date — this shifts year escalations to fire here instead, and prorates the billing period this date falls inside.",
    negativeLeaseYearsStartLabel: "Negative Lease Years Start",
    negativeLeaseYearsStartHint:
      "Rent-free from this date until the commencement date — those months post at $0 in the schedule. Must be before commencement, and no more than 5 years earlier.",
    renewalStartLabel: "Renewal Start",
    renewalExpirationLabel: "Renewal Expiration",
    monthToMonthLabel: "Month-to-Month?",
    commentsLabel: "Comments",
    optionYes: "Yes",
    optionNo: "No",

    billingNameLabel: "Billing Name",
    attentionLabel: "Attention",
    billingAddressLabel: "Billing Address",
    cityLabel: "City",
    stateZipLabel: "State / Zip",
    billingEmailLabel: "E-mail",
    billingPhoneLabel: "Billing Phone #",

    premisesSection: "Premises",
    spaceTypeLabel: "Space Type",
    spaceTypeTenant: "Tenant",
    spaceTypeAmenity: "Amenity",
    spaceTypeMechanical: "Mechanical",
    countyLabel: "County",
    totalRentableSfLabel: "Total Rentable SF",
    totalRentableSfHint: "Rolled up from units",
    buildingProRataLabel: "Building Pro-Rata %",
    projectProRataLabel: "Project Pro-Rata %",
    marketRentLabel: "Market Rent ($/SF/yr)",

    unitsSection: "Units",
    addUnitAction: "Add unit",
    removeUnitAction: "Remove unit",
    unitsEmpty: "No units yet. Add one to define the premises.",
    unitBldgIdColumn: "Bldg ID",
    unitFloorColumn: "Floor",
    unitNumberColumn: "Unit #",
    unitRentableSfColumn: "Rentable SF",
    unitUsableSfColumn: "Usable SF",
    unitLoadColumn: "Load %",
    unitExpirationColumn: "Unit Expiration",

    parkingSection: "Parking",
    reservedStallsLabel: "Reserved Stalls",
    unreservedStallsLabel: "Un-reserved Stalls",
    freeStallsLabel: "# Free Stalls",
    paidStallsLabel: "# Paid Stalls",
    paidStallRateLabel: "Paid Stall Rate",
    mustStallsLabel: "# Must Stalls",
    mustStallRateLabel: "Must Stall Rate",

    storageSection: "Storage",
    storageRequiredLabel: "Required?",
    storageUnitLabel: "Storage Unit #",
    storageMonthlyRateLabel: "Monthly Rate",
    storageSquareFeetLabel: "Square Feet",
    storageIncludedLabel: "Included in Building SF?",
    storageRestrictionsLabel: "Restrictions",

    baseRentSection: "Base Rent & Escalation",
    baseRentAnnualLabel: "Base Rent (Annual)",
    monthlyLabel: "Monthly",
    monthlyHint: "Derived from annual ÷ 12",
    rateLabel: "Rate $/SF/yr",
    rateHint: "Derived from annual ÷ RSF",
    escalationTypeCpi: "CPI-indexed",
    escalationValueFieldLabel: "Escalation Value (%)",
    firstEscalationLabel: "First Escalation (after EOY1)",

    cpiSection: "CPI Adjustments",
    cpiNameLabel: "CPI Name & Base Index",
    cpiNamePlaceholder: "e.g. CPI-U US City Avg",
    baseYearRentLabel: "Base Year Monthly Rent",
    baseIndexMoYrLabel: "Base Index Mo/Yr",
    monthShort: "MM",
    yearShort: "YYYY",
    monthToApplyLabel: "Month to Apply",
    lifeAnnualCapLabel: "Life and/or Annual Cap",
    lifeCapShort: "Life %",
    annualCapShort: "Annual %",
    cpiCommentsLabel: "Comments / Calculation",

    lateFeesSection: "Late Fees",
    rentDueDayLabel: "Rent Due Day",
    gracePeriodLabel: "Grace Period",
    firstLateFeeMethodLabel: "1st Late Fee Method",
    firstLateFeeRateLabel: "1st Late Fee Rate %",
    flatRateFeeLabel: "Flat Rate Fee",
    includedInLateFeeLabel: "Included in Late Fee",
    secondLateFeeMethodLabel: "2nd Late Fee Method",
    maxFeeRateLabel: "Max Fee Rate %",
    additionalCommentsLabel: "Additional Comments",
    lateFeePercentMethod: "% Owed-Total",
    lateFeeFixedMethod: "Flat Rate",

    percentageRentSection: "Percentage Rent",
    percentRateLabel: "Percent Rate %",
    breakpointLabel: "Breakpoint",
    ceilingLabel: "Ceiling",
    naturalOrFixedLabel: "Natural or Fixed",
    effectiveDateLabel: "Effective Date",
    salesReportedLabel: "Sales Reported",
    percentageRentHint: "Percentage rent isn't tracked by the backend yet.",

    scheduleLyColumn: "LY",
    schedulePeriodColumn: "Period",
    scheduleMonthsColumn: "Months",
    scheduleMonthlyColumn: "Monthly Rent",
    scheduleAnnualColumn: "Annual Rent",
    schedulePerSfColumn: "$/SF",
    scheduleStepColumn: "Step",
    scheduleTotalLabel: "Total contracted base rent",
    scheduleEmpty: "No schedule available yet.",
    scheduleGroupByMonthly: "Monthly",
    scheduleGroupByYearly: "Yearly",
    scheduleOverrideIndicator: "This period has a manual override",
    scheduleProratedTag: "{{count}}d prorated",
    scheduleProratedIndicator:
      "This period is split by the end of Lease Year 1 boundary and billed for a partial number of days",
    scheduleRentFreeTag: "Rent free",
    scheduleRentFreePartialTag: "{{count}} mo rent-free",
    scheduleRentFreeIndicator:
      "This period falls within the lease's rent-free window and is billed at $0",
    scheduleBilledAnnotation: "billed {{amount}}",

    scheduleEditTitle: "Edit period rent",
    schedulePerSfUnavailableHint:
      "This period has no rentable SF on file, so $/SF can't be overridden.",
    scheduleModePinnedLabel: "One-off (pinned)",
    scheduleModePinnedHint:
      "Only this period changes. Future periods are unaffected.",
    scheduleModeRippleLabel: "New base going forward (ripple)",
    scheduleModeRippleHint:
      "This becomes the new base — future periods recalculate off it.",
    scheduleModeRippleUnavailableHint:
      "Not available for rent-free periods — use one-off instead.",
    scheduleYearlyLabel: "Apply to all 12 months of this lease year",
    scheduleYearlyHint:
      "Anchored to the lease year, independent of the Monthly/Yearly view above.",
    scheduleSaveAction: "Save override",

    camSection: "CAM / Expense Recapture",
    leaseTypeLabel: "Lease Type",
    recaptureTypeLabel: "Recapture Type",
    recaptureProRata: "Pro-Rata Share",
    recaptureBaseYearStop: "Base Year Stop",
    recaptureExpenseStop: "Expense Stop",
    recaptureFixed: "Fixed",
    recaptureNone: "None",
    proRataShareLabel: "Pro-Rata Share %",
    baseYearLabel: "Base Year",
    baseYearGrossUpLabel: "Base Year Gross-Up %",
    annualExpenseCapLabel: "Annual Expense Cap %",

    reconciliationSection: "Reconciliation & Audit",
    rightToAuditLabel: "Right to Audit?",
    reconciliationDueLabel: "Reconciliation Due",
    daysToAuditLabel: "# Days to Audit",
    protestDateLabel: "Protest Date",

    prorationSection: "Proration & Maintenance",
    prorationRuleHint: "Partial-month rule",
    maintenanceNotesLabel: "Maintenance Responsibility & Clause Notes",

    depositsSection: "Deposits · Letter of Credit · Guaranty",
    securityDepositLabel: "Security Deposit",
    prepaidRentLabel: "Prepaid Rent",
    letterOfCreditLabel: "Letter of Credit / Bond",
    guarantyLabel: "Guaranty?",
    guarantorNameLabel: "Guarantor Name",
    guarantyAmountLabel: "Guaranty Amount",

    optionsSection: "Options & Rights",
    addOptionAction: "Add option",
    removeOptionAction: "Remove option",
    optionsEmpty: "No options yet. Add one to track renewals, ROFO, etc.",
    optionColumn: "Option",
    optionInLeaseColumn: "In Lease?",
    optionNoticeColumn: "Notice Period",
    optionTicklerColumn: "Tickler Date",
    optionEncumberedColumn: "Encumbered Space",
    optionRenewal: "Renewal",
    optionExtension: "Extension",
    optionRofo: "Right of First Offer",
    optionRofr: "Right of First Refusal",
    optionExpansion: "Expansion",
    optionContraction: "Contraction",
    optionTermination: "Termination",
    optionPurchase: "Purchase",

    tiSection: "Tenant Improvements & Insurance",
    tiAllowanceLabel: "TI Allowance ($/SF)",
    tiCalcMethodLabel: "TI Calc Method",
    tiCalcPsf: "PSF",
    tiCalcLumpSum: "Lump Sum",
    tiCalcTurnkey: "Turnkey",
    tiCalcNone: "None",
    glInsuranceLabel: "GL Insurance (Aggregate)",

    commissionSection: "Commission",
    landlordBrokerLabel: "Landlord Broker",
    tenantBrokerLabel: "Tenant Broker",
    commissionBasisLabel: "Commission Based On",
    commissionTotalLeaseValue: "Total lease value",
    commissionNetRent: "Net rent",
    commissionGrossRent: "Gross rent",
    commissionPsf: "PSF",
    commissionFlat: "Flat",
    commissionRateLabel: "Rate (psf or %)",
    dueOnExecutionLabel: "Due on Execution %",
    dueOnCommencementLabel: "Due on Commencement %",
    commissionTotalLabel: "Total",
    paymentScheduleLabel: "Payment Schedule",

    documentsSection: "Lease Documents",
    addDocumentAction: "Add document",
    removeDocumentAction: "Remove document",
    documentsEmpty: "No documents yet. Add the executed lease and amendments.",
    documentColumn: "Document",
    documentDateColumn: "Date",
    documentDescriptionColumn: "Description",
  },

  banking: {
    allAccounts: "All Accounts",
    connected: "{{count}} connected",
    toReview: "to review",
    addAccount: "Add Account",
    connectBank: "Connect Bank",
    connectBankDescription: "Auto-sync via Plaid",
    addManualAccount: "Add Manual Account",
    addManualAccountDescription: "CSV import only",
    syncNow: "Sync Now",
    updatedAgo: "Updated {{time}}",
    transactionsToReview: "{{count}}",

    allTab: "All",
    needsReviewTab: "Needs Review",
    matchedTab: "Matched",
    expenseCreatedTab: "Expense Created",
    excludedTab: "Excluded",

    dateColumn: "Date",
    descriptionColumn: "Description",
    vendorColumn: "Vendor",
    accountColumn: "Account",
    sourceColumn: "Source",
    amountColumn: "Amount",
    statusColumn: "Status",
    actionsColumn: "Actions",
    fromToColumn: "From/To",
    attachmentsColumn: "Attachments",

    moreCounterparties: "+{{count}}",
    suggestedVendorTooltip:
      "Suggested vendor. Not assigned until the transaction is categorized or matched.",
    possiblePaymentChip_one: "Recorded payment",
    possiblePaymentChip_other: "{{count}} recorded payments",
    possiblePaymentsTitle: "Recorded payments found",
    possiblePaymentsHint:
      "Already recorded on a bill. Match it so the same payment isn't recorded twice.",
    possiblePaymentPaysBills: "Pays {{bills}}",
    possiblePaymentCheck: "Check / Ref #{{number}}",
    possiblePaymentMatchAction: "Match",
    attachmentsTitle: "Attachments",
    addAttachments: "Add attachments",
    attachmentsCount_one: "{{count}} attachment",
    attachmentsCount_other: "{{count}} attachments",

    bankFeedSource: "Bank Feed",
    csvImportSource: "CSV Import",
    needsReviewStatus: "Needs Review",
    matchedStatus: "Matched",
    excludedStatus: "Excluded",
    paidStatus: "Paid",

    categorizeAction: "Categorize",
    matchAction: "Match",
    transferAction: "Transfer",
    excludeAction: "Exclude",
    unexcludeAction: "Unexclude",
    unmatchAction: "Unmatch",

    undoAction: "Undo",
    undoMatchConfirmAction: "Undo",
    undoMatchTitle: "Undo This Match",
    undoMatchDescription:
      "The matched bill or expense goes back to unpaid, and this transaction returns to Needs Review so you can categorize or match it again.",
    undoTransferTitle: "Undo This Transfer",
    undoTransferDescription:
      "The link between the two transactions is removed. Both sides return to Needs Review, including the one on the other account.",
    undoBulkTitle: "Undo {{count}} Transactions",
    undoBulkDescription:
      "Each of the {{count}} transactions returns to Needs Review. Matched bills and expenses go back to unpaid, and transfers are unlinked on both accounts.",
    undoBillLabel: "Bill #{{number}} ({{amount}})",
    undoVoidBillsDescription:
      "This will also void the bills below, which were created from the bank transaction. A voided bill keeps its lines, attachments and history, but it can't be un-voided.",
    undoChangedBillsDescription:
      "These bills were also created from the bank transaction, but have been changed since. You can keep them or void them too. A voided bill can't be un-voided.",
    undoKeepBillsAction: "Undo, Keep Bills",
    undoVoidBillsAction: "Undo & Void Bills",
    undoPreviewFailed:
      "We couldn't check which bills this would void, so the action is paused. Close this and try again.",

    deleteTransactionTitle: "Delete Transaction",
    deleteTransactionDescription:
      "Are you sure you want to delete this bank transaction? This cannot be undone.",
    forceDeleteTransactionTitle: "Transaction Is In Use",
    forceDeleteTransactionDescription:
      "You can still delete it, but whatever is linked to it will be released first: a matched bill or expense goes back to unpaid, and a transfer is undone with its other side returned to Needs Review.",
    forceDeleteTransactionAction: "Delete Anyway",
    forceDeleteKeepBillsAction: "Delete, Keep Bills",
    forceDeleteVoidBillsAction: "Delete & Void Bills",
    deleteTransactionsDescription:
      "Are you sure you want to delete these {{count}} bank transactions? This cannot be undone.",
    deleteBlockedFallback:
      "Something is linked to one of these transactions, so nothing was deleted.",

    bulkCreateExpensesAction: "Create Expenses",
    bulkExcludeAction: "Exclude",
    bulkUnexcludeAction: "Unexclude",
    bulkUndoAction: "Undo",
    bulkDeleteAction: "Delete",
    bulkProcessing:
      "Processing {{count}} transactions. This can take a moment.",
    bulkLimitError: "Select at most {{limit}} transactions at a time.",
    bulkMixedDirectionTooltip:
      "Select only money out (expenses) or only money in (deposits) to categorize in bulk.",
    bulkDeletePlaidTooltip:
      "Bank feed transactions can't be deleted. Clear them from the selection to delete the rest.",

    bulkResultTitle: "Some Transactions Were Skipped",
    bulkResultSummary: "{{successCount}} updated, {{errorCount}} skipped.",
    bulkNotesTitle: "Done",
    bulkNotesSummary: "{{successCount}} updated. Please note:",

    bulkExpenseTitle: "Create Expenses",
    bulkDepositTitle: "Categorize Deposits",
    bulkDepositSummary:
      "Each of the {{count}} selected deposits, totalling {{total}}, will be booked in full to the account below.",
    bulkDepositDescriptionPlaceholder: "Leave empty to keep each bank memo",
    bulkCategorizeDepositsAction: "Categorize Deposits",
    bulkExpenseSummary:
      "One expense will be created for each of the {{count}} selected transactions, totalling {{total}}.",
    bulkExpenseDescriptionHint:
      "Each expense keeps the description of its own transaction.",
    bulkExpenseFilesHint:
      "To attach files, create the expense from a single transaction instead.",

    addManualAccountTitle: "Add Manual Account",
    manualAccountWarning:
      'Manual accounts require CSV import for transactions. For automatic bank feeds, use "Connect Bank" instead.',
    bankNameLabel: "Bank Name",
    bankNamePlaceholder: "e.g., Chase, Wells Fargo",
    accountNicknameLabel: "Account Name",
    accountNicknamePlaceholder: "e.g., Operating Account",
    accountNumberLabel: "Account Number (last 4)",
    accountNumberPlaceholder: "1234",
    routingNumberLabel: "Routing Number",
    routingNumberPlaceholder: "021000021",
    accountTypeLabel: "Account Type",
    glAccountLabel: "Account Name",
    defaultBuildingLabel: "Default Building (optional)",
    allBuildings: "All Buildings",
    accountTypeChecking: "Checking",
    accountTypeSavings: "Savings",
    accountTypeCreditCard: "Credit Card",
    accountNicknameRequired: "Account nickname is required",
    glAccountRequired: "Account is required",

    connectBankTitle: "Connect Your Bank",
    connectBankSubtitle: "Securely link your bank account via Plaid",
    searchBankPlaceholder: "Search for your bank...",

    activateAccountsTitle: "Activate Bank Accounts",
    activateAccountsDescription:
      "Select the accounts you want to track and assign an account to each.",
    activateAction: "Activate Accounts",

    importTitle: "Import Bank Transactions",
    selectBankAccount: "Select Bank Account",
    dropCsvHere: "Drop a CSV file here or click to browse",
    browseFiles: "Browse Files",

    reviewTitle: "Review Transaction",
    matchedTransactionTitle: "Matched Transaction",
    matchedBillsTitle: "Bills and expenses paid by this transaction",
    matchedInvoicesTitle: "Invoices paid by this transaction",
    matchedTransferDescription:
      "Recorded as a transfer between your accounts. Use Undo to unlink both sides.",
    matchedDepositDescription:
      "Categorized as a deposit to the chosen accounts. Use Undo to release it and categorize it again.",
    bankMemoLabel: "Bank Memo (read-only)",
    bankDateLabel: "Bank date",
    suggestedVendorLabel: "Suggested vendor",
    categorizeTab: "Categorize",
    categorizeTabDescription: "Record expense",
    categorizeTabDescriptionIncome: "Record income / deposit",
    categorizeTabDescriptionCharge: "Record charge",
    categorizeTabDescriptionCardCredit: "Record refund / credit",
    amountLabel: "Amount",
    cardChargeLabel: "Charge",
    cardCreditLabel: "Refund / credit",
    findMatchTab: "Find match",
    findMatchTabDescription: "Open bills",
    findMatchTabDescriptionIncome: "Open invoices",
    findMatchInlineLabel: "Find match",
    recordTransferTab: "Record as transfer",
    recordTransferTabDescription: "Between your accounts",
    receiveAction: "Receive",
    anotherAccount: "Another account",
    possibleMatchFoundLabel: "Possible match found.",
    possibleMatchFoundDetail: "Offsets transfer ({{amount}}) in {{source}}.",
    openInvoicesFor: "Open Invoices for {{vendor}}",
    invoicesFound: "{{count}} invoices found",
    noOpenInvoices: "No open invoices found.",
    exclude: "Exclude",
    excludeDescription: "Remove from queue",
    notBusinessTransaction: "Not a business transaction? Exclude",
    confirmMatch: "Confirm Match",
    recordExpenseAction: "Record Expense",
    recordDepositAction: "Record Deposit",
    recordCardCreditAction: "Record Refund / Credit",
    recordRefundAction: "Record Refund",
    depositAccountSection: "Deposit to account",
    depositAccountHint:
      "Income, an owner contribution (equity) or any other account. Split lines must add up to the full {{amount}}.",
    depositUnbalanced:
      "Allocations ({{total}}) must add up to the full deposit ({{amount}}).",
    recordTransferAction: "Record transfer",
    transferPostMonthKeep: "Same as the transfer",
    possibleTransferTitle: "Possible transfer",
    possibleTransferHint:
      "The opposite amount landed in another of this property's accounts. Record both rows as one transfer.",
    possibleTransferAmount: "Record a {{amount}} transfer",
    possibleTenantRefund: "Tenant refund",
    possibleVendorRefund: "Vendor refund",
    payeeLabel: "Vendor / Tenant",
    payeeRequired: "Vendor or tenant is required",
    tenantRefundHint:
      "Money back to a tenant is booked as a refund of their payments: it shows on their ledger and creates no bill.",
    refundPaymentsSection: "Refund of their payments",
    vendorRefundPaymentsSection: "Refund of payments to this vendor",
    refundPaymentsLoading: "Loading payments...",
    refundPaymentsEmpty: "No payments with money left to refund.",
    refundPaymentColumn: "Payment",
    refundDateColumn: "Date",
    refundPaidColumn: "Paid",
    refundLeftColumn: "Left to refund",
    refundAmountColumn: "Refund",
    refundMustAddUp: "Refunds of {{allocated}} must add up to {{total}}",
    refundOverRefundable: "Payment #{{number}} has {{amount}} left to refund",
    depositVendorLabel: "Vendor (optional)",
    depositVendorPlaceholder: "A refund from a vendor",
    depositModeAccount: "Book to an account",
    depositModeRefund: "Refund of a payment to them",
    matchedRefundsTitle: "Refunds",
    matchedRefundNumber: "Refund of payment #{{number}}",
    excludeTransaction: "Exclude Transaction",

    openBillsFor: "Open Bills for {{vendor}}",
    billsFound: "{{count}} bills found",
    noOpenBills: "No open bills found.",

    matchDatepickerPlaceholder: "Filter by invoice date",
    matchVendorPlaceholder: "Filter by vendor",
    matchLoading: "Loading...",
    noInvoicesMatchFilters: "No invoices match your filters.",
    noBillsMatchFilters: "No bills match your filters.",
    selectedInvoicesCount_one: "Selected: {{count}} invoice",
    selectedInvoicesCount_other: "Selected: {{count}} invoices",
    selectedBillsCount_one: "Selected: {{count}} bill",
    selectedBillsCount_other: "Selected: {{count}} bills",
    exactMatchLabel: "Exact match",
    matchAllocatedOf: "Allocated {{allocated}} of {{total}}",
    matchDifferenceLabel: "{{amount}} difference",
    matchOverLabel: "{{amount}} over",
    matchOfOpenBalance: "of {{amount}} open",
    matchOfTotal: "of {{amount}} total",
    matchExistingBills: "Bills already on this transaction",
    matchExistingInvoices: "Invoices already on this transaction",
    matchUseUndo: "To remove every match, use Undo on the transaction instead.",
    matchAmountRequired: "Enter an amount",
    matchAmountOverBalance: "Max {{amount}}",
    matchOverAllocated:
      "The amounts add up to {{amount}} more than the transaction. Lower them to continue.",
    matchDifferenceTitle: "Resolve the difference",
    matchDifferenceDescription:
      "The selected bills leave {{amount}} of this transaction unaccounted for. Post it to an account to confirm the match, or change the selection.",
    matchDifferenceDescriptionIncome:
      "The selected invoices leave {{amount}} of this deposit unaccounted for. Post it to an account, such as late fee income, to confirm the match, or change the selection.",
    matchDifferenceAccountLabel: "Account for the difference",
    matchDifferenceAccountPlaceholder: "Select account...",
    matchDifferenceDescriptionLabel: "Description (optional)",
    matchDifferenceAmountLabel: "Difference",
    matchUnfinishedDescription:
      "This match was started before matches had to balance, so part of the amount is still unaccounted for. Finish it below so the whole amount is accounted for, or use Undo.",
    allocatedLabel: "Allocated",
    dueDateLabel: "Due {{date}}",
    matchInvoiceNumber: "Inv #{{number}}",
    matchBillNumber: "Bill #{{number}}",
    matchBillDate: "Bill date {{date}}",
    matchInvoiceDate: "Invoice date {{date}}",
    matchOpenBalanceLabel: "Open balance",
    matchTotalLabel: "Total",
    matchShowLines: "Show lines",
    matchHideLines: "Hide lines",
    matchLinesLoading: "Loading lines...",
    matchLinesEmpty: "This bill has no lines.",
    matchSummaryTitle: "Review before confirming",
    matchSummaryApplying: "Applying {{amount}}",

    vendorLabel: "Vendor",
    vendorRequired: "Vendor is required",
    addVendorAction: "Add New Vendor",
    propertyBuildingLabel: "Property / Building",
    selectProperty: "Select property...",
    descriptionLabel: "Description",
    applyToBillLabel: "Apply to Bill (optional)",
    selectBill: "Select bill to apply payment...",
    applyToBillOption: "{{number}} · {{date}} · {{amount}} open",
    applyToBillNoVendor: "Choose a vendor to see their open bills",
    applyToBillEmpty: "No open bills for this vendor",
    applyToBillHint:
      "Picking a bill switches to Find match with it selected, so you can check its lines and confirm the match there.",
    glAccountSection: "Account",
    glAccountColumn: "ACCOUNT NAME",
    glAccountAmountColumn: "AMOUNT",
    lineDescriptionColumn: "DESCRIPTION",
    lineDescriptionPlaceholder: "Description",
    addLineAction: "+ Add Line",
    splitAction: "Split",
    removeSplitAction: "Remove Split",
    totalLabel: "Total:",
    balancedLabel: "Balanced",
    unbalancedLabel: "Off by {{amount}}",
    moneyOutDestination: "Money out — destination account",
    moneyInSource: "Money in — source account",
    transferSummaryOut: "{{amount}} out of {{source}} → into {{destination}}",
    transferSummaryIn: "{{amount}} into {{destination}} ← from {{source}}",
    selectAccount: "Select account...",

    excludeMessage:
      "This transaction will be removed from the review queue and won't affect your accounting records.",
    commonExclusions: "Common exclusions:",
    commonExclusionsDetail:
      "internal transfers, bank fees, duplicate entries, or non-business transactions.",

    expenseRecorded: "Expense recorded",
    removeAccount: "Remove Account",
    removeAccountConfirm: "Are you sure you want to remove this bank account?",
    editAccount: "Edit Account",
    editAccountTitle: "Edit Bank Account",
  },

  expenses: {
    pageTitle: "Bills & Expenses",
    allTab: "All",
    openTab: "Open",
    partlyPaidTab: "Partly Paid",
    paidTab: "Paid",
    overpaidTab: "Overpaid",
    voidTab: "Void",

    typeBill: "Bill",
    typeExpense: "Expense",
    typeCreditMemo: "Credit memo",
    sortLabel: "Sort",
    sortNewest: "Newest first",
    sortDueOldest: "Due date: oldest first",
    sortDueNewest: "Due date: newest first",
    typeExpenseHint:
      "Paid at once - no due date or terms. The payment is recorded from the account below.",
    typeCreditMemoHint:
      "A credit from the vendor. Apply it to their open bills once saved - it is never paid itself.",
    paidFromRequired: "Paid from is required for an expense",
    duplicateInvoiceNumberConfirm: "{{message}}\n\nSave it anyway?",
    billLockedHint:
      "Paid or credited - the invoice # and amounts can't change. Void the payment or unapply the credit first.",
    creditMemoLockedHint:
      "Applied to bills - the invoice # and amounts can't change. Unapply it first.",
    lineLockedHint:
      "The bill is paid or credited, so quantity and price stay as they are. The account and description can still change.",
    creditsTab: "Credits",
    creditAppliedToTab: "Applied to",
    creditsSection: "Credit Memos Applied",
    creditAppliedToSection: "Applied to Bills",
    creditsEmpty: "No credit memos applied to this bill.",
    creditAppliedToEmpty: "Not applied to any bill yet.",
    creditMemoColumn: "Credit memo",
    creditAppliedColumn: "Applied",
    creditUnappliedLabel: "Not Applied",
    applyCreditAction: "Apply to Bills",
    applyCreditTitle: "Apply Credit Memo",
    applyCreditDescription:
      "Spread the {{amount}} credit across this vendor's open bills. The list replaces what is applied today - set a bill to 0 to unapply it.",
    applyCreditColumn: "Apply",
    applyCreditTotalLabel: "Applied of {{amount}}",
    applyCreditOverTotal: "More than the {{amount}} credit is applied.",
    applyCreditSubmit: "Apply Credit",

    statusOpen: "Open",
    statusPartlyPaid: "Partly Paid",
    statusPaid: "Paid",
    statusOverpaid: "Overpaid",
    statusVoid: "Void",

    paymentTermDueOnReceipt: "Due on Receipt",
    paymentTermNet15: "Net 15",
    paymentTermNet30: "Net 30",
    paymentTermNet45: "Net 45",
    paymentTermNet60: "Net 60",

    numberColumn: "Number",
    invoiceNumberColumn: "Invoice #",
    controlNumberColumn: "Control #",
    filterTitle: "Filter Bills",
    vendorsFilter: "Vendors",
    vendorsFilterPlaceholder: "All vendors",
    glAccountsFilter: "GL accounts",
    glAccountsFilterPlaceholder: "All accounts",
    billDateFilter: "Invoice date",
    billDateFilterPlaceholder: "Any date",
    typeColumn: "Type",
    vendorColumn: "Vendor",
    dateColumn: "Bill Date",
    dueDateColumn: "Due Date",
    amountColumn: "Total",
    paidColumn: "Paid",
    balanceDueColumn: "Balance Due",
    statusColumn: "Status",
    actionsColumn: "Actions",

    newExpenseAction: "New",
    recordPaymentAction: "Record Payment",
    deleteExpensesAction: "Delete",
    deleteExpenseConfirm:
      "Are you sure you want to delete this expense? This cannot be undone.",
    addLineAction: "Add Line",
    saveAction: "Save",
    cancelAction: "Cancel",

    createTitle: "New Expense",
    editTitle: "Edit Expense",
    vendorLabel: "Vendor",
    vendorPlaceholder: "Select vendor...",
    vendorRequired: "Vendor is required",
    invoiceNumberLabel: "Vendor Invoice #",
    invoiceNumberPlaceholder: "Add vendor's invoice number...",
    invoiceNumberTooltip:
      "The invoice number printed on the vendor's paperwork. Separate from this bill's own number.",
    buildingLabel: "Building",
    buildingPlaceholder: "Select building...",
    typeLabel: "Type",
    dateLabel: "Bill Date",
    dateRequired: "Bill date is required",
    dueDateLabel: "Due Date",
    paymentTermLabel: "Terms",
    descriptionLabel: "Memo",
    descriptionPlaceholder: "Add memo...",
    bankMemoLabel: "Bank Memo",
    bankMemoTooltip:
      "The wording of the most recently matched bank transaction. It stays as the bank sent it, even if the memo is edited.",
    lineBankMemo: "Bank: {{memo}}",
    amountLabel: "Total",
    amountRequired: "Total is required",

    approvedByLabel: "Created By",
    balanceDueLabel: "Balance Due",
    totalLabel: "Total",

    lineItemsTab: "Line Items",
    paymentsTab: "Payments",
    attachments: "Attachments",

    lineItemsSection: "Line Items",
    lineItemsEmpty: 'No line items. Click "Add Line" to add one',
    accountColumn: "Account",
    accountPlaceholder: "Select account...",
    accountRequired: "Account is required",
    glDescriptionPlaceholder: "Description",
    glAmountPlaceholder: "0.00",

    addLineItemTitle: "Add Line Item",
    editLineItemTitle: "Edit Line Item",
    lineQtyLabel: "Qty",
    lineAmountLabel: "Unit Price",
    lineTotalLabel: "Total",
    fromPoBadge: "From PO",
    fromPoTooltip: "From PO line {{number}}",

    paymentsSection: "Payment History",
    paymentsEmpty: "No payments recorded yet",
    paymentNumberColumn: "Payment",
    paymentBankAccountColumn: "Bank Account",
    paymentClearedColumn: "Cleared",
    paymentOutstanding: "Outstanding",
    paymentVoided: "Voided",
    paymentShareOfTotal: "of {{total}} payment",
    voidPaymentAction: "Void payment",
    voidPaymentConfirm:
      "Void payment {{number}} for {{total}}? The whole payment is voided and every bill it paid is reopened for the amount it covered. This can't be undone.",
    voidSharedPaymentConfirm:
      "Void payment {{number}}? It paid {{total}} across several bills, and voiding it reopens every one of them - not just the {{share}} on this bill. This can't be undone.",
    balanceLabel: "Balance",

    recordPaymentTitle: "Record Payment",
    recordPaymentSubmit: "Record Payment",
    paymentAmountLabel: "Amount",
    paymentAmountRequired: "Amount is required",
    paymentAmountMin: "Amount must be greater than zero",
    paymentAmountMax: "Amount can't exceed the {{amount}} still outstanding",
    paymentDateLabel: "Payment Date",
    paymentBankAccountLabel: "Paid From",
    paymentBankAccountPlaceholder: "Select bank account",
    paymentBankAccountRequired: "Bank account is required",
    paymentNoteLabel: "Note",
    paymentNotePlaceholder: "Mailed Tuesday with the remittance slip",
    paymentNoteMax: "Note can't be longer than 1000 characters",
    checkNumberLabel: "Check / Ref #",
    checkNumberPlaceholder: "e.g. 1182 or ACH103026",
    checkNumberMax: "Check / Ref # can't be longer than 64 characters",
    duplicateCheckNumberConfirm: "{{message}}\n\nRecord it anyway?",
    providerCash: "Manual",
    providerBank: "Bank",
    providerStripe: "Card",

    payBillsAction: "Pay Bills",
    payBillsTitle: "Pay Bills",
    payBillsVendorTitle: "Pay Bills - {{vendor}}",
    payBillsDescription:
      "Record one payment - usually a check - across several of this vendor's open bills. Enter what it pays on each bill and leave the rest blank.",
    payBillsSection: "Open Bills",
    payBillsOpenCount_one: "{{count}} open bill",
    payBillsOpenCount_other: "{{count}} open bills",
    payBillsOpenBalanceColumn: "Open Balance",
    payBillsPaymentColumn: "Payment",
    payBillsLoading: "Loading open bills…",
    payBillsEmpty: "This vendor has no open bills",
    payBillsTotalLabel_one: "Payment total ({{count}} bill)",
    payBillsTotalLabel_other: "Payment total ({{count}} bills)",
    payBillsAmountNegative: "Amount can't be negative",
    payBillsLineRequired: "Enter an amount on at least one bill",

    attachmentsUploadAction: "Upload",
    attachmentRemoveConfirm: "Remove this attachment?",

    subtotal: "Subtotal",
    total: "Total",

    activityTitle: "Activity",
    commentPlaceholder: "Write a comment here…",

    expensesFound: "{{count}} found",
  },

  receiving: {
    pageTitle: "Receiving",
    singleTitle: "Item Receipt",
    pageNote:
      "Item receipts are created from a purchase order — open an issued PO and choose New Receiving.",
    searchPlaceholder: "Search by receipt #, PO # or vendor…",

    allTab: "All",
    postedTab: "Posted",
    voidedTab: "Voided",

    receiptColumn: "Receipt #",
    dateColumn: "Date",
    purchaseOrderColumn: "PO #",
    vendorColumn: "Vendor",
    propertyColumn: "Property",
    receivedByColumn: "Received By",
    linesColumn: "Lines",
    totalColumn: "Total",
    statusColumn: "Status",

    purchaseOrderLabel: "Purchase Order",
    propertyLabel: "Property",
    receivedByLabel: "Received By",
    vendorLabel: "Vendor",
    receiptDateLabel: "Receipt Date",
    packingSlipLabel: "Packing Slip",
    receiptTotalLabel: "Receipt Total",
    notesLabel: "Notes",

    receivedItemsTitle: "Received items",
    descriptionColumn: "Description",
    orderedColumn: "Ordered",
    receivedColumn: "Received",
    unitPriceColumn: "Unit Price",
    amountColumn: "Amount",
    glAccountColumn: "Account Name",
    totalLabel: "Total: {{amount}}",
    noLinesEmpty: "No lines on this receipt.",

    voidReceiptAction: "Void Receipt",
    voidReceiptTitle: "Void {{number}}",
    voidReceiptWarning:
      "Voiding returns {{count}} line(s) totalling {{amount}} to the open quantity on {{purchaseOrder}}. The receipt stays on record as voided.",
    voidReasonLabel: "Reason for voiding",
    voidReasonPlaceholder:
      "e.g., Received against the wrong PO line; goods returned to vendor as damaged.",

    convertToBillAction: "Convert to Bill",
    convertToBillTitle: "Convert {{number}} to a bill",
    convertToBillDescription:
      "A new bill will be created from this receipt and will appear on the Expenses page. Once billed, the receipt can no longer be voided.",
    billDateLabel: "Bill date",
    termsLabel: "Terms",
    dueDateLabel: "Due date",
    glAccountLabel: "Account name",
    billTotalLabel: "Bill total",
    multipleGLAccounts: "Multiple",
    createBillAction: "Create Bill",
  },

  poApprovals: {
    pageDescription:
      "Purchase orders route for approval based on their dollar amount. Set each approver's limit below — a PO can be approved by anyone whose limit covers the amount.",

    approvalRuleTitle: "Approval Rule",
    saveSettingsAction: "Save Settings",
    blockSelfApprovalLabel: "Creator can't approve their own PO",
    blockSelfApprovalDescription:
      "Separation of duties. When on, the person who created a purchase order is excluded from approving it, even if their limit would otherwise cover the amount.",

    routingTitle: "How POs Route ({{count}})",
    routingReadOnly: "Derived from approver limits — read only",
    routingNote:
      "The PO's creator is excluded from these approvers for their own purchase orders.",
    routingEmpty: "Add an approver to see how purchase orders will route.",
    poAmountColumn: "PO Amount",
    canBeApprovedByColumn: "Can Be Approved By",
    tierOver: "Over {{amount}}",
    tierAnyAmount: "Any amount",

    approversTitle: "Approvers ({{count}})",
    addApproverAction: "Add Approver",
    addApproverTitle: "Add Approver",
    editApproverTitle: "Edit Approver",

    nameColumn: "Name",
    roleColumn: "Role",
    approvalLimitColumn: "Approval Limit",

    userLabel: "User",
    userPlaceholder: "Search for a user",
    userImmutableTooltip:
      "An approver can't be moved to a different user. Delete this approver and add the other user instead.",
    roleLabel: "Role",
    approvalLimitLabel: "Approval Limit ($)",
    approvalLimitPlaceholder: "e.g. 10000",
    approvalLimitRequired: "Enter an approval limit, or tick Unlimited.",
    approvalLimitHint: "Highest PO total this person may approve.",
    unlimitedLabel: "Unlimited",

    rolePropertyManager: "Property Manager",
    roleRegionalManager: "Regional Manager",
    roleVpOperations: "VP Operations",
    roleCfo: "CFO",
    roleController: "Controller",
  },

  purchaseOrders: {
    pageTitle: "Purchase Orders",

    allTab: "All",
    draftTab: "Draft",
    pendingApprovalTab: "Pending Approval",
    activeTab: "Approved",
    closedTab: "Closed",
    cancelledTab: "Cancelled",

    statusDraft: "Draft",
    statusPendingApproval: "Pending Approval",
    statusActive: "Approved",
    statusClosed: "Closed",
    statusCancelled: "Cancelled",

    receivingStatusNotReceived: "Not Received",
    receivingStatusPartiallyReceived: "Partially Received",
    receivingStatusFullyReceived: "Fully Received",

    numberColumn: "PO #",
    vendorColumn: "Vendor",
    propertyColumn: "Property",
    dateColumn: "Order Date",
    requiredDateColumn: "Required By",
    amountColumn: "Total Amount",
    statusColumn: "Status",
    receivingStatusColumn: "Receiving",

    newPurchaseOrderAction: "New Purchase Order",
    deletePurchaseOrderAction: "Delete",
    deletePurchaseOrderConfirm:
      "Are you sure you want to delete this purchase order? This cannot be undone.",
    cancelAction: "Cancel",

    createTitle: "New Purchase Order",
    editTitle: "Edit Purchase Order",
    createComingSoon: "Purchase order form coming soon.",

    vendorLabel: "Vendor",
    vendorPlaceholder: "Select vendor...",
    vendorRequired: "Vendor is required",
    buildingLabel: "Property / Building",
    buildingPlaceholder: "Select property...",
    dateLabel: "PO Date",
    dateRequired: "PO date is required",
    requiredDateLabel: "Required By Date",
    paymentTermLabel: "Payment Terms",
    shipToAddressLabel: "Ship-To Address",
    shipToAddressPlaceholder: "Auto-filled from property",

    lineItemsLabel: "Line Items",
    lineDescriptionColumn: "Description",
    lineDescriptionPlaceholder: "Item description...",
    lineQtyColumn: "Qty",
    lineUnitPriceColumn: "Unit Price",
    lineAmountColumn: "Amount",
    lineGlAccountColumn: "Account Name",
    lineGlAccountPlaceholder: "Select account...",
    lineItemsTotal: "Total: {{amount}}",
    addLineItemAction: "Add Line Item",
    removeLineItemAction: "Remove line item",
    addLineItemToPoAction: "Add Line Item",
    editLineItemTitle: "Edit Line Item",
    removeLineItemConfirm:
      "Are you sure you want to remove this line item? This cannot be undone.",
    lineQuantityBelowReceived:
      "Quantity cannot be less than the received amount ({{received}})",

    notesLabel: "Notes",
    notesPlaceholder: "Additional notes or instructions...",

    attachmentsLabel: "Attachments",

    relatedWorkOrderLabel: "Related Work Order (optional)",
    relatedWorkOrderPlaceholder: "Enter work order ID...",
    relatedWorkOrderShortLabel: "Work Order",
    workOrderNone: "None",

    requestedByLabel: "Requested By",
    approvedByLabel: "Approved By",
    approvedByPending: "Pending",
    glAccountLabel: "Account Name",
    glAccountMultiple: "{{count}} accounts",
    totalAmountLabel: "Total Amount",
    remainingLabel: "Remaining",
    billedLabel: "Billed",
    budgetCategoryLabel: "Budget Category",
    tenantBillbackLabel: "Tenant Billback",
    shipToLabel: "Ship To",
    attachmentsCount: "{{count}} file(s)",

    lineItemsTab: "Line Items ({{count}})",
    receivingTab: "Receiving ({{count}})",
    invoicesTab: "Invoices / Bills",
    approvalsTab: "Approvals",
    attachments: "Attachments",

    lineRcvdColumn: "Rcvd",
    lineInvdColumn: "Inv'd",
    lineItemsEmpty: "No line items on this purchase order yet.",
    receivingEmpty: "No receiving activity yet.",
    invoicesEmpty: "No invoices linked yet.",
    approvalsEmpty: "No approvals recorded yet.",
    approvalsDraftEmpty: "No approval workflow (Draft).",

    approvalRequirementLabel: "Approval requirement:",
    approvalRequirementValue:
      "{{amount}} can be approved by any one of: {{approvers}}.",
    approvalRequirementNobody:
      "{{amount}} is above every approver's limit — no one can approve it as configured.",

    approvalDateColumn: "Date",
    approvalEventColumn: "Event",
    approvalUserColumn: "User",
    approvalAmountColumn: "Amount",
    approvalCommentColumn: "Comment",
    approvalPreviousAmount: "was {{amount}}",

    approvalEventRequested: "Requested",
    approvalEventApproved: "Approved",
    approvalEventRejected: "Rejected",
    approvalEventInvalidated: "Invalidated",

    approvePoTitle: "Approve Purchase Order",
    approvePoDescription:
      "This approves the purchase order at its current total. Add a comment if it would help whoever reads the approval history later.",
    approveCommentLabel: "Comment (optional)",
    rejectPoTitle: "Reject Purchase Order",
    rejectPoDescription:
      "This sends the purchase order back. A reason helps the requester understand what to change.",
    rejectReasonLabel: "Reason (optional)",
    approvalCommentPlaceholder: "Add a note…",

    toApproveTitle: "Awaiting my approval",
    toApproveAction: "Show POs awaiting my approval",
    allPurchaseOrdersAction: "Show all purchase orders",

    editAction: "Edit",
    submitForApprovalAction: "Submit for Approval",
    approveAction: "Approve",
    rejectAction: "Reject",
    cancelPoAction: "Cancel PO",
    generateDocumentAction: "Generate PDF",
    downloadDocumentAction: "Download PDF",
    documentLabel: "Vendor PDF",
    documentNotGenerated: "Not generated",
    documentPrintError:
      "The purchase order document couldn't be generated. Please try again.",
    documentReadyToast: "The purchase order PDF is ready — click to open it.",

    newReceivingAction: "New Receiving",
    createReceivingTitle: "New Receiving",
    editReceivingTitle: "Edit Receiving",
    receivedDateLabel: "Received Date",
    receivedDateRequired: "Received date is required",
    receivedByLabel: "Received By (optional)",
    receivedByPlaceholder: "Select user...",
    referenceNumberLabel: "Reference Number",
    referenceNumberPlaceholder: "Enter reference number...",
    receivingNotesLabel: "Notes",
    receivingNotesPlaceholder: "Additional notes...",
    receivingLineItemLabel: "Line Item",
    receivingLineItemPlaceholder: "Select line item...",
    receivingQuantityLabel: "Quantity Received",
    receivingRemainingHint: "Remaining: {{remaining}}",
    receivingQuantityExceedsRemaining:
      "Quantity exceeds remaining ({{remaining}})",
    addReceivingLineAction: "Add Line",
    removeReceivingLineAction: "Remove line",
    voidReceivingAction: "Void",
    voidReceivingConfirm: "Are you sure you want to void this receiving?",
    repostReceivingAction: "Repost",
    receivingDateColumn: "Date",
    receivingReferenceColumn: "Reference #",
    receivingReceivedByColumn: "Received By",
    receivingLinesColumn: "Lines",
    receivingRecordStatusColumn: "Status",
    receivingStatusPosted: "Posted",
    receivingStatusVoid: "Void",
  },

  budgets: {
    noAccess: "You don't have permission to view budgets.",

    allTab: "All",
    statusDraft: "Draft",
    statusSubmitted: "Submitted",
    statusApproved: "Approved",
    statusLocked: "Locked",

    allYearsOption: "All years",
    yearFilterLabel: "Year",

    titleColumn: "Name",
    yearColumn: "Year",
    statusColumn: "Status",
    updatedAtColumn: "Last Updated",
    accountColumn: "Account",
    totalColumn: "Total",
    summaryColumn: "Summary",

    newBudgetAction: "New Budget",
    createAction: "Create Budget",
    editAction: "Edit",
    importAction: "Import",
    downloadExcelAction: "Download Excel",
    downloadCsvAction: "Download CSV",
    varianceAction: "Budget vs Actual",
    cloneAction: "Copy",
    submitAction: "Submit",
    approveAction: "Approve",
    lockAction: "Lock",
    returnToDraftAction: "Return to Draft",
    deleteAction: "Delete",
    deleteConfirm:
      "Are you sure you want to delete this budget? This cannot be undone.",

    createTitle: "New Budget",
    editTitle: "Edit Budget",
    cloneTitle: "Copy Budget",
    copyTitleDefault: "{{title}} (copy)",

    titleLabel: "Name",
    titlePlaceholder: "e.g. 2027 Operating Budget",
    titleRequired: "Name is required",
    yearLabel: "Year",
    editYearNote:
      "Changing the year renames the period - every figure stays in its month.",

    seedModeLabel: "Starting point",
    seedModeBlank: "Blank",
    seedModeCopyBudget: "Copy of another budget",
    seedModePriorYearActuals: "Last year's actuals",
    seedGrowth: " · {{percent}}% growth",
    sourceBudgetLabel: "Budget to copy",
    sourceBudgetPlaceholder: "Select budget...",
    sourceBudgetRequired: "Pick the budget to copy",
    sourceBudgetOption: "{{title}} · {{year}} · {{status}}",
    growthPercentLabel: "Growth %",
    growthPercentPlaceholder: "e.g. 3",
    growthPercentInvalid: "Growth must be a percent between -100 and 1000.",

    previewTitle: "Preview",
    previewPickSource:
      "Pick a budget to copy to see the figures it would bring.",
    previewEmpty:
      "There are no figures to bring over - the budget will start empty.",
    previewCount_one:
      "{{count}} figure across {{accounts}} account(s) would be written.",
    previewCount_other:
      "{{count}} figures across {{accounts}} account(s) would be written.",

    createdLabel: "Created",
    submittedLabel: "Submitted",
    approvedLabel: "Approved",
    lockedLabel: "Locked",
    stepBy: "{{user}} · {{date}}",

    gridTitle: "Budget",
    showNonBudgetableLabel:
      "Show bank, receivable, payable and equity accounts",
    inactiveAccount: "Inactive",
    returnToDraftToEdit:
      "This budget is {{status}}. Return it to draft to edit its figures.",
    gridHint:
      "Works like a spreadsheet: type a figure or a formula (=1200*12, =Jan*1.03), paste from Excel, drag the fill handle or use Ctrl+D / Ctrl+R, and Ctrl+Z to undo.",
    entryInvalid:
      '"{{value}}" isn\'t a number or a formula this grid understands - the figure was not saved.',
    cellsSkipped_one:
      "{{count}} cell was left as it was - its value isn't a number or a formula.",
    cellsSkipped_other:
      "{{count}} cells were left as they were - their values aren't numbers or formulas.",
    headingNotEditable:
      "Headings don't take figures - enter them on the accounts under it.",
    subtotalNotEditable:
      "Subtotal lines add up the accounts under them - enter figures on those accounts.",
    inactiveNotEditable:
      "This account is switched off for the property. Its figures can be removed with Delete, but no new ones added.",
    cellSaveError: "The figure was not saved. Error: {{message}}",

    summaryRevenue: "Revenue",
    summaryOperatingExpenses: "Operating expenses",
    summaryNoi: "NOI",
    summaryBelowTheLine: "Below the line",
    summaryCapital: "Capital",
    summaryNet: "Net",

    importTitle: "Import Budget",
    importFileLabel: "Spreadsheet",
    importDescription:
      "Rows are matched to accounts by number, then by name - a file downloaded from this budget can be brought straight back. A blank month empties that cell.",
    importResult:
      "{{applied}} figure(s) taken, {{cleared}} emptied, {{skipped}} row(s) skipped.",
    importRowColumn: "Row",
    importAccountColumn: "Account",
    importReasonColumn: "Why it was skipped",
    importAnotherAction: "Import another file",
  },

  budgetVariance: {
    title: "{{title}} · {{year}}",
    monthLabel: "Month",
    showZeroValuesLabel: "Show zero values",
    openBudgetAction: "Open Budget",
    exportAction: "Export",

    accountColumn: "Account",
    budgetColumn: "{{block}} budget",
    actualColumn: "{{block}} actual",
    varianceColumn: "{{block}} variance",
    percentColumn: "Var %",
    annualBudgetColumn: "Annual budget",
    ytdBlock: "YTD",

    sectionRevenue: "Revenue",
    sectionOperatingExpenses: "Operating expenses",
    sectionBelowTheLine: "Below the line",
    sectionCapital: "Capital",
    sectionTotal: "Total {{section}}",

    exportTitle: "Export Budget vs Actual",
    exportDescription:
      "Covers {{month}} and the year to date, with the rows shown on screen.",
    csvFormat: "CSV",
    exportReadyToast:
      "The Budget vs Actual report is ready. Click here to download it.",
    exportFailedToast: "The Budget vs Actual report could not be generated.",
  },

  vendors: {
    allVendorsTitle: "All Vendors",
    singleTitle: "Vendor",
    minimizedTitle: "Vendor",
    name: "Name",
    company: "Company",
    phone: "Phone",
    email: "Email",
    address: "Address",
    status: "Status",
    balance: "Outstanding Balance",
    openBills: "Open Bills",
    dateCreated: "Date Created",
    ein: "EIN",
    is1099: "1099 Vendor",
    is1099Tooltip:
      "Payments to this vendor count toward the year's 1099 report.",
    paymentTerm: "Payment Terms",
    paymentTermPlaceholder: "No default",
    editEinTitle: "Edit EIN",
    einPlaceholder: "Enter EIN number",
    notes: "Notes",
    notesPlaceholder: "Enter notes",
    copyLinkAction: "Copy link",
    balanceReportAction: "Vendor Balance",
    balanceReportReadyToast:
      "The vendor balance report is ready. Click here to open it.",
    balanceReportPendingToast:
      "The vendor balance report is being generated - you'll find it under Reports shortly.",

    billingTitle: "Billing",
    openingBalance: "Opening Balance",
    openingBalanceDate: "Opening Balance As Of",
    asOfDate: "As of {{date}}",
    balanceForwardBreakdown:
      "As of {{date}} · {{bills}} billed, {{payments}} paid, {{refunds}} refunded since {{since}}",
    editOpeningBalanceTitle: "Opening Balance",
    openingBalanceAmount: "Amount",
    openingBalanceAmountPlaceholder: "0.00",
    openingBalanceAmountTooltip:
      "What was owed to this vendor before its BuildingHub history began. Use a negative amount if the vendor owes you.",
    openingBalanceAmountRequired: "Amount is required",
    openingBalanceAsOfDate: "As of Date",
    openingBalanceAsOfDateRequired: "As of date is required",
    openingBalanceDescription:
      "The as of date must be before the vendor's first bill or payment. Every balance and report for this vendor starts from this amount.",
    removeOpeningBalanceTitle: "Remove opening balance",
    removeOpeningBalanceDescription:
      "The opening balance of {{amount}} as of {{date}} will be removed. This vendor's balances and reports will be recalculated without it.",
    removeOpeningBalanceAction: "Remove",

    contactsTitle: "Contacts",
    contactsCount: "{{count}} contacts",
    addContactAction: "Add Contact",
    editContactAction: "Edit",
    removeContactAction: "Remove",
    setPrimaryAction: "Set Primary",
    primaryBadge: "Primary",
    contactName: "Name",
    contactNamePlaceholder: "Enter name",
    contactEmail: "Email",
    contactEmailPlaceholder: "Enter email",
    contactPhone: "Phone",
    contactPhonePlaceholder: "Enter phone",
    contactType: "Type",
    contactTypeBillingContact: "Billing Contact",
    contactTypeContact: "Contact",
    contactTypeOther: "Other",

    coiTitle: "COI",
    coiCount: "{{count}} certificates on file",
    insurer: "Insurer",
    policyNumber: "Policy #",
    coverageType: "Coverage Type",
    effective: "Effective",
    expiration: "Expiration",

    documentsTitle: "Documents",
    documentName: "Document Name",
    documentCategory: "Category",
    documentCategoryPlaceholder: "Select or create category",
    documentSize: "Size",
    documentUploadedBy: "Uploaded By",
    documentUploadDate: "Upload Date",
    noResultsFound: "No results found",
    uploadDocumentAction: "Upload Document",
    archiveAction: "Archive",
    unarchiveAction: "Unarchive",
    showArchivedAction: "Show Archived",
    hideArchivedAction: "Hide Archived",

    transactionsTitle: "Transactions",
    transactionId: "ID",
    transactionDate: "Date",
    transactionType: "Type",
    transactionDescription: "Description",
    transactionAmount: "Amount",
    transactionBalance: "Balance",
    transactionCreatedBy: "Created By",
    transactionTypeCreate: "Created",
    transactionTypeAdd: "Added",
    transactionTypeUpdate: "Updated",
    transactionTypeDelete: "Deleted",
    transactionTypeSucceeded: "Succeeded",
    transactionTypeCanceled: "Canceled",
    transactionTypeFailed: "Failed",

    billsTitle: "Bills",
    purchaseOrdersTitle: "Purchase Orders",
    paymentsTitle: "Payments",
    workOrdersTitle: "Work Orders",
  },

  adminSidebar: {
    dashboardPage: "Settings",

    activityLogPage: "Activity Log",
    activityLogPageIcon: "activity",

    // Global Settings
    globalSettingsSection: "Global Settings",

    notificationsPage: "Notifications",
    notificationsPageIcon: "email",

    menuManagerPage: "Menu Manager",
    menuManagerPageIcon: "menuSettings",

    reportingSettingsPage: "Reporting Settings",
    reportingSettingsPageIcon: "report",

    guestSettingsPage: "Guest Settings",
    guestSettingsPageIcon: "person",

    printersPage: "Printers",
    printersPageIcon: "printer",

    kioskPage: "Kiosk",
    kioskPageIcon: "phone",

    devicesPage: "Devices",
    devicesPageIcon: "devices",

    // Global Data

    globalDataSection: "Global Data",

    accountingPage: "Accounting",
    accountingPageIcon: "accountBalance",

    vendorDocumentCategoriesPage: "Vendor Document Categories",
    vendorDocumentCategoriesPageIcon: "folder",

    laborRatesPage: "Labor Rates",
    laborRatesPageIcon: "moneyBill",

    employeesPage: "Employees",
    employeesPageIcon: "multiplePersons",

    predefinedBroadcastsPage: "Predefined Broadcasts",
    predefinedBroadcastsPageIcon: "message",

    propertyAddonsPage: "Property Addons",
    propertyAddonsPageIcon: "puzzle",

    stripeSection: "Stripe",
    stripeSectionIcon: "card",

    stripeSettingsPage: "Settings",

    // Hidden

    buildingsPage: "Buildings",
    buildingsPageIcon: "building",

    // Hidden

    importPage: "Import",
    importPageIcon: "exchange",

    // -- Materials Section

    materialsSection: "Materials",
    materialsSectionIcon: "tools",

    materialsPage: "Materials",
    materialGroupsPage: "Material Groups",

    waiversPage: "Waivers",
    waiversPageIcon: "document",

    productsPage: "Products",
    productsPageIcon: "cart",

    couponsPage: "Coupons",
    couponsPageIcon: "dollar",

    leasingChargeTypesPage: "Leasing - Charge Types",
    leasingChargeTypesPageIcon: "clipboardText",

    shippingProvidersPage: "Shipping Providers",
    shippingProvidersPageIcon: "truck",

    customFieldsPage: "Custom Fields",
    customFieldsPageIcon: "database",

    // Amenities Section

    amenitySection: "Amenities",

    // -- MeetingHub Section

    meetingHubSection: "MeetingHub",
    meetingHubSectionIcon: "chair",

    meetingHubRoomsPage: "Rooms",
    meetingHubSettingsPage: "Settings",

    // Operations Section

    operationsSection: "Operations",

    // -- COI Section

    coiSection: "COI",
    coiSectionIcon: "wrench",

    coiEmailsPage: "Email to COI",

    // -- Work Orders Section

    workOrdersSection: "Work Orders",
    workOrdersSectionIcon: "wrench",

    workOrdersIssueTypesPage: "Issue Types",
    workOrdersIssueTypesGroupsPage: "Issue Types - Groups",
    workOrdersStatusesPage: "Statuses",
    workOrdersPrioritiesPage: "Priorities",
    workOrdersGuestFormsPage: "Guest Forms",
    workOrdersEmailPage: "Email to Work Order",

    // -- Incidents Section

    incidentsSection: "Incidents",
    incidentsSectionIcon: "warningAmber",

    incidentsIssueTypesPage: "Issue Types",
    incidentsIssueTypesGroupsPage: "Issue Types - Groups",
    incidentsStatusesPage: "Statuses",

    // -- Preventive Maintenance Section

    preventiveMaintenanceSection: "Preventive Maintenance",
    preventiveMaintenanceSectionIcon: "subTask",

    preventiveMaintenanceTaskTypesPage: "Task Types",
    preventiveMaintenanceCustomIdsPage: "Custom IDs",
    preventiveMaintenanceRoundCategoriesPage: "Round Categories",

    // Connected Apps Section

    connectedAppsSection: "Connected apps",

    integrationsPage: "Integrations",
    integrationsPageIcon: "integration",

    amanoSettingsPage: "Amano Settings",
    amanoSettingsPageIcon: "cog",

    geneaSettingsPage: "Genea Settings",
    geneaSettingsPageIcon: "cog",

    // Leasing Hub Section

    leasingHubSection: "Leasing hub",

    // -- Stacking Plan Section

    stackingPlanSection: "Stacking plan",
    stackingPlanSectionIcon: "stack",

    stackingPlanOverviewPage: "Overview",
    stackingPlanFloorsPage: "Floors",
    stackingPlanSuitesPage: "Suites",
  },
  adminMeetingHubSettings: {
    // Page subtitle
    subtitle: "Settings",

    // Fields
    defaultWorkOrderCategory: "Default Issue Type",
    defaultWorkOrderCategoryDescription:
      "Issue Type when generating Work Orders",
    defaultWorkOrderCategoryPlaceholder: "Select issue type",
    defaultWorkOrderCategoryError: "Issue type is required",

    generateWorkOrderOnReservationApprove: "Generate Work Order on Approval",
    generateWorkOrderOnReservationApproveDescription:
      "Automatically generate Work Order when reservation is approved",

    notifyUsers: "Notify Users for Pending Approval Reservations",
    notifyUsersDescription:
      "Which users will be notified when reservation requires approval (pending).",
    notifyUsersPlaceholder: "Select user(s)..",
  },
  adminMeetingHubRooms: {
    openingHours: "Opening hours",
    closingHours: "Closing hours",

    // Sizes
    largeSize: "Large",
    mediumSize: "Medium",
    smallSize: "Small",

    // Rates
    halfFullDayRate: "Period",
    hourlyRate: "Hourly",

    // Intervals
    halfHourInterval: "Per half hour",
    hourInterval: "Per hour",

    // Single Room Modal Steps
    detailsStep: "Details",
    pricingStep: "Pricing",
    termsStep: "Terms & Addons",

    // Single Room Modal Fields
    addRoomTitle: "Add Room",
    editRoomTitle: "Edit Room",
    title: "Name",
    titlePlaceholder: "Enter name",
    titleError: "Name is required",
    subtitle: "Subtitle",
    subtitlePlaceholder: "Enter subtitle",
    image: "Image",
    imageError: "Image is required",
    color: "Color",
    size: "Room Size",
    sizePlaceholder: "Select size",
    sizeError: "Room size is required",
    connectedRooms: "Connected Rooms",
    connectedRoomsPlaceholder: "Select rooms",
    glAccount: "Account Name",
    glAccountPlaceholder: "Select account",
    arCode: "AR Code",
    arCodePlaceholder: "Select AR code",
    chargeCode: "Charge Code",
    chargeCodePlaceholder: "Select charge code",
    requiresApproval: "Requires approval",
    timeFrom: "Available from",
    timeTo: "Available to",
    timeSelectPlaceholder: "Select time",
    timeError: "Please select valid time",
    advanceDays: "# of Days in advance that room can be booked",
    advanceDaysTooltip: "Leave empty if you do not want to set the limit",
    advanceDaysPlaceholder: "Enter amount of days",
    advanceDaysError: "Value must be greater than 0",

    amenities: "Room amenities",
    amenityName: "Amenity Name",
    amenityNamePlaceholder: "Amenity name",
    amenityNameError: "Please enter amenity name",
    amenityIcon: "Icon",
    amenityIconPlaceholder: "Select icon",
    amenityIconError: "Please select an icon",
    addAmenityAddonButton: "Add +",

    priceModel: "Pricing model",
    priceModelPlaceholder: "Select price model",
    priceModelError: "Please select pricing model",

    rate: "Rate",
    ratePlaceholder: "Enter rate",
    rateError: "Please enter rate",

    interval: "Interval",
    intervalPlaceholder: "Select interval",
    intervalError: "Please select interval",

    halfDayRate: "Half-Day Rate",
    halfDayRatePlaceholder: "Enter half-day rate",
    halfDayRateError: "Enter half-day rate",
    fullDayRate: "Full-Day Rate",
    fullDayRatePlaceholder: "Enter full-day rate",
    fullDayRateError: "Enter full-day rate",
    maxRate: "Max rate",
    maxRatePlaceholder: "Enter max rate",
    halfDayRateLimit: "Half-Day Rate Limit",
    halfDayRateLimitPlaceholder: "Select half-day rate limit",
    halfDayRateLimitError: "Please select half-day rate limit",

    minBookingTime: "Minimum Booking Time",
    minBookingTimePlaceholder: "Select minimum time",
    minBookingTimeError: "Please select minimum booking time",
    maxBookingTime: "Maximum Booking Time",
    maxBookingTimePlaceholder: "Unlimited",
    bufferTime: "Buffer Time",
    bufferTimePlaceholder: "No buffer time",

    cancellationPeriod: "Cancellation Period",
    cancellationPeriodPlaceholder: "No cancellation time",

    liabilityPolicy: "Liability Policy",
    liabilityPolicyPlaceholder: "Enter liability policy",
    cancellationPolicy: "Cancellation Policy",
    cancellationPolicyPlaceholder: "Enter cancellation policy",
    notes: "Notes",
    notesPlaceholder: "Enter notes",

    addonsLabel: "Addons",
    material: "Material",
    materialPlaceholder: "Select material",
    addonPricePerReservation: "Addon price per reservation",
  },
  meetingHub: {
    requiresApprovalWarning: "This room requires approval",
    detailsStep: "Details",
    additionalDetailsStep: "Additional Details",
    paymentStep: "Payment",
    reviewStep: "Review and Submit",
    room: "Room",
    date: "Date",
    time: "Time",
    from: "From",
    to: "to",
    timezone: "Timezone",
    requestedBy: "Requested By",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requester is required",
    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    addons: "Add-ons",
    detailsLabel: "Any other details for any other requests",
    detailsPlaceholder: "Add your message here..",
    attachments: "Attachments",
    paymentMethodDescription: "Which payment are you using?",
    paymentMethodCreditCard: "Credit Card",
    paymentMethodWorkOrder: "Bill through Work Order",
    paymentMethodWorkOrderMessage:
      "Your reservation will be processed through the Work Order.",
    stripeError:
      "Selected payment method is not available. Stripe is not integrated.",
    stripeServiceFee: "Service Fee",
    walletPermissionError:
      "Selected payment method is not available. You do not have permission to access wallet of selected requester.",
    creditCardPermissionError:
      "Selected payment method is not available. Selected requester does not have permission to use credit card payment.",
    offlineError:
      "Selected payment method is not available. Selected requester does not have permission to use offline payment.",
    card: "{{name}}'s Card",
    cardPlaceholder: "Select card",
    cardError: "Card is required",
    roomDetails: "Room Details",
    paymentByWorkOrder:
      "You have chosen billing by Work Order. Charges will be applied to Rent Statement. (Company Administrators ONLY)",
    paymentByCreditCard: "You have chosen billing by Credit Card.",
    paymentApproval:
      "Your credit card will be charged once booking has been approved.",
    termsConditions: "Terms & Conditions",
    termsAgree: "I agree to the Terms & Conditions",
    total: "Total",
    totalPrice: "Total price",
    liabilityPolicy: "Liability Policy",
    cancellationPolicy: "Cancellation Policy",
    notes: "Notes",
    termsError: "The terms and conditions must be accepted.",
    permissionsError:
      "We could not verify if requester has required permissions to use offline and credit card payments.",
    customFieldsError:
      "Some custom fields might not be available. Error: {{error}}",
    roomNotFoundError: "Sorry, we could not find the room",
    roomNotFoundDescriptionError: "Please try again later..",
    payment: "Payment",
    bookNowButton: "Book now",
    timeTakenError:
      "You selected the time that's already taken. Please change the time.",
    tooLateError: "You are too late. Time has already passed.",
    advanceDaysError: "Bookings can be made up to {{days}} days in advance.",
    minBookingError: "Minimum booking time is: {{minutes}} minutes.",
    maxBookingError: "Maximum booking time is: {{minutes}} minutes.",
    selectTimeError: "Please select a time",
    selectDateError: "Please select the date first",
    noRooms: "There are no rooms available at this moment.",
    dateRequiredError: "Date is required!",
    allSizesFilter: "All sizes",
  },
  meetingHubReservations: {
    id: "ID",
    tableDatepickerPlaceholder: "Filter by Reservation Date",
    activeStatus: "Active",
    canceledStatus: "Canceled",
    deniedStatus: "Denied",
    pendingStatus: "Pending",
    processingStatus: "Processing",
    requiresActionStatus: "Requires Action",
    requiresCaptureStatus: "Requires Capture",
    requiresConfirmationStatus: "Requires Confirmation",
    requiresPaymentMethodStatus: "Requires Payment Method",
    succeededStatus: "Succeeded",
    approveAction: "Approve",
    cancelAction: "Cancel",
    denyAction: "Deny",
    minimizedTitle: "Room Reservation - {{index}}",
    generateWorkOrderNowAction: "Generate Work Order (Now)",
    generateWorkOrderScheduleAction: "Generate Work Order (Schedule)",
    generateWorkOrderSuccess:
      "Success! Click to open new Work Order - {{index}}",
    syncWorkOrderAction: "Sync Work Order",
    unsyncWorkOrderAction: "Unsync Work Order - {{index}}",
    syncWorkOrderValueName: "Work Order Index",
    paymentType: "Payment type",
    paymentTypeWorkOrder: "Work Order",
    paymentStatus: "Payment status",
    paymentLink: "Payment link",
    paymentLinkText: "View payment",
    reservationDate: "Reservation Date",
    reservationTime: "Reservation Time",
    date: "Date",
    time: "Time",
    to: "to",
    total: "Total",
    company: "Company",
    status: "Status",
    room: "Room Name",
    timezone: "Property Timezone",
    chargesTitle: "Charges & Addons",
    requestedBy: "Requested By",
    requestedByEmail: "Requested by email",
    requestedByCompany: "Requested by company",
    createdAt: "Date Created",
    createdBy: "Created By",
    printDetails: "Details",
    details: "Details for any other requests",
    detailsPlaceholder: "Enter room reservation details",
    customFieldsTitle: "Additional Information",
    activityLogTitle: "Activity Log",
    attachments: "Attachments",
    imageAttachments: "Image Attachments",
    otherAttachments: "Other Attachments",
    noImages: "No images",
    noOtherAttachments: "No other attachments",
  },
  dashboard: {
    editMode: "Edit mode",
    restaurants: "Restaurants",
    hotels: "Hotels",
    transit: "Transit",
    atms: "ATMs",
    addWidgetTitle: "Add Widget",
    noWidgets: "No widgets available",
    noWidgetsDescription: "Currently there are no widgets available.",
    widgetTypeError: "Please select a widget type",
    widgetTypeDescription:
      "Click here to add {{title}} widget. This will show all records you have access to.",
    myWidgetTypeDescription:
      "Click here to add {{title}} widget. This widget will show all records that are assigned to you.",
    upcomingForMeWidgetTypeDescription:
      "Click here to add the {{title}} widget. This will show records that will be created for you in the next 7 days.",
    title: "Title",
    titleError: "Title is required",
    icon: "Icon",
    iconError: "Icon is required",
    color: "Color",
    colorError: "Color is required",
    myRecords: "My {{title}}",
    widgetLoadingTitle: "Widget is loading..",
  },
  news: {
    mainTitle: "News",

    description: "Post Description",
    descriptionPlaceholder: "Enter description",
    descriptionError: "Description is required",
    attachments: "Attachments",
    like: "Like",
    unlike: "Unlike",
    noLikes: "Nobody liked this post",
    addPostTitle: "Add new post",
    editPostTitle: "Edit post",
  },
  events: {
    mainTitle: "Events",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
    description: "Description",
    descriptionPlaceholder: "Enter description",
    descriptionError: "Description is required",
    location: "Location",
    locationPlaceholder: "Enter location",
    eventDate: "Start Date",
    eventDateError: "Event date is required",
    startTime: "Start Time",
    startTimeError: "Start time is required",
    eventLength: "Event Length",
    eventLengthPlaceholder: "Enter event length",
    eventLengthError: "Event length is required",
    eventLengthTooltip:
      "Event length is the duration of the event in minutes. It must be a multiple of 15.",
    timezone: "Timezone",
    timezonePlaceholder: "Select timezone",
    timezoneError: "Timezone is required",
    capacity: "Capacity",
    capacityPlaceholder: "Enter capacity",
    showCapacity: "Show capacity",
    repeat: "Is this event repeated?",
    enableRegistration: "Enable event registration",
    attachments: "Attachments",

    addEventTitle: "Add new event",
    editEventTitle: "Edit event",

    readyForReview: "Event is ready for review. Click to add review.",

    editThisEventButton: "Edit this event",
    editAllEventsButton: "Edit all events",
    editFutureEventsButton: "Edit this + future events",

    deleteThisEventButton: "Delete this event",
    deleteAllEventsButton: "Delete all events",
    deleteFutureEventsButton: "Delete this + future events",

    addRating: "Add rating",
    yourRating: "Your Rating",
    createdBy: "Created By",
    comment: "Comment: {{comment}}",
    clickToUpdate: "Click to update.",
    youCanRateTheEventAfterItStarts: "You can rate the event after it starts.",
    averageSatisfaction: "Average satisfaction: {{rating}}",
    capacityAvailable: "{{capacity}} available",
    readMore: "Read more",
    register: "Register",
    unregister: "Unregister",
    spotsLabel: "Spots",
    spot: "spot",
    spots: "spots",

    // Attendees
    sendMessageButton: "Send Message",
    attendeesUserTableHeader: "User",
    attendeesRegisteredOnTableHeader: "Registered On",
    attendeesRatingTableHeader: "Rating",
    attendeesCommentTableHeader: "Comment",

    // New Message
    newMessageTitle: "Add new message",
    sendViaLabel: "Send via",
    sendViaError:
      "Please select how you would like this message to be sent by! (*)",
    subject: "Subject",
    subjectPlaceholder: "Enter message subject",
    subjectError: "Subject is required",
    message: "Message",
    messagePlaceholder: "Enter message..",
    messageError: "Message is required",
    email: "Email",
    sms: "SMS",
    app: "App",
  },
  propertySelector: {
    title: "Select Property",
    description: "Select a property to log in to",
    noActiveRecords:
      "No active properties available. Please try again requesting new magic link..",
    recordsCount: "{{count}} properties available",
    backButton: "Back to login",
  },
  portfolioSelector: {
    title: "Welcome!",
    description: "Select a portfolio to log in to",
    noActiveRecords:
      "No active portfolios available. Please try again requesting new magic link..",
    recordsCount: "{{count}} portfolios available for {{email}}",
    notYou: "Not you?",
    backButton: "Use a different email",
  },
  equipment: {
    equipmentView: "Equipment",
    minimizedTitle: "Equipment - {{title}}",
    allEquipmentTableTitle: "All Equipment",
    newEquipmentButton: "New Equipment",
    newCategoryButton: "New Category",
    manageCategoriesButton: "Manage Categories",

    include: "Include",
    only: "Only",
    exclude: "Exclude",
    serviceHistory: "Service History",
    workOrders: "Work Orders",

    id: "ID",
    count: "Count",
    status: "Status",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
    category: "Category",
    categoryPlaceholder: "Select category",
    categoryError: "Category is required",
    building: "Building",
    buildingPlaceholder: "Select buildings",
    floor: "Floor",
    floorPlaceholder: "Select floor",
    suite: "Suite",
    suitePlaceholder: "Select suite",
    specificLocation: "Specific Location",
    specificLocationPlaceholder: "Enter location",
    assetTag: "Asset Tag",
    assetTagPlaceholder: "Enter asset tag",
    serialNumber: "Serial Number",
    serialNumberPlaceholder: "Enter serial number",
    manufacturer: "Manufacturer",
    manufacturerPlaceholder: "Enter manufacturer name",
    attachments: "Attachments",
    details: "Details",
    detailsPlaceholder: "Enter equipment details",
    noDetailsDefault: "No details",

    printQRCode: "Print QR Code",
    generateQRCode: "Generate QR Code (PDF)",

    filterTitle: "Filter Equipment",
    addEquipmentTitle: "Add Equipment",
    allCategoriesTableTitle: "All Equipment Categories",
    allCategoriesTableSubtitle: "All Categories",
    addCategoryTitle: "Add Category",
    editCategoryTitle: "Edit Category",

    nonBuildingRecordsLabel: "Records without default building",
    nonBuildingRecordsTooltip:
      "Not all equipment records have default building. You can choose to include, exclude or select only those records.",
  },
  procedures: {
    proceduresView: "Procedures",
    id: "ID",
    allProcedures: "All Procedures",
    procedures: "Procedures",
    singleProcedure: "Single Procedure",
    addProcedure: "Add Procedure",
    addSection: "Add Section",
    addStep: "Add Step",
    addStepCheckbox: "Checkbox",
    addStepInput: "Text Input",
    addStepNumberInput: "Number Input",
    addStepDropdown: "Dropdown",
    addStepPhoto: "Photo",
    stepHasCustomId: "Has custom ID?",
    stepHasCustomIdTooltip:
      "By adding custom ID, you can mark, specify or group step(s).",
    stepCustomIdPlaceholder: "Enter custom ID",
    stepHasInputUnit: "Has input unit?",
    stepHasInputUnitTooltip:
      "You can add unit to the input field. This unit will be displayed next to the input field.",
    stepInputUnitPlaceholder: "Enter unit",
    sections: "Sections",
    sectionTitlePlaceholder: "Enter section title",
    sectionTitleError: "Section title is required",
    noSectionsError: "There are no sections. Please add one.",

    stepTitlePlaceholder: "Enter step title (label)",
    stepDropdownPlaceholder: "Create dropdown items",
    stepDropdownNoOptionsMessage: "Start typing to create new item..",
    stepDropdownIsMulti: "Multi select?",
    stepDropdownIsMultiTooltip:
      "By default this step will allow selecting one option. If you want to enable multi select, please check this option.",
    stepTitleError: "Step title is required",
    stepDropdownItemsError: "Atleast one item is required",
    stepTitleAndDropdownError: "Step title and atleast one item is required",
    noStepsError: "There are no steps. Please add one.",

    schedulesCount: "Schedules Count",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    estimatedLabor: "Estimated Labor",
    estimatedLaborPlaceholder: "Enter estimated labor",
    estimatedLaborTooltip:
      "Estimated labor is the time it takes to complete the procedure in minutes.",

    createdBy: "Created By",
    dateCreated: "Date Created",
    status: "Status",

    notes: "Notes",
    notesPlaceholder: "Enter procedure notes",

    minSectionsError: "Atleast one section is required!",
    sectionDataStructure:
      "Every section must have title and atleast one step! Every step must have title!", // used as a tooltip or toaster for validation
  },
  schedules: {
    schedulesView: "Schedules",
    minimizedTitle: "Schedule - #{{index}}",
    newScheduleTitle: "New Schedule",
    addEquipmentTitle: "Add Equipment",
    addProcedureTitle: "Add Procedure",
    tableDatepickerPlaceholder: "Filter by Next Date",

    scheduleStep: "Schedule",
    detailsStep: "Details",
    equipmentStep: "Equipment",

    id: "ID",
    title: "Name",
    titlePlaceholder: "Enter name",
    titleError: "Name is required",
    status: "Status",
    notes: "Schedule Notes",
    notesPlaceholder: "Enter schedule notes",
    notesError: "Notes are required",
    noNotesDefault: "No schedule notes",
    attachments: "Attachments",

    date: "Date",
    startDate: "Start Date",
    nextDate: "Next Date",
    previousDate: "Previous Date",
    noPreviousDate: "No previous date",
    noNextDate: "No next date",
    dateCreated: "Date Created",
    interval: "Interval",
    timezone: "Timezone",
    timezonePlaceholder: "Select timezone",

    building: "Building",
    buildingPlaceholder: "Select buildings",
    buildingError: "Building is required",
    floor: "Floor",
    suite: "Suite",

    estimatedLabor: "Estimated Labor",
    dueAfter: "Due After",
    dueAfterDays: "Due After",
    dueAfterDaysPlaceholder: "Enter number of days",
    dueAfterDaysTooltip: "Number of days until task is due after generation",
    taskType: "Task Type",
    taskTypePlaceholder: "Select task types",
    assignedTo: "Assigned To",
    assignedToPlaceholder: "Select assignee",
    createSeparateTask: "Create separate task for each selected equipment?",

    procedure: "Procedure",
    procedurePlaceholder: "Select procedure",
    procedureError: "Procedure is required",
    procedures: "Procedures",
    proceduresPlaceholder: "Select procedures",
    proceduresError: "Atleast one procedure is required",

    equipment: "Equipment",
    equipmentPlaceholder: "Select equipment",
    equipmentError: "Equipment is required",
    equipmentCategoryFilterPlaceholder: "Select category",
    includeEquipment: "Include?",

    createdBy: "Created By",
    createdByEmail: "Created by email",

    skippedDates: "Skipped Dates",
    hasSkippedDates: "Yes",
    noSkippedDates: "None",

    imageAttachments: "Image Attachments",
    otherAttachments: "Other Attachments",
    noImages: "No images",
    noOtherAttachments: "No other attachments",
    activityLog: "Activity Log",
  },
  tasks: {
    filterBuildingPlaceholder: "All buildings",
    minimizedTitle: "Task #{{index}}",
    filterTitle: "Filter Tasks",
    relatedRecords: "Related Records",

    title: "Title",
    id: "ID",
    status: "Status",

    taskType: "Task Type",
    taskTypePlaceholder: "Select task types",
    category: "Category",

    building: "Building",
    floor: "Floor",
    suite: "Suite",

    startDate: "Start Date",
    dueDate: "Due Date",
    dueDatePlaceholder: "Select due date",
    dateCreated: "Date Created",
    createdAt: "Created At",
    completedAt: "Completed At",
    completedAtPlaceholder: "Select completed at",
    estimatedLabor: "Est. Labor",

    active: "Active",
    completed: "Completed",
    complete: "Complete",
    overdue: "Overdue",
    notCompleted: "Not Completed",
    completeTask: "Complete Task",
    uncompleteTask: "Uncomplete Task",

    createdBy: "Created By",
    createdByEmail: "Created by email",
    createdByPlaceholder: "Select created by",
    assignedTo: "Assigned To",
    assignedToEmail: "Assigned to email",
    assignedToPhone: "Assigned to phone",
    assignedToPlaceholder: "Select assignee",

    watchers: "Watchers",
    watchersPreview: "{{count}} users watching",

    proceduresTitle: "Procedures",
    addProcedureTitle: "Add Procedure",
    procedure: "Procedure",
    procedurePlaceholder: "Select procedure",
    procedureRequired: "Procedure is required",
    noProcedures: "This task does not have any procedure. Please add one.",
    procedureNotes: "Procedure Notes",
    noProcedureNotes: "This procedure does not have any notes",
    expandProcedure: "Expand procedure.",
    collapseProcedure: "Collapse procedure.",
    openProcedureInNewTab: "Open procedure in new tab.",
    removeProcedure: "Remove this procedure and its sections/steps.",

    addEquipmentTitle: "Add Equipment",
    equipment: "Equipment",
    equipmentPlaceholder: "Select equipment",
    equipmentRequired: "Equipment is required",
    equipmentsCount: "{{count}} equipment",

    noSections: "This procedure does not have any section",
    expandSection: "Expand this section.",
    collapseSection: "Collapse this section.",
    noSteps: "This section does not have any steps.",

    notes: "Notes",
    notesPlaceholder: "Enter notes",
    noNotes: "No notes",

    charges: "Charges",
    generateWorkOrder: "Generate Work Order",
    newWorkOrderTaskId: "Task ID: #{{index}}",
    newWorkOrderTaskName: "Task Name: {{title}}",
    newWorkOrderProcedureName: "Procedure Name: {{title}}",
    newWorkOrderSectionName: "Section Name: {{title}}",
    newWorkOrderStepName: "Step: {{title}}",
    scheduleName: "Schedule Name",
    customIdTooltip: "Custom ID",
    addCustomIdTooltip: "Click to add Custom ID",
    updateCustomIdTooltip: "Click to update Custom ID",

    // Custom ID Modal
    newCustomIdTitle: "Add Custom ID",
    editCustomIdTitle: "Edit Custom ID",
    customId: "Custom ID",
    customIdDescription:
      "Custom ID is a unique identifier for a step. By adding Custom ID, you can mark, specify or group step(s). If you want, you can completely remove Custom ID too.",
    customIdPlaceholder: "Enter custom ID",
    customIdError: "Custom ID is required",

    attachments: "Attachments",
    imageAttachments: "Image Attachments",
    otherAttachments: "Other Attachments",
    noImages: "No images",
    noOtherAttachments: "No other attachments",
    activityLog: "Activity Log",
  },
  reporting: {
    typeStep: "Type",
    filtersStep: "Filters",
    emailsStep: "Emails",

    pending: "Pending",
    ready: "Ready",
    error: "Error",

    createNewReportTitle: "Create new report",

    dateRange: "Date Range",
    visibility: "Visibility",
    status: "Status",
    dateCreated: "Date created",
    link: "Link",

    reportType: "Report type",
    reportTypePlaceholder: "Select report type",
    reportTypeError: "Report type is required",

    reportDate: "Report date",
    reportDateError: "Please select valid report date",

    endTime: "End Time",
    endTimeError: "Please select valid end time",

    reportCharge: "Charges",
    reportChargePlaceholder: "Select report charge",
    reportChargeError: "Report charge is required",

    filterByCompany: "Filter by company (Optional)",
    filterByCompanyPlaceholder: "Select companies",

    privateReport: "Private report",
    showZeroAmount: "Show zero amount Work Orders",
    sendReportInEmail: "Send report in email (Optional)",

    pmCompletionSummaryReportType: "PM Completion Summary Report",
    rentStatementBackupType: "Rent Statement Backup",
    workOrderSummaryByTenantType: "Work Order Summary by Tenant",
    workOrderJournalExtractType: "Work Order Journal Extract",
    workOrderJournalExtractGroupedType:
      "Single Line WO for Tenant Rent Statements",
    budgetVarianceType: "Budget vs Actual",
    toastFileNotReadyYetError: "There is no file yet. Please try again later..",

    allReportsTitle: "All Reports",
    reportChargesAll: "All",
    reportChargesBillable: "Billable",
    reportChargesNonBillable: "Non billable",
    private: "Private",
    public: "Public",
    generating: "Generating..",
  },
  reports: {
    vendorDetailType: "Vendor Transaction Detail",
    vendorSummaryType: "Vendor Balance Summary",

    numberColumn: "#",
    dateRangeColumn: "Date Range",
    statusColumn: "Status",
    createdAtColumn: "Created",
    downloadColumn: "Download",

    statusPending: "Pending",
    statusReady: "Ready",
    statusError: "Error",

    newReportAction: "New Report",
    downloadAction: "Download",
    deleteReportConfirm: "Are you sure you want to delete this report?",
    fileNotReadyError: "There is no file for this report yet.",

    createTitle: "New Vendor Report",
    typeLabel: "Report type",
    typePlaceholder: "Select report type",
    typeRequired: "Report type is required",
    formatLabel: "Format",
    pdfFormat: "PDF",
    excelFormat: "Excel",
    fromLabel: "From",
    fromRequired: "Start date is required",
    toLabel: "To",
    toRequired: "End date is required",
    vendorsLabel: "Vendors (Optional)",
    vendorsPlaceholder: "All vendors",
    includeInactiveLabel: "Include inactive vendors",
    emailsLabel: "Send report in email (Optional)",
    generateAction: "Generate",
    generating: "Generating..",
    reportReadyToast: "Report is ready!",
    generationFailedError: "Report generation failed. Please try again.",
  },
  repositoryFiles: {
    allFilesTitle: "All Files",
    addFolderTitle: "Add Folder",
    editFolderTitle: "Edit Folder",
    folderAddedToast: "Folder added!",
    folderUpdatedToast: "Folder updated!",
    name: "Name",
    namePlaceholder: "Enter name",
    nameError: "Name is required",

    isPrivate: "Is this folder private?",
    toastSelectFileError:
      "Please first select the file/folder you want to open.",
    toastOpenEditOnlyOneFileError:
      "It is possible to open/edit only one file/folder at once.",
    toastFileHelperError:
      "Sorry, we could not open this file. Please try again..",
    toastSelectFolderError: "Please first select the folder you want to edit.",
    toastEditOnlyFoldersError: "It is possible to edit folders only.",
    toastSelectFileDownloadError:
      "Please first select the file you want to download.",
  },
  resetPassword: {
    header: "Change your password",
    description: "Create new password below",
    confirmPassword: "Confirm Password",
    rememberedPasswordButton: "Remembered your password?",
    signInButton: "Sign in!",
    newPassword: "New Password",
    newPasswordPlaceholder: "Enter new password",
    newPasswordError: "Password is required - should be 6 chars minimum.",
    passwordConfirmation: "Repeat password",
    passwordConfirmationPlaceholder: "Type the new password again",
    passwordConfirmationError: "Passwords must match",
    passwordSetSuccessToast: "Success! Your new password is all set!",
  },
  rounds: {
    filterCategoryPlaceholder: "All categories",
    newTitle: "Add New Round",
    filterTitle: "Filter Rounds",
    minimizedTitle: "Round - {{index}}",

    id: "ID",

    building: "Building",
    buildingError: "Building is required",

    title: "Title",
    titleError: "Title is required",

    createdBy: "Created By",
    createdAt: "Date Created",
    assignedTo: "Assigned To",

    category: "Category",
    categoryPlaceholder: "Select category",

    notes: "Notes",
    noNotes: "No notes provided",

    status: "Status",
    activeStatus: "Active",
    pausedStatus: "Paused",
    deletedStatus: "Deleted",

    attachments: "Attachments",
    attachmentValueText: "link",

    activityLogTitle: "Activity Log",
    stepsTitle: "Steps",
    noStepsTitle: "No steps",
    noStepsDescription: "This round does not have any steps.",
    stepValueRequired: "Step value is required",

    newStepTitle: "Add New Step",
    completeStepTitle: "Complete Step",

    stepVoidStatus: "Void",
    stepCompletedStatus: "Completed",
    stepDueStatus: "Due",
    stepOverdueStatus: "Overdue",

    stepValue: "Value",

    stepType: "Type",
    stepTypeError: "Type is required",

    stepTypeInput: "Input",
    stepTypeCheckbox: "Checkbox",
    stepTypeDropdown: "Dropdown",

    stepUnit: "Unit",
    stepUnitError: "Unit is required",

    stepUnitNone: "None",
    stepUnitHours: "Hours",
    stepUnitMinutes: "Minutes",
    stepUnitGallons: "Gallons",
    stepUnitCubicFeet: "Cubic Feet",

    generateWorkOrder: "Generate Work Order",
    viewHistory: "View History",
    deleteStep: "Delete Step",
    completeAction: "Complete",

    stepDropdownItemsLabel: "Dropdown Items",
    stepDropdownItemsPlaceholder: "Enter dropdown items",
    stepDropdownItemsError: "Dropdown items are required",
    stepDropdownStartTyping: "Start typing to add dropdown items",

    historyTitle: "History",

    historyCreatedAt: "Timestamp",
    historyCreatedBy: "User",

    onTimeStatus: "On Time",
    lateStatus: "Late",

    newWorkOrderRoundId: "Round ID: #{{index}}",
    newWorkOrderRoundName: "Round Name: {{title}}",
    newWorkOrderRoundStepName: "Step Name: {{title}}",

    deleteStepConfirmationTitle: "Delete Step",
    deleteStepConfirmationDescription:
      "Are you sure you want to delete this step?",
  },
  stackingPlan: {
    vacantTitle: "Vacant",
    allFloorsAndSuitesPlaceholder: "All floors and suites",
    leaseExpirationLabel: "Lease Expiration",
    company: "Company",
    leaseDate: "Lease Date",
    floorPlan: "Floor Plan",
    floorSize: "sq ft",
    suitePlan: "Suite Plan",
    suiteSize: "sq ft",

    toastDateRequired: "Date is required!",
    pleaseSelectBuildingTitle: "Please select the building first.",
    pleaseSelectBuildingDescription:
      "Please select the building first. You can find building selector in Topbar above.",
  },
  watchlist: {
    allTitle: "All Watchlist",
    newTitle: "Create a watchlist",
    minimizedTitle: "Watchlist - {{index}}",

    firstName: "First Name",
    firstNamePlaceholder: "Enter first name",
    firstNameError: "First name is required",

    lastName: "Last Name",
    lastNamePlaceholder: "Enter last name",
    lastNameError: "Last name is required",

    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",

    requestedBy: "Requested By",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requester is required",

    details: "Details",
    detailsPlaceholder: "Enter watchlist details",
    noDetailsDefault: "No details",

    dateCreated: "Date Created",

    image: "Image",
    attachments: "Attachments",

    id: "ID",
    user: "User",

    filterTitle: "Filter Watchlist",
  },
  widgets: {
    filterTitle: "Filters",
    adjustFilters: "Adjust filters",
    noDataTitle: "No data",
    noDataDescription: "Currently there is no data for selected filters.",
    date: "Date",
    selectDate: "Please select date..",
    total: "Total",
    rating: "Rating",

    building: "Building",
    buildingPlaceholder: "Select buildings",
    company: "Company",
    companyPlaceholder: "Select company",
    facility: "Facility",
    facilityPlaceholder: "Select facility",
    facilityRequired: "Facility is required. Please select one.",

    issueType: "Issue Type",
    issueTypePlaceholder: "Select issue type",

    pending: "Pending",
    compliant: "Compliant",
    expired: "Expired",
    notCompliant: "Not Compliant",

    coiExpirationTitle: "COI Expiration",
    occupancyTitle: "Occupancy",
    propertyPopulationTitle: "Property Population",
    taskOpenTitle: "Open Tasks",
    taskOverdueTitle: "Overdue Tasks",
    workOrdersIssueTypesTitle: "Work Orders By Issue Type",
    workOrdersStatusesTitle: "Work Orders By Status",
    workOrdersSatisfactionTitle: "Work Order Satisfaction",
    workOrdersReviewsTitle: "Work Orders Reviews",
    workOrdersRevenueTitle: "Work Orders Revenue",

    contractChartTitle: "Contract",
    transientChartTitle: "Transient",
    vacantChartTitle: "Vacant",

    labor: "Labor",
    material: "Material",
    markup: "Markup",
  },
  users: {
    activeStatus: "Active",
    deactivatedStatus: "Deactivated",
    deletedStatus: "Deleted",

    // Genea Cards

    expiredStatus: "Expired",
    pendingActivationStatus: "Pending Activation",
    pendingDownloadStatus: "Pending Download",
    indefinitelyLabel: "Indefinitely",
    neverLabel: "Never",
    keycardLabel: "Keycard (Physical)",
    mobileLabel: "Mobile",

    type: "Type",
    typePlaceholder: "Select type",
    typeError: "Type is required",

    cardNumber: "Card Number",
    cardNumberPlaceholder: "Enter card number",
    cardNumberError: "Card number is required",

    badgeType: "Badge Type",
    badgeTypePlaceholder: "Select badge type",
    badgeTypeError: "Badge type is required",

    activationTime: "Activation Time",
    deactivationTime: "Deactivation Time",
    activationTimeError: "Activation time is required",

    validityColumnHeader: "Validity",
    lastUsedColumnHeader: "Last Used",

    // Building and Company Access Options

    includeAccessOption: "Include",
    onlyAccessOption: "Only",
    excludeAccessOption: "Exclude",

    // User Related

    informationViewLabel: "Basic Information",
    securityViewLabel: "Security",
    geneaCardsViewLabel: "Genea Cards",
    walletViewLabel: "Wallet",
    preferencesViewLabel: "Preferences",
    notificationsViewLabel: "Notifications",
    activityViewLabel: "Activity",

    resendEmailAction: "Resend welcome email",

    addNewUserTitle: "Add new user",
    addNewGeneaCardTitle: "Add new Genea card",

    name: "Name",
    roleAssigned: "Role Assigned",

    status: "Status",
    statusPlaceholder: "Select status",
    statusError: "Status is required",

    printerLabel: "Printer",
    printerDescription: "Default printer",
    printerPlaceholder: "Select printer",
    printerTooltip:
      "If no printer is selected user will be able to use any printer manually, that is locally connected.",

    homepageLabel: "Homepage",
    homepagePlaceholder: "Select page",
    homepageDescription: "Default homepage",

    buildingsCount: "{{count}} buildings",
    buildings: "Buildings",
    buildingsDescription: "All buildings user has access to",
    buildingsPlaceholder: "Select buldings",
    buildingError: "Building is required",

    allBuildings: "All buildings",
    allBuildingsAccess: "Has access to all buildings inside property?",
    allBuildingsAccessDescription: "Access to all current and future buildings",

    primaryBuilding: "Primary Building",
    primaryBuildingPlaceholder: "Select primary building",
    primaryBuildingDescription: "Primary building of this user",

    email: "Email",
    emailPlaceholder: "Enter email",
    emailError: "Please enter valid email",

    firstName: "First Name",
    firstNamePlaceholder: "Enter first name",
    firstNameError: "First name is required",

    lastName: "Last Name",
    lastNamePlaceholder: "Enter last name",
    lastNameError: "Last name is required",

    smsColumnHeader: "SMS",
    appColumnHeader: "App",

    companies: "Companies",
    companiesDescription: "All companies user has access to",
    companiesPlaceholder: "Select companies",
    companiesError: "Company is required",

    allCompanies: "All companies",
    allCompaniesAccess: "Has access to all companies inside property?",
    allCompaniesAccessDescription: "Access to all current and future companies",

    primaryCompany: "Primary Company",
    primaryCompanyDescription: "Primary company of this user",
    primaryCompanyPlaceholder: "Select primary company",

    primarySuite: "Primary Suite",
    primarySuiteDescription: "Primary suite of this user",

    role: "Role",
    rolePlaceholder: "Select role",
    roleError: "Role is required",

    lastLoginLabel: "Last Login",
    lastLoginDescription: "Time when user last logged in",
    lastLoginNotRecorded: "Login activity not recorded",

    email2FaLabel: "2FA by Email",
    email2FaDescription: "Securely login by verifying email",

    phone2FaLabel: "2FA by Phone",
    phone2FaDescription: "Securely login by verifying phone",

    defaultPaymentMethodLabel: "Default Payment Method",
    defaultPaymentMethodDescription: "Default payment method for this user",

    cardsLabel: "Cards",
    cardsDescription: "All user cards",
    addNewCardButton: "Add new card",

    bankAccountsLabel: "Bank Accounts",
    bankAccountsDescription: "All user bank accounts",
    addNewBankAccountButton: "Add new bank account",

    title: "Title",
    titlePlaceholder: "Enter title (Engineer, Manager..)",
    titleDescription: "User's title",

    mobileNumber: "Mobile Number",
    mobileNumberPlaceholder: "No mobile number",
    phoneRemovalTitle: "Phone removal",
    phoneRemovalDescription:
      "This action will remove the phone and any other Phone 2FA associated with it? Are you sure you want to proceed?",

    userPhoto: "User photo",
    userPhotoPlaceholderDescription: "Supported formats:",

    // Filters

    filterTitle: "Filter Users",
    fullAccessCompanyUsers: "Full Company Access Users",
    fullAccessCompanyUsersTooltip:
      "You can choose to include, exclude or select only users that have full access to all companies.",
    fullAccessBuildingUsers: "Full Building Access Users",
    fullAccessBuildingUsersTooltip:
      "You can choose to include, exclude or select only users that have full access to all buildings.",

    reassignTitle: "Deactivate and reassign work",
    reassignDeleteTitle: "Delete and reassign work",
    reassignDescription:
      "Work order templates and PM schedules assigned to the selected user(s) will move to the person you choose below.",
    reassignFieldDescription:
      "Who takes over this user's work order templates and PM schedules",
    reassignEmptyDescription:
      "If you leave this empty, nothing is reassigned - their recurring work stays assigned to them and will keep generating new records.",
    reassignLabel: "Reassign work to",
    reassignPlaceholder:
      "Select who takes over their work orders and schedules - optional",
    reassignSelfError:
      "Choose someone who is not part of this change, otherwise the work stays orphaned.",
    reassignDeactivateAction: "Deactivate",
    reassignDeleteAction: "Delete",

    toastNoAvatarError: "This user does not have an avatar.",
    toastStatusBulkSelfError: "You can not change status for your own profile.",
    toastUserAlreadyExist:
      "User with entered email already exist in the property.",
    toastUserAlreadyExistDescription:
      "User ({{name}}) with requested email already exist in portfolio. You can choosee to add it in this property or go back and enter different email.",
    toastOwnProfileChange:
      "Success! We detected that you've changed your own profile (buildings, companies, roles). We will refresh the page so we keep the latest data.",
    toastHomepageChange:
      "Success! We detected that you've changed your homepage. We will refresh the page so we keep the latest data.",
    disabledOptionError:
      "You can not enable this option as it is disabled in your property.",
  },
  workOrders: {
    id: "ID",
    tableDatepickerPlaceholder: "Filter by Start Date",
    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required",
    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    floor: "Floor",
    floorPlaceholder: "Select floor",
    suite: "Suite",
    suitePlaceholder: "Select suite",
    status: "Status",
    statusPlaceholder: "Select status",
    priority: "Priority",
    priorityPlaceholder: "Select priority",
    details: "Details",
    detailsPlaceholder: "Enter incident details",
    noDetailsDefault: "No details",
    issueType: "Issue Type",
    issueTypePlaceholder: "Select issue type",
    issueTypeError: "Issue type is required",
    requestedBy: "Requested By",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requester is required",
    requestedByEmail: "Requested by email",
    requestedByCompany: "Requested by company",
    assignedTo: "Assigned To",
    assignedToPlaceholder: "Select assignee",
    createdBy: "Created By",
    createdByGuest: "Guest: {{name}}",
    dateCreated: "Date Created",
    startDate: "Start Date",
    dueDate: "Due Date",
    closedAt: "Closed At",
    closedAtPlaceholder: "Select closed at date..",
    rating: "Rating",
    noRatingComment: "No comment was given.",
    addRating: "Add rating",
    noRating: "No rating",
    attachments: "Attachments",
    imageAttachments: "Image Attachments",
    otherAttachments: "Other Attachments",
    noImages: "No images",
    noOtherAttachments: "No other attachments",
    filterTitle: "Filter Work Orders",
    filterBuildingPlaceholder: "All buildings",
    minimizedTitle: "Work Order - {{index}}",
    newTitle: "Create a work order",
    watchersButton: "Watchers",
    watchersPreview: "{{count}} users watching",
    customFieldsTitle: "Additional Information",
    activityLogTitle: "Activity Log",
    chargesTitle: "Charges",
    noPermissionsCharges:
      "You do not have required permissions to see charges.",
    equipmentTitle: "Equipment",
    acknowledgeButton: "Acknowledge",
    readyForReview: "Your work order is ready for review. Click to add review.",
    generatedByTemplateTooltip:
      "This Work Order has been generated by Work Order Template - #{{index}}. Click to view.",

    // New Equipment
    addEquipmentTitle: "Add Equipment",
    equipmentLabel: "Equipment",
    equipmentPlaceholder: "Select equipment",
    equipmentError: "Equipment is required",
  },
  workOrderTemplates: {
    id: "ID",
    tableDatepickerPlaceholder: "Filter by Next Date",
    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required",
    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",
    floor: "Floor",
    floorPlaceholder: "Select floor",
    suite: "Suite",
    suitePlaceholder: "Select suite",
    status: "Status",
    statusPlaceholder: "Select status",
    priority: "Priority",
    priorityPlaceholder: "Select priority",
    details: "Details",
    detailsPlaceholder: "Enter incident details",
    noDetailsDefault: "No details",
    issueType: "Issue Type",
    issueTypePlaceholder: "Select issue type",
    issueTypeError: "Issue type is required",
    requestedBy: "Requested By",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requester is required",
    requestedByEmail: "Requested by email",
    requestedByCompany: "Requested by company",
    assignedTo: "Assigned To",
    assignedToPlaceholder: "Select assignee",
    createdBy: "Created By",
    dateCreated: "Date Created",
    startDate: "Start Date",
    previousDate: "Previous Date",
    noPreviousDate: "No previous date",
    nextDate: "Next Date",
    noNextDate: "No next date",
    skippedDates: "Skipped Dates",
    hasSkippedDates: "Yes",
    noSkippedDates: "None",
    dueAfterDays: "Due After",
    dueAfterDaysTooltip:
      "Number of days until work order is due after generation",
    dueAfterDaysPlaceholder: "Enter number of days",
    interval: "Interval",

    attachments: "Attachments",
    filterTitle: "Filter Work Order Templates",
    filterBuildingPlaceholder: "All buildings",
    minimizedTitle: "Work Order Template - {{index}}",
    newTitle: "Create a work order template",
    watchersButton: "Watchers",
    watchersPreview: "{{count}} users watching",
    customFieldsTitle: "Additional Information",
    activityLogTitle: "Activity Log",
    chargesTitle: "Charges",
    noPermissionsCharges:
      "You do not have required permissions to see charges.",
    workOrdersTitle: "Related Records",
  },
  workOrderGuestForms: {
    subtitle: "Please fill in the form below",
    errorTitle: "An error occurred",
    notFoundTitle: "Sorry, we could not find the guest form",
    notFoundDescription: "Please try again later..",

    firstName: "First Name",
    firstNamePlaceholder: "Enter first name",
    firstNameError: "First name is required",

    lastName: "Last Name",
    lastNamePlaceholder: "Enter last name",
    lastNameError: "Last name is required",

    email: "Email",
    emailPlaceholder: "Enter email",
    emailError: "Please enter valid email",

    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required",

    issueType: "Issue Type",
    issueTypePlaceholder: "Select issue type",
    issueTypeError: "Issue type is required",

    status: "Status",
    statusPlaceholder: "Select status",
    statusError: "Status is required",

    priority: "Priority",
    priorityPlaceholder: "Select priority",
    priorityError: "Priority is required",

    assignee: "Assignee",
    assigneePlaceholder: "Select assignee",
    assigneeError: "Assignee is required",

    floor: "Floor",
    floorPlaceholder: "Select floor",
    floorError: "Floor is required",

    suite: "Suite",
    suitePlaceholder: "Select suite",
    suiteError: "Suite is required",

    details: "Details",
    detailsPlaceholder: "Enter work order details",

    attachments: "Attachments",
    attachmentsError: "Attachment is required",

    // Success messages
    successTitle: "Success!",
    successDescription: "You have successfully submitted work order.",

    // Sign in link
    signInText: "Have an account?",
    signInButton: "Sign in!",
  },
  adminARCodes: {
    arCodeTitle: "AR Code", // used on table header and view in topbar
    editArCodeTitle: "Edit AR Code",
    addArCodeTitle: "Add AR Code",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
  },
  adminChargeCodes: {
    chargeCodeTitle: "Charge Code", // used on table header and view in topbar
    editChargeCodeTitle: "Edit Charge Code",
    addChargeCodeTitle: "Add Charge Code",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
  },
  adminGLAccounts: {
    glTitle: "Account Name", // used on table header and view in topbar
    editGLAccountTitle: "Edit Account",
    addGLAccountTitle: "Add Account",
    title: "Account Name",
    titlePlaceholder: "Enter account name",
    titleError: "Account name is required",

    accountNumber: "Account #",
    accountNumberPlaceholder: "Enter account number",
    detailType: "Detail Type",
    detailTypePlaceholder: "Select detail type",
    detailTypeError: "Detail type is required",
    detailTypeHelp:
      "A sub-category of the account type. It sets the type, classification and normal balance shown below.",
    parentAccount: "Parent Account",
    parentAccountPlaceholder: "Select parent account",
    parentAccountHelp:
      "Rolls this account up under another one on reports. It doesn't change where amounts post.",
    parentAccountNumber: "Rolls Into",
    form1099Box: "1099 Box",
    form1099BoxPlaceholder: "NEC 1 (default)",
    form1099BoxHelp:
      "Payments to 1099 vendors coded to this account are reported in this box.",
    form1099BoxNec1: "NEC 1 - Nonemployee compensation",
    form1099BoxMisc1: "MISC 1 - Rents",
    form1099BoxMisc3: "MISC 3 - Other income",
    form1099BoxMisc6: "MISC 6 - Medical and health care",
    form1099BoxMisc10: "MISC 10 - Attorney",
    role: "Role",
    rolePlaceholder: "Select role",
    roleHelp:
      "Only regular accounts take postings. Heading and subtotal accounts organise the chart and can't be picked on bills, payments or entries.",
    accountType: "Type",
    classification: "Classification",
    normalBalance: "Normal Balance",
    derivedHint:
      "Type, classification and normal balance are set automatically from the detail type.",
    allTypesFilter: "All types",
    allClassificationsFilter: "All classifications",

    roleREG: "Regular",
    roleTOT: "Subtotal",
    roleTXT: "Heading",

    classificationASSET: "Asset",
    classificationLIABILITY: "Liability",
    classificationEQUITY: "Equity",
    classificationREVENUE: "Revenue",
    classificationEXPENSE: "Expense",

    normalBalanceDEBIT: "Debit",
    normalBalanceCREDIT: "Credit",

    typeBANK: "Bank",
    typeACCOUNTS_RECEIVABLE: "Accounts Receivable",
    typeOTHER_CURRENT_ASSET: "Other Current Asset",
    typeFIXED_ASSET: "Fixed Asset",
    typeOTHER_ASSET: "Other Asset",
    typeACCOUNTS_PAYABLE: "Accounts Payable",
    typeCREDIT_CARD: "Credit Card",
    typeOTHER_CURRENT_LIABILITY: "Other Current Liability",
    typeLONG_TERM_LIABILITY: "Long Term Liability",
    typeEQUITY: "Equity",
    typeINCOME: "Income",
    typeCOST_OF_GOODS_SOLD: "Cost of Goods Sold",
    typeEXPENSE: "Expense",
    typeOTHER_INCOME: "Other Income",
    typeOTHER_EXPENSE: "Other Expense",
  },
  adminMarkups: {
    markupTitle: "Markup", // used on table header and view in topbar
    editMarkupTitle: "Edit Markup",
    addMarkupTitle: "Add Markup",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
    description: "Markup Details",
    descriptionPlaceholder: "Enter details",
    type: "Type",
    typePlaceholder: "Select type",
    typeError: "Type is required",
    amount: "Amount",
    amountPlaceholder: "Enter amount",
    glAccount: "Account Name",
    glAccountPlaceholder: "Select account",
    arCode: "AR Code",
    arCodePlaceholder: "Select AR Code",
    chargeCode: "Charge Code",
    chargeCodePlaceholder: "Select Charge Code",
    normalType: "Normal ($)",
    percentType: "Percent (%)",
  },
  adminTaxes: {
    taxTitle: "Tax", // used on table header and view in topbar
    newTaxTitle: "Add new tax",
    editTaxTitle: "Edit tax",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
    amount: "Amount",
    amountPlaceholder: "Enter amount",
    glAccount: "Account Name",
    glAccountPlaceholder: "Select account",
    arCode: "AR Code",
    arCodePlaceholder: "Select AR Code",
    chargeCode: "Charge Code",
    chargeCodePlaceholder: "Select Charge Code",
    statusActive: "Active",
    statusInactive: "Inactive",
  },
  adminActivityLog: {
    featureTitle: "Title",
    tableTitle: "All activity Log",
    filterTitle: "Filter Activity Log",
    activities: "{{count}} activities",
    noActivityData: "No activity",
    comment: "Comment",
    user: "User",
    userPlaceholder: "Select user",
    type: "Type",
    activity: "Activity",
    author: "Author",
    feature: "Feature",
    date: "Date",

    coiFeatureTitle: "COI",
    companyFeatureTitle: "Company",
    dealFeatureTitle: "Deal",
    equipmentFeatureTitle: "Equipment",
    guestFeatureTitle: "Guest",
    incidentFeatureTitle: "Incident",
    leaseFeatureTitle: "Lease",
    meetingHubFeatureTitle: "Meeting Hub",
    procedureFeatureTitle: "Procedure",
    scheduleFeatureTitle: "Schedule",
    subscriptionFeatureTitle: "Subscription",
    suiteFeatureTitle: "Suite",
    taskFeatureTitle: "Task",
    vendorAccessFeatureTitle: "Vendor Access",
    watchlistFeatureTitle: "Watchlist",
    workOrderFeatureTitle: "Work Order",
    workOrderTemplateFeatureTitle: "Work Order Template",
  },
  adminAmanoSettings: {
    tableTitle: "Amano Settings",

    editAmanoSettingTitle: "Edit Amano Setting",
    addAmanoSettingTitle: "Add Amano Setting",

    id: "ID",

    product: "Product",
    productPlaceholder: "Select product",
    productError: "Product is required",

    facility: "Facility",
    facilityPlaceholder: "Select facility",
    facilityError: "Facility is required",

    accessGroups: "Access Groups",
    accessGroupsPlaceholder: "Select access group",
    accessGroupsError: "Access group is required",
  },
  adminBuildings: {
    editBuildingTitle: "Edit Building",
    addBuildingTitle: "Add Building",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",
  },
  adminPredefinedBroadcasts: {
    editTitle: "Edit Predefined Broadcast",
    addTitle: "Add Predefined Broadcast",
    subject: "Subject",
    subjectPlaceholder: "Enter subject",
    subjectError: "Subject is required",
    body: "Body",
    bodyTooltip: "Used for SMS or APP broadcasts",
    bodyPlaceholder: "Enter body",
    emailBody: "Email Body",
    emailBodyPlaceholder: "Enter email body",
    toastFieldsRequired: "Body or Email Body is required!",
  },
  adminPropertyAddons: {
    tableTitle: "Property Addons",
    editTitle: "Edit Property Addon",
    addTitle: "Add Property Addon",
    key: "Key",
    keyPlaceholder: "Enter key",
    keyError: "Key is required",
    title: "Title",
    titleError: "Title is required",
    icon: "Icon",
    iconError: "Icon is required",
    value: "Text",
    valueError: "Text is required",
    workingHours: "Working Hours",
    email: "Email",
    phone: "Phone",
    primaryContact: "Primary Contact",
    other: "Other",
  },
  adminCoupons: {
    tableTitle: "Coupons",

    editCouponTitle: "Edit Coupon",
    addCouponTitle: "Add Coupon",

    durationOnce: "Once",
    durationForever: "Forever",
    durationRepeating: "Repeating",

    discountTypeAmount: "Amount",
    discountTypePercent: "Percent",

    codeColumnHeader: "Code",
    createdAtColumnHeader: "Created At",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    duration: "Duration",
    durationPlaceholder: "Select duration",
    durationError: "Duration is required",

    durationInMonths: "Valid for how many months?",
    durationInMonthsPlaceholder: "Enter months",
    durationInMonthsError: "Months are required",

    discountType: "Discount Type",
    discountTypePlaceholder: "Select discount type",
    discountTypeError: "Discount type is required",

    discountAmount: "Discount Amount",
    discountAmountPlaceholder: "Enter discount amount",

    products: "Products",
    productsPlaceholder: "Select products this coupon applies to",
  },
  adminCustomFields: {
    workOrderView: "Work Orders",
    incidentView: "Incidents",
    meetingHubView: "Meeting Hub",
    coiView: "COI",

    inputOption: "Input",
    dropdownOption: "Dropdown",
    checkboxOption: "Checkbox",
    singleDatepickerOption: "Single Datepicker",
    rangeDatepickerOption: "Range Datepicker",

    textOption: "Text",
    colorOption: "Color",
    numberOption: "Number",
    emailOption: "Email",

    addNewCustomFieldTitle: "Add new custom field",
    customKeysValidationError:
      "Custom keys can only contain letters and numbers.",
    editCustomFieldTitle: "Edit custom field",
    noViewSelected: "Please select the view first.",
    noViewSelectedDescription: "You can find view selector in Topbar above.",

    deleteConfirmationTitle: "Delete Confirmation",
    continueAction: "Continue",
    deleteConfirmationDescription:
      "This action will also delete data tied to the selected {{count}} field(s) in existing records. Are you sure you want to continue?",

    activationConfirmationTitle: "Activation Confirmation",
    deactivationConfirmationTitle: "Deactivation Confirmation",

    activationConfirmationDescription:
      "Do you also want to reactivate data tied to the selected field in existing records?",
    deactivationConfirmationDescription:
      "Do you also want to deactivate (hide) data tied to the selected field in existing records?",

    orderTableHeader: "Order",

    key: "Key",
    keyPlaceholder: "Enter key",
    keyTooltip:
      "Value of this field will be used as variable in Email Templates. Changing this field will also require updating Email Template(s) which use this field currently. Field should not have any spaces or special characters. Valid examples: dueDate, expiresIn",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleTooltip: "Value of this field will be used as label/title for user.",
    titleError: "Title is required",
    unknownType: "Unknown Type - {{type}}",

    fieldType: "Field Type",
    fieldTypeTooltip:
      "Type is not editable. You can create new custom field, deactivate or delete this custom field if you wish to change the type.",
    fieldTypePlaceholder: "Select field type",
    fieldTypeError: "Field type is required",

    inputTypeLabel: "Input Type",
    inputTypePlaceholder: "Select input type",
    inputTypeTooltip:
      "Default is Text. You can choose other if you need to accept email or number.",
    inputTypeError: "Input type is required",

    dropdownItemsLabel: "Dropdown Items",
    dropdownItemsPlaceholder: "Create dropdown items",
    dropdownStartTyping: "Start typing to create new item..",
    dropdownItemsError: "Dropdown items are required",

    dropdownTypeLabel: "Dropdown Type",
    dropdownTypePlaceholder: "Select dropdown type",
    dropdownTypeTooltip:
      "Default is Text. If you choose Color type, please add items/options as hex. For example: #E7F5FF, #1864AB",

    dropdownMultiCheckbox: "Multi select?",
    dropdownMultiCheckboxTooltip:
      "By default this field will allow selecting one option. If you want to enable multi select, please check this option.",

    rangeDatepickerSeparateCheckbox: "Display two separate datepickers?",
    rangeDatepickerSeparateCheckboxTooltip:
      "By default this field will be displayed as one datepicker. If you check this option this field will be displayed as two separate datepickers (from/to).",

    requiredCheckbox: "Required on creation?",
    requiredCheckboxTooltip:
      "Is this custom field required to fill when creating new record?",

    addToExistingRecordsCheckbox: "Add to existing records?",
    addToExistingRecordsCheckboxTooltip:
      "By default, new field will be available when creating new records. If you want to add this field to existing records, please check this field.",

    toastToggleError: "Please first select record you want to toggle.",
  },
  adminDashboard: {
    pageTitle: "General Property Settings",
    toastDataChanged:
      "Success! We have detected that some of main data has been changed so page will now refresh to take the effect of the changes you made.",

    propertyNameLabel: "Property name",
    propertyNameDescription: "Actual name of the property",
    propertyNamePlaceholder: "Enter property name",
    propertyNameError: "Property name is required",

    filterAccessibleSuiteLabel: "Filter accessible suites",
    filterAccessibleSuiteDescription: "Only show suites the user has access to",

    timezoneLabel: "Timezone",
    timezoneDescription: "Timezone of the property",
    timezonePlaceholder: "Select timezone",
    timezoneError: "Timezone is required",

    primaryColorLabel: "Primary color",
    primaryColorDescription: "Primary color of the property",
    primaryColorPlaceholder: "Select primary color",

    propertyLogoLabel: "Property logo",
    propertyLogoDescription: "Add a logo image for your property",

    propertyBgImageLabel: "Background image",
    propertyBgImageDescription: "Background image used on the log in screen",

    faviconLabel: "Favicon",
    faviconDescription: "Icon displayed in the address bar of a browser",

    addressLabel: "Address",
    addressDescription: "Address of the property",
    addressPlaceholder: "Enter address",

    longitudeLabel: "Longitude",
    longitudeDescription: "Longitude of the property",
    longitudePlaceholder: "Enter longitude",

    latitudeLabel: "Latitude",
    latitudeDescription: "Latitude of the property",
    latitudePlaceholder: "Enter latitude",
  },
  adminDevices: {
    pageTitle: "Devices",

    editDeviceTitle: "Edit Device",
    addDeviceTitle: "Add Device",

    appVersionTableHeader: "App Version",
    osVersionTableHeader: "OS Version",
    isPairedTableHeader: "Paired?",
    isPrimaryTableHeader: "Primary?",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    printer: "Printer",
    printerPlaceholder: "Select printer",
    printerError: "Printer is required",

    deviceLogsPageTitle: "All Waivers",

    viewLogAction: "View Log",
    setAsPrimaryAction: "Set as Primary",
    pairDeviceAction: "Pair device",
    pairedButton: "Paired",

    deviceLogsMessageTableHeader: "Message",
    deviceLogsTypeTableHeader: "Type",
    deviceLogsLocationTableHeader: "Location",
  },
  adminEmployees: {
    editEmployeeTitle: "Edit Employee",
    addEmployeeTitle: "Add Employee",
    tableTitle: "All Employees",

    rateTableHeader: "Rate",
    laborRateTableHeader: "Labor Rate",
    glAccountTableHeader: "Account Name",
    arCodeTableHeader: "AR Code",
    chargeCodeTableHeader: "Charge Code",
    markupTableHeader: "Markup",
    taxTableHeader: "Tax",
    assignableTableHeader: "Assignable?",

    user: "User",
    userPlaceholder: "Select user",
    userError: "User is required",

    laborRate: "Labor Rate",
    laborRatePlaceholder: "Select labor rate",

    tax: "Tax",
    taxPlaceholder: "Select tax",
  },
  adminGeneaSettings: {
    geneaSettingsTitle: "Genea Settings",

    productTableHeader: "Property Product",

    accessGroupTableHeader: "Genea Access Group",
    accessGroupPlaceholder: "Select access group",

    productAccessGroupMappingTitle: "Genea Product/Access Group Mapping",
    productAccessGroupMappingTooltip:
      "Connect your Building Hub products with specific Genea Access Groups.",

    companyTableHeader: "Building Hub Company",
    tenantTableHeader: "Genea Company",

    tenantPlaceholder: "Select tenant",
    tenantMappingTitle: "Genea Tenant Mapping",
    tenantMappingTooltip:
      "In order for users to be created on Genea, a tenant in BuildingHub must be mapped to a tenant in Genea.",

    companyTypePlaceholder: "Filter company type",

    emailLabel: "Email",
    emailDescription: "Email of master visitor",
    emailPlaceholder: "Enter email",

    badgeTypeLabel: "Badge Type",
    badgeTypeDescription: "Badge type",
    badgeTypePlaceholder: "Select badge type",

    badgeTypeRequiredError: "Badge type is required",
  },
  adminStripeSettings: {
    title: "Stripe Settings",

    cardFee: "Card Fee",
    cardFeeDescription: "Card fee for the property",
    cardFeePlaceholder: "Enter fee",

    achFee: "ACH Fee",
    achFeeDescription: "ACH fee for the property",
    achFeePlaceholder: "Enter fee",

    achCap: "ACH Cap",
    achCapDescription: "ACH cap for the property",
    achCapPlaceholder: "Enter cap",
  },
  adminGuestSettings: {
    guestSettingsTitle: "Guest Settings",
    badgeFormatGuestID: "GuestID",
    badgeFormatJulianDate: "Julian Date",

    badgeLogoLabel: "Badge Logo",
    badgeLogoDescription: "Add a badge logo for your property",

    printQRCodeLabel: "Print guest QR code on badge",
    printQRCodeDescription:
      "When enabled, the guest QR code will print on the visitor badge.",

    printQRCodeCheckboxLabel: "Print guest QR code on badge",

    badgeFormatLabel: "Badge Format",
    badgeFormatDescription: "Choose badge format. Default is GuestID.",
    badgeFormatPlaceholder: "Select badge format",
  },
  adminIntegrations: {
    integrationTitle: "Integration",

    integrationStripe: "Stripe",
    integrationStripeDescription:
      "Easy to use online payment processing for your business",

    integrationGenea: "Genea",
    integrationGeneaDescription: "Manage security in the property",

    integrationIntercom: "Intercom",
    integrationIntercomDescription:
      "Build better customer relationships through conversational experiences",

    integrationAmano: "Amano",
    integrationAmanoDescription:
      "Enable parking integration for property users",

    integrationEmailSms: "Email/SMS",
    integrationMailgunDescription:
      "Send, receive, and track emails using Mailgun",
    integrationTwilioDescription:
      "Personalized interactions and trusted global communications with customers",

    integrationCloudPrinting: "Cloud Printing",
    integrationCloudPrintingDescription:
      "Connect any printer to your application with the BuildingHub Cloud Printing",

    integrationUnknown: "Unknown",
    integrationUnknownDescription: "Unknown integration",

    integrationManageButton: "Manage",
    integrationTestButton: "Test",

    integrationCreateAccountButton: "Create an account",
    integrationDontHaveAccountTitle: "Don't have an account?",
    integrationDontHaveAccountDescription:
      "If you dont't have an account please feel free to open one.",

    integrationEnterDataPlaceholder: "Enter data",
    integrationEnterDataError: "Please insert valid data",

    toastIntegrationAddIntegrationData: "Please add integration data first.",
    toastIntegrationTestSuccess:
      "Looks like everything is fine with the data you provided!",
    toastIntegrationTestError:
      "Looks like something is wrong with the data you provided!",
    toastNoIntegrationsTitleError: "No integrations",
    toastNoIntegrationsDescriptionError:
      "No integrations available in this moment..",
  },
  adminKiosk: {
    cameraTypeBack: "Back",
    cameraTypeFront: "Front",

    appearanceTitle: "Appearance",
    backgroundImageLabel: "Background Image",
    backgroundImageDescription: "Background image used on kiosk app homescreen",

    primaryColorLabel: "Primary Color",
    primaryColorDescription: "Primary color of the kiosk app",
    primaryColorPlaceholder: "Solid color",

    welcomeTextLabel: "Welcome Text",
    welcomeTextDescription: "Welcome text for the kiosk app",
    welcomeTextPlaceholder: "Enter welcome text",

    welcomeSubtitleLabel: "Subtitle",
    welcomeSubtitleDescription: "Subtitle for the kiosk app",
    welcomeSubtitlePlaceholder: "Enter subtitle",

    identificationTitle: "Identification",

    qrCodeCheckInLabel: "QR code",
    qrCodeCheckInDescription: "Allow visitors to check in with QR code",
    qrCodeCheckInCheckboxLabel: "Visitors Can Check In With QR code",

    locatorCheckInLabel: "Record locator",
    locatorCheckInDescription: "Allow visitors to check in with Record locator",
    locatorCheckInCheckboxLabel: "Visitors Can Check In With Record Locator",

    cameraSettingsTitle: "Camera Settings",

    qrCodeScanningCameraLabel: "QR Code Scanning Camera",
    qrCodeScanningCameraDescription:
      "Select which camera will be used for scanning QR code",
    qrCodeScanningCameraPlaceholder: "Front or back side",

    qrCodeScanningCameraFlipLabel: "QR code scanning camera flip",
    qrCodeScanningCameraFlipCheckboxLabel: "Mirror camera image horizontally",

    selfieScanningCameraLabel: "Selfie scanning camera",
    selfieScanningCameraDescription:
      "Select which camera will be used for taking pictures",
    selfieScanningCameraPlaceholder: "Front or back side",

    selfieScanningCameraFlipLabel: "Selfie scanning camera flip",
    selfieScanningCameraFlipCheckboxLabel: "Mirror camera image horizontally",
  },
  adminLaborRates: {
    addNewLaborRateTitle: "Add new labor rate",
    editLaborRateTitle: "Edit labor rate",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleRequired: "Title is required",

    description: "Description",
    descriptionPlaceholder: "Enter description",

    markup: "Markup",
    markupPlaceholder: "Select markup",

    glAccount: "Account Name",
    glAccountPlaceholder: "Select account",

    arCode: "AR Code",
    arCodePlaceholder: "Select AR Code",

    chargeCode: "Charge Code",
    chargeCodePlaceholder: "Select Charge Code",

    rate: "Rate",
    ratePlaceholder: "Enter rate per hour",
  },
  adminLeasingChargeTypes: {
    addNewChargeTypeTitle: "Add new charge type",
    editChargeTypeTitle: "Edit charge type",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleRequired: "Title is required",
  },
  adminShippingProviders: {
    addNewShippingProviderTitle: "Add new shipping provider",
    editShippingProviderTitle: "Edit shipping provider",

    allShippingProvidersLabel: "All Shipping Providers",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleRequired: "Title is required",

    logo: "Logo",
    logoPlaceholder: "Select logo",
    logoRequired: "Logo is required",

    createdAt: "Created At",
    updatedAt: "Updated At",
  },
  adminMaterialGroups: {
    allMaterialGroupsTitle: "All Material Groups",

    addNewMaterialGroupTitle: "Add new material group",
    editMaterialGroupTitle: "Edit material group",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleRequired: "Title is required",

    countTableHeader: "Count",
  },
  adminMaterials: {
    allMaterialsTitle: "All Materials",

    addNewMaterialTitle: "Add new material",
    editMaterialTitle: "Edit material",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleRequired: "Title is required",

    group: "Group",
    groupPlaceholder: "Select group",
    groupRequired: "Group is required",

    description: "Description",
    descriptionPlaceholder: "Enter description",

    markup: "Default Markup",
    markupPlaceholder: "Select markup",

    tax: "Tax",
    taxPlaceholder: "Select tax",

    glAccount: "Account Name",
    glAccountPlaceholder: "Select account",

    arCode: "AR Code",
    arCodePlaceholder: "Select AR Code",

    chargeCode: "Charge Code",
    chargeCodePlaceholder: "Select Charge Code",

    price: "Price",
    pricePlaceholder: "Enter price",
  },
  adminMenuItems: {
    addNewMenuItemTitle: "Add new menu item",
    editMenuItemTitle: "Edit menu item",

    title: "Title",
    titlePlaceholder: "Enter menu title",
    titleError: "Title is required",

    icon: "Icon",
    iconPlaceholder: "Select icon",

    type: "Type",
    typePlaceholder: "Select item type",
    typeError: "Type is required",

    sectionTitle: "Section",
    feature: "Feature",
    featurePlaceholder: "Select feature",
    featureError: "Feature is required",

    product: "Product",
    productPlaceholder: "Select product",
    productError: "Product is required",

    externalLink: "External Link",
    internalLink: "Internal Link",
    embed: "Embed",

    urlSlug: "URL Slug",

    urlDomain: "URL Domain",

    urlPlaceholder: "Enter URL",
    urlError: "URL is required",

    embedBody: "Embed Body",

    embedBodyPlaceholder: "Enter embed body",
    embedBodyError: "Embed body is required",

    example: "Example:",
    webView: "Web View",
    mobileView: "Mobile View",
  },
  adminNotifications: {
    emailTestSuccess: "Success! Please check your email.",
    defaultEmailTemplateError:
      "Sorry, you can not update default Email Template. Please make it custom first.",
    pleaseAddSubject: "Please add subject!",
    pleaseAddEmailBody: "Please add email body!",
    variableCopied:
      "Variable has been copied! You can now paste it anywhere you need it.",
    customEmailTemplateDescription: "This is custom email template.",
    defaultEmailTemplateDescription:
      "This is default email template. If you wish to update this email template, please make it custom first.",

    allNotificationsLabel: "All Notifications",

    sendByLabel: "Send by",
    selectVariable: "Select variable",
    email: "Email",
    sms: "SMS",
    app: "App",

    cc: "CC",
    ccPlaceholder: "Enter email..",
    ccNoOptionsError: "Enter email..",

    bcc: "BCC",
    bccPlaceholder: "Enter email..",
    bccNoOptionsError: "Enter email..",

    subject: "Subject",
    subjectPlaceholder: "Enter subject..",
    subjectError: "Subject is required",

    emailBody: "Email Body",
    emailBodyPlaceholder: "Enter email body",
    emailBodyError: "Body is required",

    smsBody: "SMS Body",
    smsBodyPlaceholder: "Enter sms body",
    smsBodyError: "Body is required",

    appBody: "App Body",
    appBodyPlaceholder: "Enter app body",
    appBodyError: "Body is required",

    testEmailButton: "Test Email",

    categoryTableHeader: "Category",
    actionTableHeader: "Action",
    customTemplateTableHeader: "Custom Template",
    customTemplateTooltip:
      "This is a default notification template. You can copy this to a custom notification template if you want to make changes.",

    useCustomTemplateAction: "Use Custom Template",
    updateTemplateAction: "Update Template",
    removeTemplateAction: "Remove Custom Template",
    viewTemplateAction: "View Template",

    globalVar: "Global",
    localVar: "Local",
  },
  adminPmCustomIds: {
    addTitle: "Add new custom id",
    editTitle: "Edit custom id",

    title: "Name",
    titlePlaceholder: "Enter name",
    titleError: "Name is required",
  },
  adminPmTaskTypes: {
    addTitle: "Add new task type",
    editTitle: "Edit task type",

    title: "Name",
    titlePlaceholder: "Enter name",
    titleError: "Name is required",
  },
  adminPmRoundCategories: {
    addTitle: "Add new round category",
    editTitle: "Edit round category",

    title: "Name",
    titlePlaceholder: "Enter name",
    titleError: "Name is required",
  },
  adminPrinters: {
    allPrintersLabel: "All Printers",

    addNewPrinterTitle: "Add new printer",
    editPrinterTitle: "Edit printer",

    title: "Printers",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    model: "Model",
    modelPlaceholder: "Select model",
    modelError: "Model is required",

    ipAddress: "IP Address",
    ipAddressPlaceholder: "Enter IP Address",
    ipAddressError: "IP Address is required",

    paperType: "Paper Type",
    paperTypePlaceholder: "Select paper type",
    paperTypeError: "Paper type is required",

    synced: "Synced",
  },
  adminReportingSettings: {
    generalReportingSettingsTitle: "General Reporting Settings",

    logo: "Logo",
    logoDescription: "Logo used on the invoice",

    address: "Address",
    addressDescription: "Your invoice footer address",
    addressPlaceholder: "Enter the invoice address",

    yardiPropertyId: "Yardi Property ID",
    yardiPropertyIdDescription: "Your Yardi (CSV) report property ID",
    yardiPropertyIdPlaceholder: "Enter property ID",

    groupedChargesGLCode: "GL Code for Grouped Charges",
    groupedChargesGLCodeDescription:
      "Billable expense income account for invoicing (for rent statements)",

    groupedChargesARCode: "AR Code for Grouped Charges",
    groupedChargesARCodeDescription:
      "Billable expense income account for invoicing (for rent statements)",
  },
  adminFloors: {
    addNewFloorTitle: "Add new floor",
    editFloorTitle: "Edit floor",

    allFloorsLabel: "All Floors",

    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    size: "Size (sf)",
    sizePlaceholder: "Enter size",
    sizeError: "Size is required",
  },
  adminStackingPlanOverview: {
    tableTitle: "Stacking Plan - Overview",
    floorType: "Floor",
    suiteType: "Suite",
  },
  adminSuites: {
    allSuites: "All Suites",
    addNewSuiteTitle: "Add new suite",
    editSuiteTitle: "Edit suite",

    tenantType: "Tenant",
    amenityType: "Amenity",
    mechanicalType: "Mechanical",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    building: "Building",
    buildingPlaceholder: "Select building",
    buildingError: "Building is required",

    floor: "Floor",
    floorPlaceholder: "Select floor",
    floorError: "Floor is required",

    size: "Size (sf)",
    sizePlaceholder: "Enter size",
    sizeError: "Size is required",

    type: "Type",
    typePlaceholder: "Select type",
    typeError: "Type is required",

    bomaSize: "BOMA Size",
    bomaSizePlaceholder: "Enter BOMA size",

    color: "Color",

    suitePlan: "Suite Plan",

    isPublic: "Is Public",
  },
  adminWaivers: {
    addNewWaiverTitle: "Add new waiver",
    editWaiverTitle: "Edit waiver",

    selectVariable: "Select variable",

    isActive: "Is Waiver Active",
    isPublic: "Is Waiver Public",
    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    text: "Text",
    textPlaceholder: "Enter text",
    textError: "Text is required",

    type: "Type",
    typePlaceholder: "Select type",
    typeError: "Type is required",

    createdBy: "Created By",

    isPublicTableHeader: "Public?",

    toastVariablesError:
      "Some waiver variables might not be available. Error: {{message}}",
    toastVariableCopied:
      "Variable has been copied! You can now paste it anywhere you need it.",
  },
  adminIncidentsIssueTypes: {
    editIssueTypeTitle: "Edit issue type",
    addIssueTypeTitle: "Add issue type",

    notifyUsers: "Notify Users",
    notifyUsersPlaceholder: "Select users to notify",

    defaultEmployee: "Default Employee",
    defaultEmployeePlaceholder: "Select employee",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    parentGroup: "Parent Group",
    parentGroupPlaceholder: "Select parent group",
    parentGroupError: "Parent group is required",

    defaultIncidentDetails: "Default Incident Details",
    defaultIncidentDetailsPlaceholder: "Enter details",
  },
  adminIncidentsIssueTypesGroups: {
    editIssueTypeGroupTitle: "Edit issue type group",
    addIssueTypeGroupTitle: "Add issue type group",

    issueTypesTableHeader: "Issue Types",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    icon: "Icon",
    iconPlaceholder: "Select icon",
  },
  adminVendorDocumentCategories: {
    addNewCategoryTitle: "Add new category",
    editCategoryTitle: "Edit category",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    color: "Color",
    colorError: "Color is required",

    isRequired: "Is Required?",
  },
  adminIncidentsStatuses: {
    addNewStatusTitle: "Add new status",
    editStatusTitle: "Edit status",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    color: "Color",
    colorError: "Color is required",
  },
  adminWorkOrderGuestForms: {
    filterWorkOrderGuestFormsTitle: "Filter Work Order Guest Forms",

    editGuestForm: "Edit Guest Form",
    newGuestForm: "New Guest Form",

    id: "ID",
    linkTableHeader: "Link",
    createdByTableHeader: "Created By",

    copyFormAction: "Copy",
    embedFormAction: "Embed",

    detailsStep: "Details",
    settingsStep: "Settings",
    overviewStep: "Overview",

    building: "Building",
    buildingTooltip:
      "Building by is required field for work order creation. Guest can not modify this field.",
    buildingPlaceholder: "Select buildings",
    buildingError: "Building is required",

    requestedBy: "Requested By",
    requestedByTooltip:
      "Requested by is required field for work order creation. Guest can not modify this field.",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requested by is required",

    company: "Company",
    companyTooltip:
      "Company is required field for work order creation. Guest can not modify this field.",
    companyPlaceholder: "Select company",
    companyError: "Company is required",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    subTitle: "Subtitle",
    subTitlePlaceholder: "Enter subtitle",

    issueType: "Issue Type",
    issueTypePlaceholder: "Select issue type",

    status: "Status",
    statusPlaceholder: "Select status",

    priority: "Priority",
    priorityPlaceholder: "Select priority",

    assignee: "Assigned To",
    assigneePlaceholder: "Select assignee",

    floor: "Floor",
    floorPlaceholder: "Select floor",

    suite: "Suite",
    suitePlaceholder: "Select suite",

    attachmentTypes: "Attachment Types",
    attachmentTypesPlaceholder: "Select attachment types",

    enableUserToSelectBuilding: "Enable user to select building",
    buildingSelectorDescription:
      "Guests can pick any active building of this property; the building above is the default. When required, no building is preselected and the guest must choose one. Floor and suite presets are unavailable while this is on.",
    enableUserToSelectStatus: "Enable user to select status",
    enableUserToSelectPriority: "Enable user to select priority",
    enableUserToSelectAssignee: "Enable user to select assignee",
    enableUserToSelectFloor: "Enable user to select floor",
    enableUserToSelectSuite: "Enable user to select suite",
    enableUserToUploadAttachments: "Enable user to upload attachments",
    enableUserToFillCustomFields: "Enable user to fill in custom fields",
    customFieldsDescription:
      "All active work order custom fields are shown on the form. Fields marked as required must be filled in before submitting.",

    requiredCheckboxLabel: "Required",

    embedCode: "Embed Code",
    urlLink: "URL Link",

    successMessage: "Success! Your guest work order form is ready.",
  },
  adminWorkOrderEmails: {
    filterWorkOrderGuestFormsTitle: "Filter",

    editTitle: "Edit",
    newTitle: "New",

    id: "ID",
    email: "Email",
    createdBy: "Created By",

    copyEmailAction: "Copy Email",

    detailsStep: "Details",
    settingsStep: "Settings",
    overviewStep: "Overview",

    building: "Building",
    buildingPlaceholder: "Select buildings",
    buildingError: "Building is required",

    requestedBy: "Requested By",
    requestedByPlaceholder: "Select requester",
    requestedByError: "Requested by is required",

    company: "Company",
    companyPlaceholder: "Select company",
    companyError: "Company is required",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    emailSlug: "Email Slug",
    emailSlugPlaceholder: "Enter email slug",
    emailSlugTooltip:
      "This is the slug that will be used to send the email to. This is optional field. If empty, system will create one automatically.",

    issueType: "Issue Type",
    issueTypePlaceholder: "Select issue type",
    issueTypeError: "Issue type is required",

    status: "Status",
    statusPlaceholder: "Select status",

    priority: "Priority",
    priorityPlaceholder: "Select priority",

    assignee: "Assigned To",
    assigneePlaceholder: "Select assignee",

    floor: "Floor",
    floorPlaceholder: "Select floor",

    suite: "Suite",
    suitePlaceholder: "Select suite",

    allowUnknownSender: "Allow unknown sender?",
    allowUnknownSenderTooltip:
      "By default, system will only allow senders whose email exists in the system. If enabled, system will allow unknown sender to create work order.",

    successMessage: "Success! Your email to work order is ready to use.",
  },
  adminCoiEmails: {
    editTitle: "Edit",
    newTitle: "New",

    id: "ID",
    email: "Email",
    createdBy: "Created By",

    copyEmailAction: "Copy Email",

    detailsStep: "Details",
    settingsStep: "Settings",
    overviewStep: "Overview",

    company: "Company",
    companyError: "Company is required",

    title: "Title",
    titleError: "Title is required",

    status: "Status",
    statusError: "Status is required",

    emailSlug: "Email Slug",
    emailSlugTooltip:
      "This is the slug that will be used to send the email to. This is optional field. If empty, system will create one automatically.",

    contactEmails: "Contact Emails",
    contactEmailsPlaceholder: "Enter email..",

    allowUnknownSender: "Allow unknown sender?",
    allowUnknownSenderTooltip:
      "By default, system will only allow senders whose email exists in the system. If enabled, system will allow unknown sender to create COI.",

    successMessage: "Success! Your email to COI is ready to use.",
  },
  adminWorkOrderIssueTypes: {
    addIssueTypeTitle: "Add issue type",
    editIssueTypeTitle: "Edit issue type",

    notifyUsersLabel: "Notify Users",
    notifyUsersPlaceholder: "Select users to notify",

    employeeLabel: "Default Employee",
    employeePlaceholder: "Select employee",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    group: "Group",
    groupPlaceholder: "Select group",
    groupError: "Group is required",

    description: "Description",
    descriptionPlaceholder: "Enter description",

    priority: "Priority",
    priorityPlaceholder: "Select priority",
  },
  adminWorkOrderIssueTypesGroups: {
    addIssueTypeGroupTitle: "Add issue type group",
    editIssueTypeGroupTitle: "Edit issue type group",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    icon: "Icon",
    iconPlaceholder: "Select icon",

    issueTypesTableHeader: "Issue Types",
  },

  adminWorkOrderPriorities: {
    addNewPriorityTitle: "Add new priority",
    editPriorityTitle: "Edit priority",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    color: "Color",
    colorError: "Color is required",
  },
  adminWorkOrderStatuses: {
    addNewStatusTitle: "Add new status",
    editStatusTitle: "Edit status",

    title: "Title",
    titlePlaceholder: "Enter title",
    titleError: "Title is required",

    color: "Color",
    colorError: "Color is required",
  },
  importBankTransactions: {
    importedTransactions: "Imported Transactions",
    skippedTransactions: "Skipped Transactions",
  },

  importCois: {
    importCoisBoxTitle: "Import COIs",
    importCoisBoxDescription:
      "Efficiently manage your COIs by importing all relevant data to enhance organization and optimize space utilization",

    importCoisTitle: "Import COIs",
    coiAttachmentTitle: "COI Attachment",
    titleColumnHeader: "Title",
    companyColumnHeader: "Company",
    statusColumnHeader: "Status",
    validFromColumnHeader: "Valid From",
    validToColumnHeader: "Valid To",
    noteColumnHeader: "Note",
    contactEmailsColumnHeader: "Contact Emails",
    attachmentColumnHeader: "Attachment",

    importedCois: "Imported COIs",
    skippedCois: "Skipped COIs",

    enterEmailPlaceholder: "Enter email..",
  },
  importCompanies: {
    importCompaniesBoxTitle: "Import Companies",
    importCompaniesBoxDescription:
      "Streamline your company's operations by seamlessly importing all your data to maximize efficiency and boost productivity.",

    importCompaniesTitle: "Import Companies",
    importedCompanies: "Imported Companies",
    skippedCompanies: "Skipped Companies",
    titleColumnHeader: "Title",
    taxCodeColumnHeader: "Tax Code",
    typeColumnHeader: "Type",
    vendorIdColumnHeader: "Vendor ID",
    geneaIdColumnHeader: "Genea ID",
    phoneColumnHeader: "Phone",
  },

  importEquipment: {
    importEquipmentBoxTitle: "Import Equipment",
    importEquipmentBoxDescription:
      "Efficiently manage your equipment by importing all relevant data to enhance organization and optimize space utilization",

    importEquipmentTitle: "Import Equipment",
    importedEquipment: "Imported Equipment",
    skippedEquipment: "Skipped Equipment",
    titleColumnHeader: "Title",
    buildingColumnHeader: "Building",
    categoryColumnHeader: "Category",
    manufacturerColumnHeader: "Manufacturer",
    serialNumberColumnHeader: "Serial Number",
    assetTagColumnHeader: "Asset Tag",
    detailsColumnHeader: "Details",
    specificLocationColumnHeader: "Specific Location",
    defaultBuildingColumnHeader: "Default Building",
    defaultFloorColumnHeader: "Default Floor",
    defaultSuiteColumnHeader: "Default Suite",
    attachmentColumnHeader: "Attachments",
  },
  importEquipmentCategories: {
    importEquipmentCategoriesBoxTitle: "Import Equipment Categories",
    importEquipmentCategoriesBoxDescription:
      "Efficiently manage your equipment categories by importing all relevant data to enhance organization and optimize space utilization",

    importedEquipmentCategories: "Imported Equipment Categories",
    skippedEquipmentCategories: "Skipped Equipment Categories",
    importEquipmentCategoriesTitle: "Import Equipment Categories",
    titleColumnHeader: "Title",
  },
  importFloors: {
    importFloorsBoxTitle: "Import Floors",
    importFloorsBoxDescription:
      "Efficiently manage your floors by importing all relevant data to enhance organization and optimize space utilization",

    importedFloors: "Imported Floors",
    skippedFloors: "Skipped Floors",
    importFloorsTitle: "Import Floors",
  },
  importIncidents: {
    importIncidentsBoxTitle: "Import Incidents",
    importIncidentsBoxDescription:
      "Efficiently manage your incidents by importing all relevant data to enhance organization and optimize space utilization",

    importIncidentsTitle: "Import Incidents",
    importedIncidents: "Imported Incidents",
    skippedIncidents: "Skipped Incidents",
    buildingColumnHeader: "Building",
    categoryColumnHeader: "Category",
    requestedForColumnHeader: "Requested For",
    companyColumnHeader: "Company",
    statusColumnHeader: "Status",
    detailsColumnHeader: "Details",
    floorColumnHeader: "Floor",
    suiteColumnHeader: "Suite",
    startDateColumnHeader: "Start Date",
    timezoneColumnHeader: "Timezone",
    attachmentsColumnHeader: "Attachments",
  },
  importMaterialGroups: {
    importMaterialGroupsBoxTitle: "Import Material Groups",
    importMaterialGroupsBoxDescription:
      "Efficiently manage your material groups by importing all relevant data to enhance organization and optimize space utilization",

    importMaterialGroupsTitle: "Import Material Groups",
    importedMaterialGroups: "Imported Material Groups",
    skippedMaterialGroups: "Skipped Material Groups",
    titleColumnHeader: "Title",
  },
  importMaterials: {
    importMaterialsBoxTitle: "Import Materials",
    importMaterialsBoxDescription:
      "Efficiently manage your materials by importing all relevant data to enhance organization and optimize space utilization",

    importMaterialsTitle: "Import Materials",
    importedMaterials: "Imported Materials",
    skippedMaterials: "Skipped Materials",
    titleColumnHeader: "Title",
    groupColumnHeader: "Group",
    priceColumnHeader: "Price",
    detailsColumnHeader: "Details",
    markupColumnHeader: "Markup",
    taxColumnHeader: "Tax",
    glAccountColumnHeader: "Account Name",
    arCodeColumnHeader: "AR Code",
    chargeCodeColumnHeader: "Charge Code",
  },
  importProcedures: {
    importProceduresBoxTitle: "Import Procedures",
    importProceduresBoxDescription:
      "Efficiently manage your procedures by importing all relevant data to enhance organization and optimize space utilization",

    importProceduresTitle: "Import Procedures",
    importedProcedures: "Imported Procedures",
    skippedProcedures: "Skipped Procedures",
    sectionsTitle: "Sections",
    sectionTitlePlaceholder: "Enter section title",
    sectionTitleError: "Section title is required",
    noSectionsError: "There are no sections. Please add one.",

    oneSection: "1 section",
    sections: "sections",
    noSections: "No sections",
    sectionsTooltip:
      "Every section must have title and atleast one step! Every step must have title!",
    toastAtleastOneSectionRequired: "Atleast one section is required!",
    toastEverySectionMustHaveTitleAndAtleastOneStep:
      "Every section must have title and atleast one step! Every step must have title!",

    titleColumnHeader: "Title",
    estimatedLaborColumnHeader: "Estimated Labor",
    notesColumnHeader: "Notes",
    sectionsColumnHeader: "Sections",
  },
  importSchedules: {
    importSchedulesBoxTitle: "Import Schedules",
    importSchedulesBoxDescription:
      "Efficiently manage your schedules by importing all relevant data to enhance organization and optimize space utilization",

    importSchedulesTitle: "Import Schedules",
    importedSchedules: "Imported Schedules",
    skippedSchedules: "Skipped Schedules",

    buildingColumnHeader: "Building",
    titleColumnHeader: "Title",
    notesColumnHeader: "Notes",
    taskTypeColumnHeader: "Task Type",
    assignedToColumnHeader: "Assigned To",
    proceduresColumnHeader: "Procedures",
    equipmentColumnHeader: "Equipment",
    equipmentAsTaskColumnHeader: "Equipment As Task?",
    recurrenceColumnHeader: "Recurrence",
    startDateColumnHeader: "Start Date",
    timezoneColumnHeader: "Timezone",
    dueAfterColumnHeader: "Due After",
    skippedDatesColumnHeader: "Skipped Dates",

    equipmentAsTaskTooltip:
      "If enabled system will create separate task for each selected equipment.",
  },
  importSuites: {
    importSuitesBoxTitle: "Import Suites",
    importSuitesBoxDescription:
      "Efficiently manage your suites by importing all relevant data to enhance organization and optimize space utilization",

    importSuitesTitle: "Import Suites",
    importedSuites: "Imported Suites",
    skippedSuites: "Skipped Suites",

    titleColumnHeader: "Title",
    typeColumnHeader: "Type",
    floorColumnHeader: "Floor",
    sizeColumnHeader: "Size",
    bomaSizeColumnHeader: "BOMA Size",
    colorColumnHeader: "Color",
    isPublicColumnHeader: "Is Public?",
  },
  importUsers: {
    importUsersBoxTitle: "Import Users",
    importUsersBoxDescription:
      "Effortlessly import all your user data to optimize productivity and save valuable time.",

    importUsersTitle: "Import Users",

    firstNameColumnHeader: "First Name",
    lastNameColumnHeader: "Last Name",
    emailColumnHeader: "Email",
    titleColumnHeader: "Title",
    phoneColumnHeader: "Phone",
    roleColumnHeader: "Role",
    buildingsColumnHeader: "Buildings",
    fullAccessBuildingsColumnHeader: "Full Access Buildings",
    companiesColumnHeader: "Companies",
    fullAccessCompaniesColumnHeader: "Full Access Companies",

    importedUsers: "Imported Users",
    skippedUsers: "Skipped Users",
  },
  importWorkOrderTemplates: {
    importWorkOrderTemplatesBoxTitle: "Import Work Order Templates",
    importWorkOrderTemplatesBoxDescription:
      "Efficiently manage your work order templates by importing all relevant data to enhance organization and optimize space utilization",

    importWorkOrderTemplatesTitle: "Import Work Order Templates",
    importedWorkOrderTemplates: "Imported Work Order Templates",
    skippedWorkOrderTemplates: "Skipped Work Order Templates",

    buildingColumnHeader: "Building",
    issueTypeColumnHeader: "Issue Type",
    requestedByColumnHeader: "Requested By",
    companyColumnHeader: "Company",
    assignedToColumnHeader: "Assigned To",
    priorityColumnHeader: "Priority",
    statusColumnHeader: "Status",
    detailsColumnHeader: "Details",
    floorColumnHeader: "Floor",
    suiteColumnHeader: "Suite",
    recurrenceColumnHeader: "Recurrence",
    startDateColumnHeader: "Start Date",
    timezoneColumnHeader: "Timezone",
    dueAfterColumnHeader: "Due After",
    skippedDatesColumnHeader: "Skipped Dates",
    attachmentsColumnHeader: "Attachments",
  },
  importWorkOrders: {
    importWorkOrdersBoxTitle: "Import Work Orders",
    importWorkOrdersBoxDescription:
      "Efficiently manage your work orders by importing all relevant data to enhance organization and optimize space utilization",

    importWorkOrdersTitle: "Import Work Orders",
    importedWorkOrders: "Imported Work Orders",
    skippedWorkOrders: "Skipped Work Orders",

    buildingColumnHeader: "Building",
    issueTypeColumnHeader: "Issue Type",
    requestedByColumnHeader: "Requested By",
    companyColumnHeader: "Company",
    assignedToColumnHeader: "Assigned To",
    priorityColumnHeader: "Priority",
    statusColumnHeader: "Status",
    detailsColumnHeader: "Details",
    floorColumnHeader: "Floor",
    suiteColumnHeader: "Suite",
    startDateColumnHeader: "Start Date",
    dueDateColumnHeader: "Due Date",
    closedAtColumnHeader: "Closed At",
    timezoneColumnHeader: "Timezone",
    attachmentsColumnHeader: "Attachments",
  },
};

// To update translation run `yarn translation:upload` from the repo root
// (see scripts/upload-translation.ts). Manual fallback: uncomment the line below.
// console.log(JSON.stringify(translation));

export default translation;
